import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  BookOpen, 
  CheckCircle2, 
  Download, 
  Eye,
  EyeOff,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Award,
  Target,
  Lightbulb,
  PenTool,
  Code,
  Globe,
  Cpu,
  Shield,
  HelpCircle,
  FileQuestion,
  CheckSquare,
  XCircle,
  FileImage,
  File,
  Video,
  Link as LinkIcon,
  Presentation,
  ScrollText,
  GraduationCap,
  List,
  AlertCircle,
  CircleDot,
  Printer,
  Users,
  UserCheck,
  Lock,
  Beaker,
  Calculator,
  FlaskConical,
  Heart,
  MessageCircle,
  Pencil,
  BarChart,
  Atom,
  Zap,
  TrendingUp,
  Building,
  Map,
  Compass,
  Leaf,
  Bug,
  DollarSign,
  FileEdit,
  Sparkles,
  Clock,
  RefreshCw
} from 'lucide-react';
import { getCurrentUser } from '@/data/users';
import apiService from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Import expanded student notes
import {
  year11ICTStudentNotes,
  year12ICTStudentNotes,
  year13ICTStudentNotes,
  year11ChemistryStudentNotes,
  year12ChemistryStudentNotes,
  year13ChemistryStudentNotes,
  noteImages
} from '@/data/studentNotes';

// Import Physics student notes
import {
  year11PhysicsStudentNotes,
  year12PhysicsStudentNotes,
  year13PhysicsStudentNotes
} from '@/data/physicsStudentNotes';

// Import Economics student notes
import {
  year11EconomicsStudentNotes,
  year12EconomicsStudentNotes,
  year13EconomicsStudentNotes
} from '@/data/economicsStudentNotes';

// Import Primary Assessments
import { primaryAssessments, getPrimaryAssessments, getPrimaryStrands } from '@/data/primaryAssessments';

// Import ICT exercises and unit tests
import { 
  year11ICTUnitTests, 
  year11ICTExercises 
} from '@/data/year11ICTUnitTests';
import { year12ICTUnitTests } from '@/data/year12ICTUnitTests';
import { year12ICTExercises } from '@/data/year12ICTExercisesAssignments';
import {
  year13ICTUnitTests,
  year13ICTExercises
} from '@/data/year13ICTData';

// Import Chemistry unit tests and exercises
import {
  year11ChemistryUnitTests,
  year12ChemistryUnitTests,
  year13ChemistryUnitTests,
  year11ChemistryExercises,
  year12ChemistryExercises,
  year13ChemistryExercises
} from '@/data/chemistryUnitTests';

// Import Physics unit tests and exercises
import {
  year11PhysicsUnitTests,
  year12PhysicsUnitTests,
  year13PhysicsUnitTests,
  year11PhysicsExercises,
  year12PhysicsExercises,
  year13PhysicsExercises
} from '@/data/physicsUnitTests';

// Import Economics unit tests and exercises
import {
  year11EconomicsUnitTests,
  year12EconomicsUnitTests,
  year13EconomicsUnitTests,
  year11EconomicsExercises,
  year12EconomicsExercises,
  year13EconomicsExercises
} from '@/data/economicsUnitTests';

// Import French curriculum, student notes, and unit tests (Junior Secondary - Years 7-10)
import { frenchCurriculum } from '@/data/frenchCurriculum';
import {
  year7FrenchStudentNotes,
  year8FrenchStudentNotes,
  year9FrenchStudentNotes,
  year10FrenchStudentNotes
} from '@/data/frenchStudentNotes';
import {
  year7FrenchUnitTests,
  year8FrenchUnitTests,
  year9FrenchUnitTests,
  year10FrenchUnitTests,
  year7FrenchExercises,
  year8FrenchExercises,
  year9FrenchExercises,
  year10FrenchExercises
} from '@/data/frenchUnitTests';

// Import History student notes (Senior Secondary - Years 11-13)
import {
  year11HistoryStudentNotes,
  year12HistoryStudentNotes,
  year13HistoryStudentNotes
} from '@/data/historyStudentNotes';

// Import Geography student notes and unit tests (Senior Secondary - Years 11-13)
import {
  year11GeographyStudentNotes,
  year12GeographyStudentNotes,
  year13GeographyStudentNotes
} from '@/data/geographyStudentNotes';
import {
  year11GeographyUnitTests,
  year12GeographyUnitTests,
  year13GeographyUnitTests,
  year11GeographyExercises,
  year12GeographyExercises,
  year13GeographyExercises
} from '@/data/geographyUnitTests';

// Import Mathematics student notes and unit tests (Senior Secondary - Years 11-13)
import {
  year11MathematicsStudentNotes,
  year12MathematicsStudentNotes,
  year13MathematicsStudentNotes
} from '@/data/mathematicsStudentNotes';
import {
  year11MathematicsUnitTests,
  year12MathematicsUnitTests,
  year13MathematicsUnitTests,
  year11MathematicsExercises,
  year12MathematicsExercises,
  year13MathematicsExercises
} from '@/data/mathematicsUnitTests';

// Import Agriculture student notes and unit tests (Senior Secondary - Years 11-13)
import {
  year11AgricultureStudentNotes,
  year12AgricultureStudentNotes,
  year13AgricultureStudentNotes
} from '@/data/agricultureStudentNotes';
import {
  year11AgricultureUnitTests,
  year12AgricultureUnitTests,
  year13AgricultureUnitTests
} from '@/data/agricultureUnitTests';

// Import Accounting student notes and unit tests (Senior Secondary - Years 11-13)
import {
  year11AccountingStudentNotes,
  year12AccountingStudentNotes,
  year13AccountingStudentNotes
} from '@/data/accountingStudentNotes';
import {
  year11AccountingUnitTests,
  year12AccountingUnitTests,
  year13AccountingUnitTests
} from '@/data/accountingUnitTests';

// Import ESL student notes and unit tests (Senior Secondary - Years 11-13)
import {
  year11ESLStudentNotes,
  year12ESLStudentNotes,
  year13ESLStudentNotes
} from '@/data/eslStudentNotes';
import {
  year11ESLUnitTests,
  year12ESLUnitTests,
  year13ESLUnitTests
} from '@/data/eslUnitTests';

// ==================== NOTES DATA ====================
const year13NotesData = {
  subject: "Year 13 ICT",
  strands: [
    {
      id: "strand1",
      number: 1,
      title: "ICT Trends & Information Systems",
      color: "blue",
      icon: Globe,
      units: [
        {
          id: "1.1",
          title: "Future ICT Trends and Their Key Features",
          overview: "This unit explores emerging ICT trends that will shape the future of education, health, and home life.",
          keyTerms: ["AI", "IoT", "VR/AR", "Telemedicine", "Smart Home", "5G/6G", "Cloud Computing"],
          notes: [
            {
              heading: "1. Introduction to ICT Trends",
              content: `Information and Communication Technology (ICT) continues to evolve rapidly, transforming how we live, work, and learn. Understanding these trends is crucial for preparing for the future.

**Key Points:**
• ICT trends refer to emerging technologies and their applications
• These trends impact education, healthcare, and daily life
• Vanuatu and the Pacific region are increasingly adopting new technologies`
            },
            {
              heading: "2. ICT Trends in Education",
              content: `**AI-Powered Personalized Learning**
• Adaptive learning platforms that adjust to student pace
• Intelligent tutoring systems providing personalized feedback
• Automated grading and assessment tools

**Virtual Reality (VR) in Education**
• Immersive learning experiences (virtual field trips, simulations)
• Safe environment for practical training (medical, engineering)
• Engaging students through interactive 3D content

**Cloud-Based Learning**
• Access to resources from anywhere with internet
• Collaborative tools for group projects
• Reduced need for physical infrastructure`
            },
            {
              heading: "3. ICT Trends in Health",
              content: `**Telemedicine**
• Remote consultations with doctors via video calls
• Especially important for remote areas in Vanuatu
• Reduces travel time and costs for patients

**AI in Healthcare**
• Disease diagnosis assistance
• Drug discovery and development
• Predictive analytics for patient care

**Wearable Health Devices**
• Fitness trackers monitoring heart rate, steps, sleep
• Medical devices for continuous health monitoring
• Data sharing with healthcare providers`
            },
            {
              heading: "4. ICT Trends at Home",
              content: `**Smart Home Technology (IoT)**
• Connected devices (lights, thermostats, security cameras)
• Voice assistants (Alexa, Google Home)
• Energy management and automation

**5G/6G Connectivity**
• Faster internet speeds
• More reliable connections
• Enabling more connected devices

**Home Entertainment**
• Streaming services
• Gaming and VR
• Smart TVs and devices`
            },
            {
              heading: "5. Key Features of ICT Trends",
              content: `When analyzing any ICT trend, consider these key features:

| Feature | Description | Example |
|---------|-------------|---------|
| Connectivity | Ability to connect and share data | IoT devices communicating via Wi-Fi |
| Automation | Tasks performed without human intervention | AI scheduling appointments |
| Personalization | Adapting to individual needs | Personalized learning paths |
| Accessibility | Available to more people | Mobile health apps |
| Efficiency | Doing more with less resources | Cloud computing reducing hardware needs |`
            }
          ],
          resources: [
            { type: "ppt", title: "Unit 1.1 - ICT Trends Overview", size: "2.4 MB" },
            { type: "pdf", title: "ICT Trends Summary Notes", size: "450 KB" },
            { type: "pdf", title: "ICT Trends Worksheet", size: "280 KB" },
            { type: "video", title: "Future of Technology (YouTube)", url: "https://youtube.com" }
          ],
          summary: [
            "ICT trends are transforming education, health, and home life",
            "Key technologies include AI, IoT, VR/AR, and cloud computing",
            "Understanding key features helps analyze any new technology",
            "Vanuatu context: Consider infrastructure limitations and opportunities"
          ]
        },
        {
          id: "1.2",
          title: "Workflows and Information Systems",
          overview: "Understanding how workflows operate within organizations and how Information Systems support business processes.",
          keyTerms: ["Workflow", "Information System", "SIS", "TMS", "SDLC", "Database", "Input-Process-Output"],
          notes: [
            {
              heading: "1. Understanding Workflows",
              content: `**Definition:** A workflow is a sequence of tasks or processes that need to be completed to achieve a specific outcome.

**Components of a Workflow:**
• **Input:** Data or materials entering the process
• **Process:** Actions performed on the input
• **Output:** Results or products of the process

**Example - School Enrollment Workflow:**
1. Student submits application form (Input)
2. Office verifies documents (Process)
3. Student is registered in system (Output)
4. Class assignment is made (Further Process)
5. Student receives timetable (Final Output)`
            },
            {
              heading: "2. Information Systems (IS)",
              content: `**Definition:** An Information System is an organized combination of people, hardware, software, data, and procedures that work together to manage information.

**Components of an IS:**
• **Hardware:** Computers, servers, network devices
• **Software:** Applications, databases, operating systems
• **Data:** Raw facts that are processed into information
• **Procedures:** Rules and guidelines for using the system
• **People:** Users, administrators, developers

**Types of Information Systems:**
• Transaction Processing Systems (TPS)
• Management Information Systems (MIS)
• Decision Support Systems (DSS)
• Student Information Systems (SIS)
• Tourism Management Systems (TMS)`
            },
            {
              heading: "3. Student Information System (SIS)",
              content: `**Purpose:** Manage student data throughout their educational journey

**Key Functions:**
• Student enrollment and registration
• Attendance tracking
• Grade management and reporting
• Timetable scheduling
• Communication with parents
• Report card generation

**Benefits:**
• Reduces paperwork and manual errors
• Quick access to student information
• Better communication between stakeholders
• Data-driven decision making
• Historical record keeping`
            },
            {
              heading: "4. Tourism Management System (TMS)",
              content: `**Purpose:** Manage tourism business operations in Vanuatu

**Key Functions:**
• Booking and reservation management
• Customer database management
• Tour package creation
• Payment processing
• Inventory management (rooms, vehicles)
• Reporting and analytics

**Vanuatu Context:**
• Supports local tour operators
• Connects with international booking platforms
• Manages cruise ship passenger activities
• Cultural tourism experiences`
            },
            {
              heading: "5. IS Development Lifecycle (SDLC)",
              content: `**Waterfall Model Phases:**

1. **Planning**
   • Identify the need for the system
   • Define scope and objectives
   • Feasibility study

2. **Analysis**
   • Gather requirements
   • Document current processes
   • Identify system requirements

3. **Design**
   • Create system architecture
   • Design user interfaces
   • Database design

4. **Implementation**
   • Write/configure software
   • Install hardware
   • Data migration

5. **Testing**
   • Unit testing
   • Integration testing
   • User acceptance testing

6. **Maintenance**
   • Bug fixes
   • Updates and enhancements
   • User support`
            }
          ],
          resources: [
            { type: "ppt", title: "Unit 1.2 - Workflows & IS", size: "3.1 MB" },
            { type: "pdf", title: "Workflow Diagrams Examples", size: "520 KB" },
            { type: "pdf", title: "SIS Case Study - Vanuatu School", size: "380 KB" },
            { type: "pdf", title: "SDLC Phases Summary", size: "290 KB" }
          ],
          summary: [
            "Workflows are sequences of tasks with inputs, processes, and outputs",
            "Information Systems combine people, hardware, software, data, and procedures",
            "SIS manages student data; TMS manages tourism operations",
            "SDLC phases: Planning → Analysis → Design → Implementation → Testing → Maintenance"
          ]
        },
        {
          id: "1.3",
          title: "ICT Infrastructure and Networking",
          overview: "Understanding ICT infrastructure components and different types of computer networks.",
          keyTerms: ["LAN", "WAN", "MAN", "Wi-Fi", "Server", "Router", "Open-source", "Proprietary"],
          notes: [
            {
              heading: "1. ICT Infrastructure Components",
              content: `**Hardware Components:**
• **Servers:** Powerful computers that provide services to other computers
• **Workstations:** Desktop/laptop computers used by employees
• **Networking Devices:** Routers, switches, modems, access points
• **Storage Devices:** Hard drives, SSDs, NAS (Network Attached Storage)
• **Peripherals:** Printers, scanners, projectors

**Software Components:**
• **Operating Systems:** Windows, Linux, macOS
• **Applications:** Office suites, specialized software
• **Database Management Systems:** MySQL, PostgreSQL
• **Security Software:** Antivirus, firewalls`
            },
            {
              heading: "2. Network Types",
              content: `**LAN (Local Area Network)**
• Covers small area (office, school, home)
• High speed (100 Mbps - 10 Gbps)
• Owned and managed by organization
• Example: School computer lab network

**MAN (Metropolitan Area Network)**
• Covers a city or large campus
• Medium speed
• May be owned by ISP or organization
• Example: Connecting multiple school campuses

**WAN (Wide Area Network)**
• Covers large geographical area (country, world)
• Variable speed
• Uses public infrastructure
• Example: Internet, company offices in different countries

**Wi-Fi (Wireless Network)**
• Wireless connection using radio waves
• Convenient but less secure than wired
• Standards: 802.11n, 802.11ac, 802.11ax (Wi-Fi 6)
• Example: School Wi-Fi network`
            },
            {
              heading: "3. Network Comparison Table",
              content: `| Feature | LAN | MAN | WAN |
|---------|-----|-----|-----|
| **Area** | Building/Campus | City | Country/Global |
| **Speed** | Very High | High | Variable |
| **Cost** | Low | Medium | High |
| **Ownership** | Private | Public/Private | Public |
| **Example** | Office network | City-wide network | Internet |`
            },
            {
              heading: "4. Open-Source vs Proprietary Software",
              content: `**Open-Source Software:**
• Source code freely available
• Can be modified and distributed
• Usually free to use
• Community support
• Examples: Linux, LibreOffice, GIMP, Firefox

**Proprietary Software:**
• Source code is secret
• Cannot be modified
• Requires license/payment
• Vendor support
• Examples: Windows, Microsoft Office, Adobe Photoshop

**Comparison:**
| Aspect | Open-Source | Proprietary |
|--------|-------------|-------------|
| Cost | Usually free | License fee |
| Customization | High | Limited |
| Support | Community | Vendor |
| Security | Transparent | Unknown |`
            },
            {
              heading: "5. Implementation Concerns in Vanuatu",
              content: `**Challenges:**
• Limited internet connectivity in remote areas
• Power supply reliability
• High cost of equipment and maintenance
• Shortage of trained IT professionals
• Import costs and delays

**Solutions:**
• Solar-powered systems for remote areas
• Satellite internet options
• Training local technicians
• Using open-source software to reduce costs
• Government support and policies`
            }
          ],
          resources: [
            { type: "ppt", title: "Unit 1.3 - Networks & Infrastructure", size: "4.2 MB" },
            { type: "pdf", title: "Network Types Comparison", size: "310 KB" },
            { type: "pdf", title: "Open Source vs Proprietary Guide", size: "280 KB" },
            { type: "video", title: "How Networks Work", url: "https://youtube.com" }
          ],
          summary: [
            "ICT infrastructure includes hardware, software, and networking components",
            "LAN = Local, MAN = Metropolitan, WAN = Wide Area Network",
            "Open-source is free and customizable; Proprietary requires licenses",
            "Vanuatu faces challenges: connectivity, power, costs, and training"
          ]
        }
      ]
    },
    {
      id: "strand2",
      number: 2,
      title: "Digital Media & Web Development",
      color: "green",
      icon: FileImage,
      units: [
        {
          id: "2.1",
          title: "Digital Media Fundamentals",
          overview: "Understanding digital media types, creation tools, and modification techniques.",
          keyTerms: ["Digital Media", "Resolution", "Pixel", "Codec", "Compression", "File Format", "Rendering"],
          notes: [
            {
              heading: "1. Introduction to Digital Media",
              content: `**Definition:** Digital media is content that is stored, transmitted, and displayed in digital (electronic) format.

**Types of Digital Media:**
• **Images:** Photographs, graphics, illustrations
• **Audio:** Music, podcasts, voice recordings, sound effects
• **Video:** Movies, tutorials, live streams
• **Text:** Documents, web pages, ebooks

**Importance:**
• Communication and sharing information
• Entertainment and education
• Business marketing and branding
• Documentation and preservation`
            },
            {
              heading: "2. Image Basics",
              content: `**Key Concepts:**
• **Pixel:** Smallest unit of a digital image
• **Resolution:** Number of pixels (e.g., 1920x1080)
• **DPI/PPI:** Dots/Pixels per inch (print quality)
• **Color Depth:** Bits used per pixel (8-bit, 24-bit)

**Common Image Formats:**
| Format | Best For | Features |
|--------|----------|----------|
| JPEG | Photos | Lossy compression, small files |
| PNG | Graphics | Lossless, transparency support |
| GIF | Animations | Limited colors, animated |
| SVG | Icons/Logos | Vector, scalable |

**Image Editing Software:**
• GIMP (Free, open-source)
• Adobe Photoshop (Professional)
• Canva (Online, easy to use)`
            },
            {
              heading: "3. Audio Basics",
              content: `**Key Concepts:**
• **Sample Rate:** Samples per second (44.1kHz standard)
• **Bit Depth:** Bits per sample (16-bit CD quality)
• **Bitrate:** Data per second (128kbps, 320kbps)
• **Channels:** Mono (1) or Stereo (2)

**Common Audio Formats:**
| Format | Type | Features |
|--------|------|----------|
| MP3 | Lossy | Universal, smaller files |
| WAV | Lossless | High quality, large files |
| AAC | Lossy | Better than MP3 at same bitrate |
| FLAC | Lossless | Compressed but lossless |

**Audio Editing Software:**
• Audacity (Free, powerful)
• GarageBand (Mac, free)
• Adobe Audition (Professional)`
            },
            {
              heading: "4. Video Basics",
              content: `**Key Concepts:**
• **Frame Rate:** Frames per second (24fps, 30fps, 60fps)
• **Resolution:** Video dimensions (1080p, 4K)
• **Codec:** Compression algorithm (H.264, H.265)
• **Container:** File format (MP4, AVI, MKV)

**Common Video Formats:**
| Format | Features |
|--------|----------|
| MP4 | Universal, good compression |
| AVI | Older, Windows |
| MOV | Apple QuickTime |
| MKV | Open format, multiple tracks |

**Video Editing Software:**
• DaVinci Resolve (Free, professional)
• OpenShot (Free, simple)
• Adobe Premiere (Professional)`
            },
            {
              heading: "5. Media Modification Techniques",
              content: `**Image Editing:**
• Cropping and resizing
• Color correction and filters
• Adding text and shapes
• Removing backgrounds
• Retouching and healing

**Audio Editing:**
• Cutting and trimming
• Volume adjustment
• Noise reduction
• Adding effects (reverb, echo)
• Fade in/out

**Video Editing:**
• Cutting and arranging clips
• Adding transitions
• Color grading
• Adding titles and text
• Audio synchronization`
            }
          ],
          resources: [
            { type: "ppt", title: "Unit 2.1 - Digital Media Overview", size: "5.8 MB" },
            { type: "pdf", title: "GIMP Tutorial Guide", size: "1.2 MB" },
            { type: "pdf", title: "Audacity Quick Start", size: "680 KB" },
            { type: "video", title: "Image Editing Basics", url: "https://youtube.com" }
          ],
          summary: [
            "Digital media includes images, audio, video, and text",
            "Resolution, format, and compression affect quality and file size",
            "Use appropriate software: GIMP (images), Audacity (audio), DaVinci (video)",
            "Key editing skills: crop, resize, filter, cut, trim, transition"
          ]
        },
        {
          id: "2.2",
          title: "Website Development Fundamentals",
          overview: "Learning web design principles and CSS styling for creating functional websites.",
          keyTerms: ["HTML", "CSS", "Selector", "Property", "Value", "Responsive", "External Stylesheet"],
          notes: [
            {
              heading: "1. Web Design Requirements",
              content: `**Essential Website Elements:**
• Clear navigation menu
• Contact information
• About page
• Services/Products pages
• Mobile-responsive design

**Good Design Principles:**
• Simple and clean layout
• Consistent color scheme
• Readable fonts and sizes
• Fast loading times
• Easy navigation

**Requirements for Local Businesses:**
• Contact details (phone, email, location)
• Operating hours
• Service descriptions
• Pricing information
• Photos of products/services`
            },
            {
              heading: "2. Introduction to CSS",
              content: `**What is CSS?**
CSS (Cascading Style Sheets) is used to style and layout web pages.

**Why CSS?**
• Separates content from presentation
• Consistent styling across pages
• Easier maintenance
• More design control

**Types of CSS:**
1. **Inline CSS:** Style attribute in HTML tag
   \`<p style="color: blue;">Text</p>\`

2. **Internal CSS:** <style> tag in HTML head
   \`<style>p { color: blue; }</style>\`

3. **External CSS:** Separate .css file (BEST PRACTICE)
   \`<link rel="stylesheet" href="styles.css">\``
            },
            {
              heading: "3. CSS Syntax",
              content: `**Basic Structure:**
\`\`\`css
selector {
    property: value;
    property: value;
}
\`\`\`

**Example:**
\`\`\`css
h1 {
    color: blue;
    font-size: 24px;
    text-align: center;
}
\`\`\`

**Common Selectors:**
| Selector | Example | Description |
|----------|---------|-------------|
| Element | p | All <p> elements |
| Class | .intro | Elements with class="intro" |
| ID | #header | Element with id="header" |
| Universal | * | All elements |`
            },
            {
              heading: "4. Essential CSS Properties",
              content: `**Text Properties:**
\`\`\`css
color: blue;           /* Text color */
font-size: 16px;       /* Font size */
font-family: Arial;    /* Font type */
text-align: center;    /* Alignment */
font-weight: bold;     /* Bold text */
\`\`\`

**Box Model Properties:**
\`\`\`css
margin: 10px;          /* Outside space */
padding: 15px;         /* Inside space */
border: 1px solid black; /* Border */
width: 100%;           /* Width */
height: 200px;         /* Height */
\`\`\`

**Background Properties:**
\`\`\`css
background-color: #f0f0f0;
background-image: url('image.jpg');
background-size: cover;
\`\`\`

**Display & Position:**
\`\`\`css
display: flex;         /* Flexbox layout */
position: relative;    /* Positioning */
float: left;          /* Float element */
\`\`\``
            },
            {
              heading: "5. Linking CSS to HTML",
              content: `**External Stylesheet (Recommended):**

**HTML File (index.html):**
\`\`\`html
<!DOCTYPE html>
<html>
<head>
    <title>My Website</title>
    <link rel="stylesheet" href="styles.css">
</head>
<body>
    <h1>Welcome</h1>
    <p class="intro">This is my website.</p>
</body>
</html>
\`\`\`

**CSS File (styles.css):**
\`\`\`css
body {
    font-family: Arial, sans-serif;
    margin: 0;
    padding: 20px;
    background-color: #f5f5f5;
}

h1 {
    color: #333;
    text-align: center;
}

.intro {
    font-size: 18px;
    color: #666;
}
\`\`\``
            }
          ],
          resources: [
            { type: "ppt", title: "Unit 2.2 - CSS Fundamentals", size: "3.5 MB" },
            { type: "pdf", title: "CSS Cheat Sheet", size: "420 KB" },
            { type: "pdf", title: "Website Project Template", size: "580 KB" },
            { type: "link", title: "W3Schools CSS Tutorial", url: "https://w3schools.com/css" },
            { type: "video", title: "CSS Crash Course", url: "https://youtube.com" }
          ],
          summary: [
            "CSS styles HTML elements using selectors, properties, and values",
            "External stylesheets are the best practice for larger websites",
            "Key properties: color, font-size, margin, padding, background",
            "Good web design: simple, consistent, responsive, accessible"
          ]
        }
      ]
    },
    {
      id: "strand3",
      number: 3,
      title: "Programming",
      color: "purple",
      icon: Code,
      units: [
        {
          id: "3.1",
          title: "Problem Solving Fundamentals",
          overview: "Developing systematic approaches to analyzing and solving problems.",
          keyTerms: ["Problem Solving", "Algorithm", "Decomposition", "Pattern Recognition", "Abstraction"],
          notes: [
            {
              heading: "1. What is Problem Solving?",
              content: `**Definition:** Problem solving is the process of identifying a problem, developing possible solutions, and implementing the best solution.

**Why is it important for programming?**
• All programs solve some problem
• Good problem solving leads to efficient programs
• Helps break complex tasks into manageable parts

**Real-world examples:**
• Planning a route (shortest path)
• Organizing a schedule (time management)
• Sorting items (alphabetically, by size)`
            },
            {
              heading: "2. The Four-Step Problem Solving Process",
              content: `**Step 1: UNDERSTAND the Problem**
• Read the problem carefully
• Identify what is given (inputs)
• Identify what is required (outputs)
• Ask clarifying questions

**Step 2: PLAN the Solution**
• Break down into smaller steps
• Think about different approaches
• Choose the best approach
• Create a rough outline

**Step 3: IMPLEMENT the Solution**
• Follow your plan
• Write the algorithm/code
• Take one step at a time

**Step 4: TEST and Evaluate**
• Check if the solution works
• Test with different inputs
• Fix any errors (debug)
• Improve if needed`
            },
            {
              heading: "3. Problem Solving Example",
              content: `**Problem:** Calculate the average of three test scores

**Step 1: Understand**
• Input: Three test scores (numbers)
• Output: Average score
• Average = sum of scores ÷ number of scores

**Step 2: Plan**
1. Get three scores from user
2. Add the three scores together
3. Divide the sum by 3
4. Display the result

**Step 3: Implement**
\`\`\`
BEGIN
    INPUT score1, score2, score3
    sum = score1 + score2 + score3
    average = sum / 3
    OUTPUT average
END
\`\`\`

**Step 4: Test**
• Test: 80, 90, 70 → Average = 80 ✓
• Test: 100, 100, 100 → Average = 100 ✓
• Test: 0, 50, 100 → Average = 50 ✓`
            },
            {
              heading: "4. Computational Thinking",
              content: `**Key Concepts:**

**Decomposition**
Breaking a complex problem into smaller parts
• Example: Making a website
  - Design layout
  - Create HTML structure
  - Write CSS styles
  - Add content

**Pattern Recognition**
Finding similarities or patterns
• Example: Sorting names uses same logic as sorting numbers

**Abstraction**
Focusing on important details, ignoring unnecessary ones
• Example: A map shows roads, not every building

**Algorithm Design**
Creating step-by-step instructions
• Must be clear and unambiguous
• Must have a start and end
• Must produce correct results`
            }
          ],
          resources: [
            { type: "ppt", title: "Unit 3.1 - Problem Solving", size: "2.8 MB" },
            { type: "pdf", title: "Problem Solving Worksheet", size: "340 KB" },
            { type: "pdf", title: "Practice Problems Set", size: "420 KB" }
          ],
          summary: [
            "Problem solving follows: Understand → Plan → Implement → Test",
            "Computational thinking: Decomposition, Pattern Recognition, Abstraction",
            "Break complex problems into smaller, manageable parts",
            "Always test your solution with different inputs"
          ]
        },
        {
          id: "3.2",
          title: "Programming Concepts and Design",
          overview: "Learning algorithm representation through pseudocode and flowcharts, and fundamental programming concepts.",
          keyTerms: ["Pseudocode", "Flowchart", "Variable", "Data Type", "Operator", "Function", "Module"],
          notes: [
            {
              heading: "1. Design Approaches",
              content: `**Top-Down Design**
• Start with the main problem
• Break into smaller sub-problems
• Continue until each part is simple
• Example: Building a house
  - Plan overall structure
  - Design rooms
  - Plan electrical, plumbing
  - Choose materials

**Bottom-Up Design**
• Start with small, simple components
• Combine into larger modules
• Build up to complete solution
• Example: Building with LEGO
  - Create small pieces
  - Combine into sections
  - Assemble final structure

**When to Use:**
• Top-Down: Large projects, clear requirements
• Bottom-Up: Reusable components, exploration`
            },
            {
              heading: "2. Pseudocode",
              content: `**Definition:** Pseudocode is an informal way of describing algorithms using plain language.

**Rules:**
• Write in plain English
• Use programming-like structure
• Be clear and specific
• One action per line

**Common Keywords:**
| Keyword | Purpose |
|---------|---------|
| BEGIN/END | Start/End of program |
| INPUT | Get data from user |
| OUTPUT/PRINT | Display data |
| SET/= | Assign value |
| IF-THEN-ELSE | Decision |
| WHILE/FOR | Loops |

**Example - Calculate Circle Area:**
\`\`\`
BEGIN
    INPUT radius
    SET pi = 3.14159
    SET area = pi * radius * radius
    OUTPUT "Area is: " + area
END
\`\`\``
            },
            {
              heading: "3. Flowcharts",
              content: `**Definition:** A flowchart is a visual representation of an algorithm using standard symbols.

**Standard Symbols:**
| Symbol | Name | Purpose |
|--------|------|---------|
| ⬭ (Oval) | Terminal | Start/End |
| ▭ (Rectangle) | Process | Action/Calculation |
| ◇ (Diamond) | Decision | Yes/No question |
| ▱ (Parallelogram) | Input/Output | Data in/out |
| → (Arrow) | Flow line | Direction |

**Example - Check if number is positive:**
\`\`\`
[Start] → [Input number] → <Is number > 0?> 
                              ↓ Yes         ↓ No
                     [Print "Positive"]  [Print "Not positive"]
                              ↓                    ↓
                              └────→ [End] ←──────┘
\`\`\``
            },
            {
              heading: "4. Variables and Data Types",
              content: `**Variable:** A named container for storing data

**Naming Rules:**
• Start with letter or underscore
• No spaces (use camelCase or snake_case)
• Descriptive names (studentName, not x)
• Case sensitive

**Data Types:**
| Type | Description | Examples |
|------|-------------|----------|
| Integer | Whole numbers | 1, -5, 100 |
| Float/Real | Decimal numbers | 3.14, -0.5 |
| String | Text | "Hello", "ABC" |
| Boolean | True/False | True, False |
| Character | Single character | 'A', '5' |

**Example:**
\`\`\`
SET studentName = "John"      // String
SET age = 17                  // Integer
SET gpa = 3.85               // Float
SET isEnrolled = True        // Boolean
\`\`\``
            },
            {
              heading: "5. Operators",
              content: `**Arithmetic Operators:**
| Operator | Description | Example |
|----------|-------------|---------|
| + | Addition | 5 + 3 = 8 |
| - | Subtraction | 5 - 3 = 2 |
| * | Multiplication | 5 * 3 = 15 |
| / | Division | 6 / 3 = 2 |
| % | Modulus (remainder) | 7 % 3 = 1 |

**Comparison Operators:**
| Operator | Description | Example |
|----------|-------------|---------|
| == | Equal to | 5 == 5 (True) |
| != | Not equal | 5 != 3 (True) |
| > | Greater than | 5 > 3 (True) |
| < | Less than | 3 < 5 (True) |
| >= | Greater or equal | 5 >= 5 (True) |
| <= | Less or equal | 3 <= 5 (True) |

**Logical Operators:**
| Operator | Description |
|----------|-------------|
| AND | Both conditions true |
| OR | At least one condition true |
| NOT | Reverses condition |`
            },
            {
              heading: "6. Control Structures",
              content: `**IF-THEN-ELSE (Selection):**
\`\`\`
IF condition THEN
    // code if true
ELSE
    // code if false
ENDIF
\`\`\`

**Example:**
\`\`\`
IF score >= 50 THEN
    OUTPUT "Pass"
ELSE
    OUTPUT "Fail"
ENDIF
\`\`\`

**FOR Loop (Counted):**
\`\`\`
FOR counter = 1 TO 10
    OUTPUT counter
ENDFOR
\`\`\`

**WHILE Loop (Conditional):**
\`\`\`
WHILE condition
    // code to repeat
ENDWHILE
\`\`\`

**Example - Print 1 to 5:**
\`\`\`
SET count = 1
WHILE count <= 5
    OUTPUT count
    count = count + 1
ENDWHILE
\`\`\``
            }
          ],
          resources: [
            { type: "ppt", title: "Unit 3.2 - Programming Concepts", size: "4.5 MB" },
            { type: "pdf", title: "Pseudocode Reference Guide", size: "380 KB" },
            { type: "pdf", title: "Flowchart Symbols Guide", size: "290 KB" },
            { type: "pdf", title: "Practice Exercises", size: "520 KB" },
            { type: "video", title: "Flowcharts Tutorial", url: "https://youtube.com" }
          ],
          summary: [
            "Top-Down breaks problems down; Bottom-Up builds up from components",
            "Pseudocode describes algorithms in plain language",
            "Flowcharts use symbols: oval, rectangle, diamond, parallelogram",
            "Variables store data; Data types: integer, float, string, boolean",
            "Control structures: IF-ELSE (decisions), FOR/WHILE (loops)"
          ]
        }
      ]
    },
    {
      id: "strand4",
      number: 4,
      title: "Microprocessors & Ethics",
      color: "red",
      icon: Cpu,
      units: [
        {
          id: "4.1",
          title: "Microprocessors and Embedded Systems",
          overview: "Understanding microprocessor architecture and embedded system applications.",
          keyTerms: ["CPU", "ALU", "Control Unit", "Register", "Bus", "Compiler", "Interpreter", "Sensor", "Actuator"],
          notes: [
            {
              heading: "1. Microprocessor Architecture",
              content: `**What is a Microprocessor?**
The microprocessor (CPU) is the "brain" of the computer that executes instructions and performs calculations.

**Main Components:**

**ALU (Arithmetic Logic Unit)**
• Performs mathematical calculations (+, -, ×, ÷)
• Performs logical operations (AND, OR, NOT)
• Compares values

**Control Unit (CU)**
• Directs operations of the processor
• Fetches instructions from memory
• Decodes and executes instructions
• Controls data flow between components

**Registers**
• Small, fast storage locations
• Hold data being processed
• Types: Accumulator, Program Counter, Instruction Register

**System Bus**
• Communication pathways
• Data Bus: Carries data
• Address Bus: Carries memory addresses
• Control Bus: Carries control signals`
            },
            {
              heading: "2. How the CPU Works (Fetch-Decode-Execute)",
              content: `**The CPU Cycle:**

1. **FETCH**
   • Get instruction from memory
   • Use Program Counter to find address
   • Load instruction into Instruction Register

2. **DECODE**
   • Interpret the instruction
   • Determine what operation to perform
   • Identify data needed

3. **EXECUTE**
   • Perform the operation
   • Use ALU for calculations
   • Store result in register or memory

**Example:**
\`\`\`
Instruction: ADD R1, R2
1. Fetch: Get "ADD R1, R2" from memory
2. Decode: Operation = ADD, Operands = R1 and R2
3. Execute: R1 = R1 + R2
\`\`\``
            },
            {
              heading: "3. Compilers vs Interpreters",
              content: `**Compiler:**
• Translates entire source code at once
• Creates executable file
• Faster execution after compilation
• Examples: C, C++, Java

**Process:**
Source Code → Compiler → Machine Code → Execution

**Interpreter:**
• Translates code line by line
• Executes immediately
• Slower execution
• Examples: Python, JavaScript

**Process:**
Source Code → Interpreter → Execution (line by line)

**Comparison:**
| Aspect | Compiler | Interpreter |
|--------|----------|-------------|
| Translation | All at once | Line by line |
| Speed | Faster execution | Slower execution |
| Debugging | Harder | Easier |
| Distribution | Executable file | Source code |`
            },
            {
              heading: "4. Sensors and Actuators",
              content: `**Sensors (Input Devices)**
Detect physical conditions and convert to electrical signals

| Sensor Type | Measures | Example Use |
|-------------|----------|-------------|
| Temperature | Heat/Cold | Thermostat |
| Light | Brightness | Automatic lights |
| Motion | Movement | Security alarm |
| Pressure | Force | Touchscreen |
| Proximity | Distance | Parking sensors |

**Actuators (Output Devices)**
Convert electrical signals into physical action

| Actuator Type | Action | Example Use |
|---------------|--------|-------------|
| Motor | Rotation | Fans, wheels |
| Solenoid | Linear push/pull | Door locks |
| Speaker | Sound | Alarms, music |
| LED | Light | Indicators |
| Relay | Switch on/off | Power control |

**Analog-to-Digital Conversion (ADC)**
• Sensors produce analog signals (continuous)
• Computers need digital signals (discrete)
• ADC converts analog to digital`
            },
            {
              heading: "5. Embedded Systems",
              content: `**Definition:** An embedded system is a computer system designed for specific functions within a larger system.

**Characteristics:**
• Dedicated to specific tasks
• Built into devices
• Real-time operation
• Limited resources (memory, power)
• Reliable and stable

**Examples:**
| Device | Embedded System Function |
|--------|------------------------|
| Washing Machine | Control wash cycles |
| Microwave | Control cooking time/power |
| Car | Engine management, ABS |
| Smart TV | Process video, interface |
| ATM | Handle transactions |
| Digital Watch | Keep time, alarms |

**Applications in Vanuatu:**
• Solar panel controllers
• Water pump automation
• Weather monitoring stations
• Agricultural sensors`
            }
          ],
          resources: [
            { type: "ppt", title: "Unit 4.1 - Microprocessors", size: "4.8 MB" },
            { type: "pdf", title: "CPU Architecture Diagram", size: "380 KB" },
            { type: "pdf", title: "Sensors & Actuators Guide", size: "450 KB" },
            { type: "video", title: "How CPUs Work", url: "https://youtube.com" }
          ],
          summary: [
            "CPU components: ALU, Control Unit, Registers, Bus",
            "CPU cycle: Fetch → Decode → Execute",
            "Compilers translate all at once; Interpreters translate line by line",
            "Sensors detect; Actuators act; ADC converts analog to digital",
            "Embedded systems are specialized computers in devices"
          ]
        },
        {
          id: "4.2",
          title: "ICT Ethics and Social Issues",
          overview: "Understanding ethical considerations and social impacts of ICT.",
          keyTerms: ["Privacy", "Copyright", "Digital Divide", "Cyberbullying", "E-waste", "Ergonomics"],
          notes: [
            {
              heading: "1. Introduction to ICT Ethics",
              content: `**What is Ethics?**
Ethics refers to moral principles that govern behavior and decision-making.

**ICT Ethics**
Applying ethical principles to the use of information and communication technology.

**Why is ICT Ethics Important?**
• Technology has great power to help or harm
• Digital actions have real-world consequences
• Laws may not cover all situations
• Responsible use benefits everyone

**Key Questions:**
• Is this action legal?
• Is this action fair?
• Would I want this done to me?
• Who might be harmed?`
            },
            {
              heading: "2. Privacy and Data Protection",
              content: `**What is Privacy?**
The right to control personal information and how it's used.

**Privacy Concerns:**
• Data collection without consent
• Tracking online activity
• Sharing personal information
• Surveillance and monitoring
• Data breaches and leaks

**Types of Personal Data:**
• Name, address, phone number
• Email and social media accounts
• Financial information
• Health records
• Location data
• Browsing history

**Protection Measures:**
• Strong passwords
• Privacy settings on social media
• Careful about sharing personal info
• Using secure websites (https)
• Being aware of scams`
            },
            {
              heading: "3. Intellectual Property and Copyright",
              content: `**What is Copyright?**
Legal protection for original creative works.

**What is Protected:**
• Books, articles, documents
• Music and audio recordings
• Videos and films
• Software and code
• Images and artwork
• Websites

**What is Piracy?**
Unauthorized copying, distribution, or use of copyrighted material.

**Examples of Piracy:**
• Downloading movies illegally
• Copying software without license
• Sharing music without permission
• Using images without credit

**Respecting Copyright:**
• Use licensed or free resources
• Give credit to creators
• Ask permission when needed
• Use Creative Commons content
• Support creators by paying for content`
            },
            {
              heading: "4. Digital Divide and Cyberbullying",
              content: `**Digital Divide**
The gap between those with access to technology and those without.

**Causes:**
• Economic inequality
• Geographic location (rural vs urban)
• Age differences
• Education levels
• Infrastructure limitations

**Impact:**
• Limited educational opportunities
• Reduced job prospects
• Social exclusion
• Less access to services

**Vanuatu Context:**
• Remote islands with limited connectivity
• High cost of internet access
• Limited electricity in some areas
• Need for localized solutions

---

**Cyberbullying**
Using technology to harass, threaten, or humiliate others.

**Forms:**
• Hurtful messages or posts
• Spreading rumors online
• Sharing embarrassing content
• Exclusion from online groups
• Creating fake accounts

**Prevention:**
• Think before posting
• Report abusive behavior
• Block bullies
• Keep evidence
• Talk to a trusted adult`
            },
            {
              heading: "5. Environmental and Health Issues",
              content: `**E-waste (Electronic Waste)**
Discarded electronic devices and components.

**Problems:**
• Toxic materials (lead, mercury, cadmium)
• Growing volume of waste
• Improper disposal in landfills
• Export to developing countries

**Solutions:**
• Reduce: Buy only what you need
• Reuse: Donate or sell old devices
• Recycle: Use proper e-waste facilities
• Repair: Fix instead of replace

---

**Health Issues from ICT Use**

**Physical:**
• Eye strain from screens
• Repetitive strain injury (RSI)
• Poor posture and back pain
• Sedentary lifestyle

**Mental:**
• Addiction to devices/games
• Sleep disruption from blue light
• Social isolation
• Anxiety and depression

**Ergonomic Solutions:**
• Take regular breaks (20-20-20 rule)
• Proper desk and chair setup
• Screen at eye level
• Good lighting
• Limit screen time`
            }
          ],
          resources: [
            { type: "ppt", title: "Unit 4.2 - ICT Ethics", size: "3.2 MB" },
            { type: "pdf", title: "Ethics Case Studies", size: "480 KB" },
            { type: "pdf", title: "Digital Citizenship Guide", size: "390 KB" },
            { type: "pdf", title: "E-waste Facts Sheet", size: "280 KB" }
          ],
          summary: [
            "ICT ethics involves moral principles for technology use",
            "Privacy: Control over personal information; protect with strong security",
            "Copyright: Legal protection for creative works; avoid piracy",
            "Digital divide: Technology access gap; affects opportunities",
            "E-waste: Recycle properly; Health: Take breaks, good ergonomics"
          ]
        }
      ]
    }
  ]
};

// ==================== YEAR 12 ICT NOTES DATA ====================
const year12NotesData = {
  subject: "Year 12 ICT",
  strands: [
    {
      id: "strand1",
      number: 1,
      title: "Word Processing & Spreadsheets",
      color: "blue",
      icon: FileText,
      units: [
        {
          id: "1.1",
          title: "Advanced Word Processing",
          overview: "Master advanced features of word processing applications including mail merge, styles, and document automation.",
          keyTerms: ["Mail Merge", "Styles", "Templates", "Headers/Footers", "Table of Contents", "Track Changes"],
          notes: [
            {
              heading: "1. Document Formatting",
              content: `**Professional Document Layout**
• Setting page margins, orientation, and size
• Creating headers and footers with page numbers
• Using section breaks for different formatting
• Applying consistent styles throughout documents

**Key Skills:**
• Format professional business letters
• Create reports with proper structure
• Design newsletters and brochures`
            },
            {
              heading: "2. Mail Merge Operations",
              content: `**Understanding Mail Merge**
• Main document: Template with placeholders
• Data source: List of recipients/information
• Merge fields: Placeholders replaced with data

**Steps for Mail Merge:**
1. Create the main document
2. Connect to data source (Excel, database)
3. Insert merge fields
4. Preview results
5. Complete the merge

**Applications:**
• Form letters, certificates, labels, envelopes`
            },
            {
              heading: "3. Styles and Templates",
              content: `**Using Styles**
• Heading styles (Heading 1, 2, 3)
• Body text styles
• Custom style creation
• Style sets and themes

**Benefits:**
• Consistent formatting
• Easy navigation
• Automatic table of contents generation

**Templates:**
• Pre-designed document layouts
• Save time on formatting
• Ensure consistency across documents`
            }
          ],
          reviewQuestions: [
            "What is mail merge and list 3 applications?",
            "Explain the difference between styles and direct formatting",
            "How do you create an automatic table of contents?"
          ],
          keyPoints: [
            "Mail merge combines a template with data to create multiple documents",
            "Styles ensure consistent formatting and enable automatic TOC",
            "Templates save time and maintain professional standards"
          ]
        },
        {
          id: "1.2",
          title: "Spreadsheet Applications",
          overview: "Learn advanced spreadsheet functions for data analysis, charts, and business calculations.",
          keyTerms: ["Formulas", "Functions", "Charts", "Pivot Tables", "Conditional Formatting", "VLOOKUP"],
          notes: [
            {
              heading: "1. Essential Functions",
              content: `**Mathematical Functions**
• SUM, AVERAGE, COUNT, MAX, MIN
• ROUND, CEILING, FLOOR

**Logical Functions**
• IF, AND, OR, NOT
• Nested IF statements
• IFERROR for error handling

**Lookup Functions**
• VLOOKUP: Search vertically
• HLOOKUP: Search horizontally
• INDEX/MATCH combination`
            },
            {
              heading: "2. Data Analysis",
              content: `**Sorting and Filtering**
• Sort by single or multiple columns
• AutoFilter for quick filtering
• Advanced filters with criteria

**Conditional Formatting**
• Highlight cells based on values
• Data bars and color scales
• Icon sets for visual indicators

**Pivot Tables**
• Summarize large datasets
• Group and analyze data
• Create dynamic reports`
            },
            {
              heading: "3. Charts and Visualization",
              content: `**Chart Types**
• Column/Bar: Compare values
• Line: Show trends over time
• Pie: Show proportions
• Scatter: Show relationships

**Chart Elements**
• Title, legend, axis labels
• Data labels and gridlines
• Trendlines for analysis

**Best Practices**
• Choose appropriate chart type
• Keep it simple and clear
• Use consistent colors`
            }
          ],
          reviewQuestions: [
            "Write the VLOOKUP formula syntax and explain each parameter",
            "What is a pivot table and when would you use it?",
            "Explain the difference between absolute and relative cell references"
          ],
          keyPoints: [
            "VLOOKUP: =VLOOKUP(lookup_value, table_array, col_index, [range_lookup])",
            "Pivot tables summarize and analyze large datasets quickly",
            "Absolute references ($A$1) stay fixed when copying formulas"
          ]
        }
      ]
    },
    {
      id: "strand2",
      number: 2,
      title: "Database Management",
      color: "green",
      icon: Cpu,
      units: [
        {
          id: "2.1",
          title: "Database Fundamentals",
          overview: "Understand database concepts, design principles, and basic SQL operations.",
          keyTerms: ["Database", "Table", "Record", "Field", "Primary Key", "Foreign Key", "SQL"],
          notes: [
            {
              heading: "1. Database Concepts",
              content: `**What is a Database?**
• Organized collection of related data
• Allows efficient storage and retrieval
• Examples: Student records, inventory, library systems

**Key Components:**
• Table: Collection of related records
• Record (Row): Single entry in a table
• Field (Column): Single piece of data
• Primary Key: Unique identifier for each record

**Database Types:**
• Flat file: Simple, single table
• Relational: Multiple linked tables (most common)
• NoSQL: Flexible, non-tabular`
            },
            {
              heading: "2. Database Design",
              content: `**Normalization**
• Process of organizing data efficiently
• Reduces redundancy
• Improves data integrity

**Relationships:**
• One-to-One: One record matches one record
• One-to-Many: One record matches many records
• Many-to-Many: Many records match many records

**Entity-Relationship Diagrams (ERD)**
• Visual representation of database structure
• Shows entities, attributes, and relationships
• Planning tool before implementation`
            },
            {
              heading: "3. Introduction to SQL",
              content: `**Basic SQL Commands:**

**SELECT - Retrieve data**
SELECT column1, column2 FROM table_name WHERE condition;

**INSERT - Add data**
INSERT INTO table_name (columns) VALUES (values);

**UPDATE - Modify data**
UPDATE table_name SET column = value WHERE condition;

**DELETE - Remove data**
DELETE FROM table_name WHERE condition;

**Example:**
SELECT StudentName, Grade FROM Students WHERE Grade > 80;`
            }
          ],
          reviewQuestions: [
            "Define primary key and explain its importance",
            "What is normalization and why is it important?",
            "Write a SQL query to find all students with grades above 75"
          ],
          keyPoints: [
            "Primary key uniquely identifies each record - cannot be null or duplicate",
            "Normalization reduces data redundancy and improves integrity",
            "SQL: SELECT * FROM Students WHERE Grade > 75"
          ]
        }
      ]
    },
    {
      id: "strand3",
      number: 3,
      title: "Web Development Basics",
      color: "purple",
      icon: Globe,
      units: [
        {
          id: "3.1",
          title: "HTML & CSS Fundamentals",
          overview: "Create and style web pages using HTML and CSS.",
          keyTerms: ["HTML", "CSS", "Tags", "Elements", "Attributes", "Selectors", "Properties"],
          notes: [
            {
              heading: "1. HTML Structure",
              content: `**Basic HTML Document:**
<!DOCTYPE html>
<html>
<head>
    <title>Page Title</title>
</head>
<body>
    <h1>Main Heading</h1>
    <p>Paragraph text</p>
</body>
</html>

**Common Tags:**
• <h1> to <h6>: Headings
• <p>: Paragraph
• <a href="">: Links
• <img src="">: Images
• <ul>, <ol>, <li>: Lists
• <table>, <tr>, <td>: Tables`
            },
            {
              heading: "2. CSS Styling",
              content: `**CSS Syntax:**
selector {
    property: value;
}

**Adding CSS:**
• Inline: style="color: red;"
• Internal: <style> in <head>
• External: Link to .css file

**Common Properties:**
• color, background-color
• font-size, font-family
• margin, padding, border
• width, height

**Selectors:**
• Element: p { }
• Class: .classname { }
• ID: #idname { }`
            },
            {
              heading: "3. Page Layout",
              content: `**Box Model**
• Content: The actual content
• Padding: Space around content
• Border: Edge around padding
• Margin: Space outside border

**Layout Techniques:**
• Float and clear
• Flexbox for flexible layouts
• CSS Grid for complex layouts

**Responsive Design:**
• Media queries for different screens
• Relative units (%, em, rem)
• Mobile-first approach`
            }
          ],
          reviewQuestions: [
            "What is the difference between inline, internal, and external CSS?",
            "Explain the CSS box model",
            "Write HTML code for a table with 3 rows and 2 columns"
          ],
          keyPoints: [
            "External CSS is preferred for maintainability and reusability",
            "Box model: Content → Padding → Border → Margin (inside out)",
            "Use semantic HTML tags for better accessibility and SEO"
          ]
        }
      ]
    },
    {
      id: "strand4",
      number: 4,
      title: "Digital Citizenship & Safety",
      color: "red",
      icon: Shield,
      units: [
        {
          id: "4.1",
          title: "Online Safety & Ethics",
          overview: "Understand responsible technology use, online safety, and ethical considerations.",
          keyTerms: ["Privacy", "Security", "Copyright", "Plagiarism", "Cyberbullying", "Digital Footprint"],
          notes: [
            {
              heading: "1. Online Safety",
              content: `**Password Security**
• Use strong, unique passwords
• Mix uppercase, lowercase, numbers, symbols
• Don't share passwords
• Use two-factor authentication

**Recognizing Threats:**
• Phishing: Fake emails/websites
• Malware: Harmful software
• Social engineering: Manipulation
• Scams: Fraudulent schemes

**Safe Browsing:**
• Check for HTTPS
• Don't click suspicious links
• Update software regularly
• Use antivirus protection`
            },
            {
              heading: "2. Digital Ethics",
              content: `**Copyright & Intellectual Property**
• Respect creators' rights
• Cite sources properly
• Use licensed content
• Understand fair use

**Avoiding Plagiarism:**
• Use your own words
• Quote and cite properly
• Use plagiarism checkers
• Give credit to sources

**Responsible Sharing:**
• Think before you post
• Respect others' privacy
• Get permission for photos
• Don't spread misinformation`
            },
            {
              heading: "3. Digital Footprint",
              content: `**What is Digital Footprint?**
• Record of online activity
• Permanent and searchable
• Affects reputation

**Managing Your Footprint:**
• Privacy settings
• Think before posting
• Regular account reviews
• Google yourself periodically

**Cyberbullying:**
• Don't participate or encourage
• Report and block
• Save evidence
• Tell a trusted adult`
            }
          ],
          reviewQuestions: [
            "List 5 characteristics of a strong password",
            "What is phishing and how can you identify it?",
            "Why is it important to manage your digital footprint?"
          ],
          keyPoints: [
            "Strong password: 12+ chars, mixed case, numbers, symbols, unique",
            "Phishing signs: Urgent tone, suspicious links, spelling errors, requests for personal info",
            "Digital footprint is permanent - future employers and schools may check"
          ]
        }
      ]
    }
  ]
};

// ==================== CHEMISTRY NOTES DATA ====================
const year11ChemistryNotesData = {
  subject: "Year 11 Chemistry",
  strands: [
    {
      id: "strand1",
      number: 1,
      title: "Atomic Structure, Bonding and related properties",
      color: "green",
      icon: Beaker,
      units: [
        {
          id: "1.1",
          title: "Atomic Structure",
          overview: "Understanding the structure of atoms, subatomic particles, and electronic configuration.",
          keyTerms: ["Atom", "Proton", "Neutron", "Electron", "Nucleus", "Isotope", "Ion"],
          notes: [
            {
              heading: "1. Subatomic Particles",
              content: `**Protons** (p⁺)
• Mass: 1 amu, Charge: +1
• Located in the nucleus
• Number of protons = Atomic number (Z)

**Neutrons** (n⁰)
• Mass: 1 amu, Charge: 0
• Located in the nucleus
• Mass number = protons + neutrons

**Electrons** (e⁻)
• Mass: ~0 amu, Charge: -1
• Orbit the nucleus in shells
• Number = protons in neutral atom`
            },
            {
              heading: "2. Electronic Configuration",
              content: `**Shell Capacity**
• 1st shell: 2 electrons (max)
• 2nd shell: 8 electrons (max)
• 3rd shell: 8 electrons (max for first 20 elements)

**Writing Configurations**
• Fill inner shells first
• Example: Sodium (11): 2,8,1
• Example: Chlorine (17): 2,8,7`
            }
          ],
          keyPoints: [
            "Atoms are made of protons, neutrons, and electrons",
            "Atomic number = number of protons, defines the element",
            "Isotopes have same protons, different neutrons"
          ]
        },
        {
          id: "1.2",
          title: "Chemical Bonding",
          overview: "Understanding ionic, covalent, and metallic bonding.",
          keyTerms: ["Ionic Bond", "Covalent Bond", "Metallic Bond", "Electronegativity"],
          notes: [
            {
              heading: "1. Ionic Bonding",
              content: `**Formation**
• Metal + Non-metal
• Electron transfer occurs
• Forms positive and negative ions

**Properties of Ionic Compounds**
• High melting/boiling points
• Conduct electricity when molten/dissolved
• Form crystalline structures`
            },
            {
              heading: "2. Covalent Bonding",
              content: `**Formation**
• Non-metal + Non-metal
• Electrons are shared
• Forms molecules

**Properties of Covalent Compounds**
• Lower melting/boiling points
• Do not conduct electricity
• Can be solids, liquids, or gases`
            }
          ],
          keyPoints: [
            "Ionic bonds: electron transfer between metal and non-metal",
            "Covalent bonds: electron sharing between non-metals",
            "Bond type affects properties"
          ]
        }
      ]
    },
    {
      id: "strand2",
      number: 2,
      title: "Quantitative and Aqueous Chemistry",
      color: "teal",
      icon: Beaker,
      units: [
        {
          id: "2.1",
          title: "Balancing Equations",
          overview: "Writing and balancing chemical equations.",
          keyTerms: ["Reactants", "Products", "Coefficients", "Conservation of Mass"],
          notes: [
            {
              heading: "Balancing Chemical Equations",
              content: `**Steps to Balance**
1. Write the unbalanced equation
2. Count atoms on each side
3. Add coefficients to balance
4. Check your work

**Example**
H₂ + O₂ → H₂O (unbalanced)
2H₂ + O₂ → 2H₂O (balanced)`
            }
          ],
          keyPoints: [
            "Mass is conserved in chemical reactions",
            "Only change coefficients, never subscripts",
            "Balance one element at a time"
          ]
        }
      ]
    },
    {
      id: "strand3",
      number: 3,
      title: "Physical Chemistry",
      color: "purple",
      icon: Beaker,
      units: [
        {
          id: "3.1",
          title: "The Mole",
          overview: "Understanding Avogadro's number and molar calculations.",
          keyTerms: ["Mole", "Avogadro's Number", "Molar Mass", "Relative Atomic Mass"],
          notes: [
            {
              heading: "The Mole Concept",
              content: `**What is a Mole?**
• 1 mole = 6.022 × 10²³ particles (Avogadro's number)
• Can be atoms, molecules, ions, electrons, etc.

**Molar Mass**
• Mass of 1 mole of a substance in grams
• Numerically equal to relative atomic/molecular mass
• Example: H₂O = 18 g/mol (2×1 + 16)

**Key Formulas**
• n = m/M (moles = mass/molar mass)
• n = N/Nₐ (moles = particles/Avogadro's number)`
            }
          ],
          keyPoints: [
            "1 mole = 6.022 × 10²³ particles",
            "Molar mass has units of g/mol",
            "n = m/M is the key formula"
          ]
        },
        {
          id: "3.2",
          title: "Mole Calculations",
          overview: "Performing calculations involving moles, mass, and particles.",
          keyTerms: ["Mole Ratio", "Reacting Masses", "Percentage Composition"],
          notes: [
            {
              heading: "Mole Calculations",
              content: `**Types of Calculations**
1. Mass to Moles: n = m/M
2. Moles to Mass: m = n × M
3. Moles to Particles: N = n × Nₐ
4. Particles to Moles: n = N/Nₐ

**Example**
How many moles in 36g of water?
n = m/M = 36/18 = 2 moles`
            }
          ],
          keyPoints: [
            "Always identify what you're given and what you need",
            "Use the triangle: m = n × M",
            "Check units in your answer"
          ]
        }
      ]
    },
    {
      id: "strand4",
      number: 4,
      title: "Oxidation and Reduction",
      color: "orange",
      icon: Beaker,
      units: [
        {
          id: "4.1",
          title: "Properties of Acids and Bases",
          overview: "Understanding acid and base characteristics.",
          keyTerms: ["Acid", "Base", "Alkali", "Indicator", "pH"],
          notes: [
            {
              heading: "Acids and Bases",
              content: `**Acids**
• Sour taste, pH < 7
• React with metals to produce hydrogen
• React with carbonates to produce CO₂
• Turn litmus red

**Bases**
• Bitter taste, slippery feel, pH > 7
• Turn litmus blue
• Alkalis are soluble bases

**Common Indicators**
• Litmus: Red in acid, Blue in base
• Phenolphthalein: Colorless in acid, Pink in base
• Universal indicator: Shows pH range`
            }
          ],
          keyPoints: [
            "Acids donate H⁺ ions",
            "Bases accept H⁺ ions",
            "pH 7 is neutral"
          ]
        },
        {
          id: "4.2",
          title: "Neutralization Reactions",
          overview: "Reactions between acids and bases to form salts.",
          keyTerms: ["Neutralization", "Salt", "Titration", "Endpoint"],
          notes: [
            {
              heading: "Neutralization",
              content: `**General Equation**
Acid + Base → Salt + Water

**Examples**
• HCl + NaOH → NaCl + H₂O
• H₂SO₄ + 2NaOH → Na₂SO₄ + 2H₂O
• HNO₃ + KOH → KNO₃ + H₂O

**Making Salts**
1. Choose correct acid for the anion
2. Choose correct base for the cation
3. HCl → chlorides, H₂SO₄ → sulfates, HNO₃ → nitrates`
            }
          ],
          keyPoints: [
            "Neutralization produces salt and water",
            "The salt depends on the acid and base used",
            "Titration measures concentration"
          ]
        }
      ]
    },
    {
      id: "strand5",
      number: 5,
      title: "Organic Chemistry",
      color: "red",
      icon: Beaker,
      units: [
        {
          id: "5.1",
          title: "Introduction to Carbon Compounds",
          overview: "Understanding why carbon forms so many compounds.",
          keyTerms: ["Organic Chemistry", "Hydrocarbon", "Tetravalent", "Catenation"],
          notes: [
            {
              heading: "Carbon - The Special Element",
              content: `**Why is Carbon Special?**
• Tetravalent: Forms 4 covalent bonds
• Catenation: Can bond to itself forming chains
• Forms stable bonds with H, O, N, S, halogens

**Types of Carbon Compounds**
• Hydrocarbons: Only C and H
• Contain functional groups for other elements

**Classification**
• Aliphatic: Chain structures (alkanes, alkenes, alkynes)
• Aromatic: Ring structures (benzene)`
            }
          ],
          keyPoints: [
            "Carbon forms 4 bonds (tetravalent)",
            "Catenation allows long carbon chains",
            "Hydrocarbons contain only C and H"
          ]
        },
        {
          id: "5.2",
          title: "Alkanes",
          overview: "The simplest hydrocarbons with single bonds only.",
          keyTerms: ["Alkane", "Saturated", "Homologous Series", "General Formula"],
          notes: [
            {
              heading: "Alkanes",
              content: `**General Formula**: CₙH₂ₙ₊₂

**Properties**
• Saturated (single bonds only)
• Low reactivity
• Undergo combustion and substitution

**Naming (First 10)**
1. Methane (CH₄)
2. Ethane (C₂H₆)
3. Propane (C₃H₈)
4. Butane (C₄H₁₀)
5. Pentane (C₅H₁₂)
6. Hexane (C₆H₁₄)
7. Heptane (C₇H₁₆)
8. Octane (C₈H₁₈)
9. Nonane (C₉H₂₀)
10. Decane (C₁₀H₂₂)`
            }
          ],
          keyPoints: [
            "Alkanes: CₙH₂ₙ₊₂",
            "All single bonds (saturated)",
            "Prefixes: meth-, eth-, prop-, but-, pent-"
          ]
        }
      ]
    }
  ]
};

const year12ChemistryNotesData = {
  subject: "Year 12 Chemistry",
  strands: [
    {
      id: "strand1",
      number: 1,
      title: "Atomic Structure, Bonding and related properties",
      color: "green",
      icon: Beaker,
      units: [
        {
          id: "1.1",
          title: "Stoichiometry Calculations",
          overview: "Using mole calculations in chemical reactions.",
          keyTerms: ["Mole", "Molar Mass", "Limiting Reagent", "Yield"],
          notes: [
            {
              heading: "1. The Mole Concept",
              content: `**Avogadro's Number**
• 1 mole = 6.022 × 10²³ particles
• This applies to atoms, molecules, ions, etc.

**Molar Mass**
• Mass of 1 mole of a substance
• Units: g/mol
• Equal to relative atomic/molecular mass in grams`
            },
            {
              heading: "2. Stoichiometric Calculations",
              content: `**Steps**
1. Write balanced equation
2. Convert given amount to moles
3. Use mole ratio from equation
4. Convert to required units

**Limiting Reagent**
• The reactant that runs out first
• Limits the amount of product formed`
            }
          ],
          keyPoints: [
            "1 mole = 6.022 × 10²³ particles",
            "Use mole ratios from balanced equations",
            "Limiting reagent determines maximum product"
          ]
        },
        {
          id: "1.2",
          title: "Thermochemistry",
          overview: "Energy changes in chemical reactions.",
          keyTerms: ["Enthalpy", "Exothermic", "Endothermic", "Hess's Law"],
          notes: [
            {
              heading: "1. Enthalpy Changes",
              content: `**Exothermic Reactions**
• Release heat to surroundings
• ΔH is negative
• Products have less energy than reactants

**Endothermic Reactions**
• Absorb heat from surroundings
• ΔH is positive
• Products have more energy than reactants`
            },
            {
              heading: "2. Hess's Law",
              content: `**Statement**
Total enthalpy change is independent of the route taken.

**Application**
• Can calculate ΔH for reactions that are difficult to measure directly
• Add enthalpy changes of intermediate steps`
            }
          ],
          keyPoints: [
            "Exothermic: releases heat, ΔH < 0",
            "Endothermic: absorbs heat, ΔH > 0",
            "Hess's Law: total ΔH is route independent"
          ]
        }
      ]
    },
    {
      id: "strand2",
      number: 2,
      title: "Quantitative and Aqueous Chemistry",
      color: "teal",
      icon: Beaker,
      units: [
        {
          id: "2.1",
          title: "Acids and Bases",
          overview: "Properties and reactions of acids and bases.",
          keyTerms: ["Acid", "Base", "pH", "Neutralization", "Buffer"],
          notes: [
            {
              heading: "pH Scale",
              content: `**pH Definition**
• pH = -log[H⁺]
• Scale from 0-14
• pH 7 = neutral
• pH < 7 = acidic
• pH > 7 = alkaline`
            }
          ],
          keyPoints: [
            "Acids donate H⁺ ions",
            "Bases accept H⁺ ions",
            "Neutralization produces salt + water"
          ]
        }
      ]
    },
    {
      id: "strand3",
      number: 3,
      title: "Physical Chemistry",
      color: "purple",
      icon: Beaker,
      units: [
        {
          id: "3.1",
          title: "Oxidation and Reduction",
          overview: "Understanding electron transfer in reactions.",
          keyTerms: ["Oxidation", "Reduction", "Oxidizing Agent", "Reducing Agent", "Oxidation State"],
          notes: [
            {
              heading: "Redox Definitions",
              content: `**OIL RIG**
• Oxidation Is Loss (of electrons)
• Reduction Is Gain (of electrons)

**Oxidation States**
• Rules for assigning oxidation numbers
• Free elements = 0
• Monatomic ions = charge
• O usually -2, H usually +1

**Agents**
• Oxidizing agent: causes oxidation, gets reduced
• Reducing agent: causes reduction, gets oxidized`
            }
          ],
          keyPoints: [
            "Oxidation = loss of electrons",
            "Reduction = gain of electrons",
            "Redox reactions involve electron transfer"
          ]
        }
      ]
    },
    {
      id: "strand4",
      number: 4,
      title: "Oxidation and Reduction",
      color: "orange",
      icon: Beaker,
      units: [
        {
          id: "4.1",
          title: "Alkenes and Alkynes",
          overview: "Unsaturated hydrocarbons with double and triple bonds.",
          keyTerms: ["Alkene", "Alkyne", "Unsaturated", "Addition Reaction"],
          notes: [
            {
              heading: "Unsaturated Hydrocarbons",
              content: `**Alkenes (CₙH₂ₙ)**
• Contain C=C double bond
• More reactive than alkanes
• Undergo addition reactions

**Alkynes (CₙH₂ₙ₋₂)**
• Contain C≡C triple bond
• Even more reactive
• Examples: ethyne (acetylene)

**Addition Reactions**
• Hydrogenation: Add H₂
• Halogenation: Add Br₂, Cl₂
• Hydration: Add H₂O`
            }
          ],
          keyPoints: [
            "Alkenes: CₙH₂ₙ (double bond)",
            "Alkynes: CₙH₂ₙ₋₂ (triple bond)",
            "Addition reactions break the π bond"
          ]
        }
      ]
    },
    {
      id: "strand5",
      number: 5,
      title: "Organic Chemistry",
      color: "red",
      icon: Beaker,
      units: [
        {
          id: "5.1",
          title: "Qualitative Analysis",
          overview: "Identifying ions and compounds through chemical tests.",
          keyTerms: ["Flame Test", "Precipitate", "Gas Tests", "Cation", "Anion"],
          notes: [
            {
              heading: "Tests for Ions",
              content: `**Flame Tests (Cations)**
• Lithium: Crimson red
• Sodium: Yellow/orange
• Potassium: Lilac
• Calcium: Orange-red
• Barium: Green
• Copper: Blue-green

**Tests for Anions**
• Carbonates: Add acid → CO₂ (limewater turns milky)
• Sulfates: Add BaCl₂ → white precipitate
• Halides: Add AgNO₃
  - Chloride: white ppt
  - Bromide: cream ppt
  - Iodide: yellow ppt`
            }
          ],
          keyPoints: [
            "Flame tests identify metal cations",
            "Precipitation tests identify anions",
            "Silver nitrate tests for halides"
          ]
        }
      ]
    }
  ]
};

const year13ChemistryNotesData = {
  subject: "Year 13 Chemistry",
  strands: [
    {
      id: "strand1",
      number: 1,
      title: "Atomic Structure, Bonding and related properties",
      color: "green",
      icon: Beaker,
      units: [
        {
          id: "1.1",
          title: "Dynamic Equilibrium",
          overview: "Understanding equilibrium in reversible reactions.",
          keyTerms: ["Equilibrium", "Kc", "Le Chatelier's Principle", "Reversible"],
          notes: [
            {
              heading: "1. What is Equilibrium?",
              content: `**Dynamic Equilibrium**
• Forward rate = Reverse rate
• Concentrations remain constant
• System must be closed

**Equilibrium Constant (Kc)**
For: aA + bB ⇌ cC + dD
Kc = [C]^c[D]^d / [A]^a[B]^b`
            },
            {
              heading: "2. Le Chatelier's Principle",
              content: `**Statement**
When a system at equilibrium is disturbed, it shifts to counteract the change.

**Effects of Changes**
• Add reactant → shifts right
• Remove product → shifts right
• Increase pressure → shifts to fewer moles
• Increase temperature → shifts endothermic direction`
            }
          ],
          keyPoints: [
            "At equilibrium, rates are equal but not zero",
            "K only changes with temperature",
            "Le Chatelier predicts response to changes"
          ]
        },
        {
          id: "1.2",
          title: "Chemical Kinetics",
          overview: "Factors affecting reaction rates.",
          keyTerms: ["Rate", "Order", "Rate Constant", "Activation Energy"],
          notes: [
            {
              heading: "Rate Laws",
              content: `**Rate Equation**
Rate = k[A]^m[B]^n

**Factors Affecting Rate**
• Concentration: higher = faster
• Temperature: higher = faster
• Surface area: larger = faster
• Catalyst: lowers activation energy`
            }
          ],
          keyPoints: [
            "Rate depends on concentration and temperature",
            "Order must be determined experimentally",
            "Catalysts speed up reactions without being consumed"
          ]
        }
      ]
    },
    {
      id: "strand2",
      number: 2,
      title: "Quantitative and Aqueous Chemistry",
      color: "teal",
      icon: Beaker,
      units: [
        {
          id: "2.1",
          title: "Electrochemistry",
          overview: "Redox reactions and electrochemical cells.",
          keyTerms: ["Oxidation", "Reduction", "Cell Potential", "Electrolysis"],
          notes: [
            {
              heading: "Redox Reactions",
              content: `**OIL RIG**
• Oxidation Is Loss (of electrons)
• Reduction Is Gain (of electrons)

**Cell Potential**
E°cell = E°cathode - E°anode
Positive E° = spontaneous reaction`
            }
          ],
          keyPoints: [
            "Oxidation = loss of electrons",
            "Reduction = gain of electrons",
            "E°cell > 0 means spontaneous"
          ]
        }
      ]
    },
    {
      id: "strand3",
      number: 3,
      title: "Physical Chemistry",
      color: "purple",
      icon: Beaker,
      units: [
        {
          id: "3.1",
          title: "Transition Metal Properties",
          overview: "Understanding the unique properties of d-block elements.",
          keyTerms: ["Transition Metal", "d-orbital", "Variable Oxidation State", "Colored Compounds", "Catalyst"],
          notes: [
            {
              heading: "Transition Metals",
              content: `**Characteristic Properties**
• Variable oxidation states
• Form colored compounds
• Good catalysts
• Form complex ions

**Electronic Configuration**
• Partially filled d-orbitals
• Example: Fe = [Ar] 3d⁶ 4s²
• Cu = [Ar] 3d¹⁰ 4s¹ (exception)

**Colors of Ions**
• Cu²⁺: Blue
• Fe²⁺: Green
• Fe³⁺: Yellow/brown
• Mn²⁺: Pink
• Cr³⁺: Green`
            }
          ],
          keyPoints: [
            "Transition metals have partially filled d-orbitals",
            "Can form multiple oxidation states",
            "Colored due to d-d electron transitions"
          ]
        }
      ]
    },
    {
      id: "strand4",
      number: 4,
      title: "Oxidation and Reduction",
      color: "orange",
      icon: Beaker,
      units: [
        {
          id: "4.1",
          title: "Nucleophilic Substitution",
          overview: "SN1 and SN2 mechanisms in haloalkanes.",
          keyTerms: ["Nucleophile", "Electrophile", "SN1", "SN2", "Carbocation"],
          notes: [
            {
              heading: "Nucleophilic Substitution",
              content: `**SN2 Mechanism**
• One-step, concerted
• Nucleophile attacks as leaving group leaves
• Inversion of configuration
• Favored by: primary substrates, strong nucleophiles

**SN1 Mechanism**
• Two-step
• Step 1: Leaving group leaves → carbocation
• Step 2: Nucleophile attacks
• Racemization occurs
• Favored by: tertiary substrates, weak nucleophiles`
            }
          ],
          keyPoints: [
            "SN2: single step, backside attack",
            "SN1: two steps via carbocation",
            "Primary → SN2, Tertiary → SN1"
          ]
        }
      ]
    },
    {
      id: "strand5",
      number: 5,
      title: "Organic Chemistry",
      color: "red",
      icon: Beaker,
      units: [
        {
          id: "5.1",
          title: "Mass Spectrometry and IR Spectroscopy",
          overview: "Using instrumental methods to identify compounds.",
          keyTerms: ["Mass Spectrum", "Molecular Ion", "Fragmentation", "IR Spectrum", "Functional Groups"],
          notes: [
            {
              heading: "Spectroscopy",
              content: `**Mass Spectrometry**
• Molecular ion peak (M⁺) gives molecular mass
• Fragmentation pattern helps identify structure
• M+1 peak due to ¹³C

**Infrared Spectroscopy**
• O-H (alcohol): 3200-3600 cm⁻¹ (broad)
• O-H (acid): 2500-3300 cm⁻¹ (very broad)
• C=O: 1680-1750 cm⁻¹ (strong)
• N-H: 3300-3500 cm⁻¹
• C-H: 2850-3100 cm⁻¹

**NMR Spectroscopy**
• ¹H NMR: Identifies hydrogen environments
• Chemical shift (δ) indicates environment
• Integration gives ratio of H atoms
• Splitting pattern shows adjacent H atoms`
            }
          ],
          keyPoints: [
            "Mass spec gives molecular mass from M⁺ peak",
            "IR identifies functional groups",
            "NMR identifies structural features"
          ]
        }
      ]
    }
  ]
};

// Helper function to get notes data by year and subject
const getNotesData = (year, subject = 'ict') => {
  if (subject === 'chemistry') {
    if (year === 'year11') return year11ChemistryNotesData;
    if (year === 'year12') return year12ChemistryNotesData;
    return year13ChemistryNotesData;
  }
  if (subject === 'physics') {
    // Convert Physics student notes to the expected format
    const physicsNotesMap = {
      year11: year11PhysicsStudentNotes,
      year12: year12PhysicsStudentNotes,
      year13: year13PhysicsStudentNotes
    };
    const notes = physicsNotesMap[year];
    if (!notes) return null;
    
    // Convert to expected data structure
    return {
      subject: `Year ${year.replace('year', '')} Physics`,
      strands: Object.entries(notes).map(([key, strand]) => ({
        id: key,
        number: key.replace('strand', ''),
        title: strand.title,
        overview: strand.overview,
        image: strand.image,
        units: strand.units || []
      }))
    };
  }
  if (subject === 'economics') {
    // Convert Economics student notes to the expected format
    const economicsNotesMap = {
      year11: year11EconomicsStudentNotes,
      year12: year12EconomicsStudentNotes,
      year13: year13EconomicsStudentNotes
    };
    const notes = economicsNotesMap[year];
    if (!notes) return null;
    
    // Convert to expected data structure
    return {
      subject: `Year ${year.replace('year', '')} Economics`,
      strands: Object.entries(notes).map(([key, strand]) => ({
        id: key,
        number: key.replace('strand', ''),
        title: strand.title,
        overview: strand.overview,
        image: strand.image,
        units: strand.units || []
      }))
    };
  }
  if (subject === 'french') {
    // Convert French student notes to the expected format (Years 7-10)
    const frenchNotesMap = {
      year7: year7FrenchStudentNotes,
      year8: year8FrenchStudentNotes,
      year9: year9FrenchStudentNotes,
      year10: year10FrenchStudentNotes
    };
    const notes = frenchNotesMap[year];
    if (!notes) return null;
    
    // Convert to expected data structure
    return {
      subject: `Year ${year.replace('year', '')} French`,
      strands: Object.entries(notes).map(([key, strand]) => ({
        id: key,
        number: key.replace('strand', ''),
        title: strand.title,
        overview: strand.overview,
        image: strand.image,
        units: strand.units || []
      }))
    };
  }
  if (subject === 'geography') {
    // Convert Geography student notes to the expected format (Years 11-13)
    const geographyNotesMap = {
      year11: year11GeographyStudentNotes,
      year12: year12GeographyStudentNotes,
      year13: year13GeographyStudentNotes
    };
    const notes = geographyNotesMap[year];
    if (!notes) return null;
    
    // Convert to expected data structure
    return {
      subject: `Year ${year.replace('year', '')} Geography`,
      strands: Object.entries(notes).map(([key, strand]) => ({
        id: key,
        number: key.replace('strand', ''),
        title: strand.title,
        overview: strand.overview,
        image: strand.image,
        units: strand.units || []
      }))
    };
  }
  if (subject === 'mathematics') {
    // Convert Mathematics student notes to the expected format (Years 11-13)
    const mathematicsNotesMap = {
      year11: year11MathematicsStudentNotes,
      year12: year12MathematicsStudentNotes,
      year13: year13MathematicsStudentNotes
    };
    const notes = mathematicsNotesMap[year];
    if (!notes) return null;
    
    // Convert to expected data structure
    return {
      subject: `Year ${year.replace('year', '')} Mathematics`,
      strands: Object.entries(notes).map(([key, strand]) => ({
        id: key,
        number: key.replace('strand', ''),
        title: strand.title,
        overview: strand.overview,
        image: strand.image,
        units: strand.units || []
      }))
    };
  }
  if (subject === 'accounting') {
    // Convert Accounting student notes to the expected format (Years 11-13)
    const accountingNotesMap = {
      year11: year11AccountingStudentNotes,
      year12: year12AccountingStudentNotes,
      year13: year13AccountingStudentNotes
    };
    const notes = accountingNotesMap[year];
    if (!notes) return null;
    
    // Convert to expected data structure
    return {
      subject: `Year ${year.replace('year', '')} Accounting`,
      strands: Object.entries(notes).map(([key, strand]) => ({
        id: key,
        number: key.replace('strand', ''),
        title: strand.title,
        overview: strand.overview,
        image: strand.image,
        units: strand.units || []
      }))
    };
  }
  if (subject === 'esl') {
    // Convert ESL student notes to the expected format (Years 11-13)
    const eslNotesMap = {
      year11: year11ESLStudentNotes,
      year12: year12ESLStudentNotes,
      year13: year13ESLStudentNotes
    };
    const notes = eslNotesMap[year];
    if (!notes) return null;
    
    // Convert to expected data structure
    return {
      subject: `Year ${year.replace('year', '')} English (ESL)`,
      strands: Object.entries(notes).map(([key, strand]) => ({
        id: key,
        number: key.replace('strand', ''),
        title: strand.title,
        overview: strand.overview,
        image: strand.image,
        units: strand.units || []
      }))
    };
  }
  if (subject === 'history') {
    // Convert History student notes to the expected format (Years 11-13)
    const historyNotesMap = {
      year11: year11HistoryStudentNotes,
      year12: year12HistoryStudentNotes,
      year13: year13HistoryStudentNotes
    };
    const notes = historyNotesMap[year];
    if (!notes) return null;
    
    // Convert to expected data structure
    return {
      subject: `Year ${year.replace('year', '')} History`,
      strands: Object.entries(notes).map(([key, strand]) => ({
        id: key,
        number: key.replace('strand', ''),
        title: strand.title,
        overview: strand.overview,
        image: strand.image,
        units: strand.units || []
      }))
    };
  }
  // Default to ICT
  if (year === 'year11') return year13NotesData; // Use year13 as fallback for year11 ICT
  if (year === 'year12') return year12NotesData;
  return year13NotesData;
};

// ==================== ASSESSMENTS DATA ====================
const year13AssessmentsData = {
  subject: "Year 13 ICT",
  strands: [
    {
      id: "strand1",
      number: 1,
      title: "ICT Trends & Information Systems",
      color: "blue",
      icon: Globe,
      assessments: [
        {
          id: "quiz-1.1",
          type: "Quiz",
          title: "Unit 1.1 - ICT Trends Quiz",
          totalMarks: 15,
          timeLimit: "15 minutes",
          questions: [
            {
              id: 1,
              type: "multiple_choice",
              question: "Which technology allows doctors to consult with patients remotely via video calls?",
              options: ["Virtual Reality", "Telemedicine", "Cloud Computing", "5G Network"],
              correctAnswer: "Telemedicine",
              explanation: "Telemedicine enables remote consultations between doctors and patients using video calls, which is especially important for remote areas like many islands in Vanuatu."
            },
            {
              id: 2,
              type: "multiple_choice",
              question: "What does IoT stand for?",
              options: ["Internet of Technology", "Internet of Things", "Integrated Online Tools", "Internal Operating Technology"],
              correctAnswer: "Internet of Things",
              explanation: "IoT (Internet of Things) refers to the network of physical devices that are connected to the internet and can communicate with each other, like smart home devices."
            },
            {
              id: 3,
              type: "multiple_choice",
              question: "Which ICT trend uses adaptive learning platforms that adjust to a student's pace?",
              options: ["Cloud Computing", "5G Technology", "AI-Powered Personalized Learning", "Blockchain"],
              correctAnswer: "AI-Powered Personalized Learning",
              explanation: "AI-Powered Personalized Learning uses artificial intelligence to create adaptive learning experiences that adjust to each student's individual pace and needs."
            },
            {
              id: 4,
              type: "true_false",
              question: "Virtual Reality (VR) can only be used for gaming and entertainment.",
              correctAnswer: false,
              explanation: "False. VR is used in many fields including education (virtual field trips), healthcare (surgical training), and real estate (virtual property tours)."
            },
            {
              id: 5,
              type: "short_answer",
              question: "List TWO key features of ICT trends and provide an example for each.",
              sampleAnswer: "1. Connectivity - IoT devices communicating via Wi-Fi\n2. Automation - AI scheduling appointments automatically\n3. Personalization - Adaptive learning platforms adjusting to student needs\n4. Accessibility - Mobile health apps available to everyone\n5. Efficiency - Cloud computing reducing hardware needs",
              marks: 4
            }
          ]
        },
        {
          id: "quiz-1.2",
          type: "Quiz",
          title: "Unit 1.2 - Workflows & Information Systems Quiz",
          totalMarks: 15,
          timeLimit: "15 minutes",
          questions: [
            {
              id: 1,
              type: "multiple_choice",
              question: "What are the three main components of a workflow?",
              options: ["Hardware, Software, Data", "Input, Process, Output", "Planning, Implementation, Testing", "Users, Systems, Networks"],
              correctAnswer: "Input, Process, Output",
              explanation: "A workflow consists of Input (data entering), Process (actions performed), and Output (results produced)."
            },
            {
              id: 2,
              type: "multiple_choice",
              question: "Which system is specifically designed to manage student data throughout their educational journey?",
              options: ["TMS", "MIS", "SIS", "DSS"],
              correctAnswer: "SIS",
              explanation: "SIS (Student Information System) is designed to manage student enrollment, attendance, grades, and other educational data."
            },
            {
              id: 3,
              type: "multiple_choice",
              question: "What is the correct order of SDLC phases?",
              options: [
                "Design, Planning, Analysis, Testing, Implementation, Maintenance",
                "Planning, Analysis, Design, Implementation, Testing, Maintenance",
                "Analysis, Planning, Design, Testing, Implementation, Maintenance",
                "Planning, Design, Analysis, Implementation, Maintenance, Testing"
              ],
              correctAnswer: "Planning, Analysis, Design, Implementation, Testing, Maintenance",
              explanation: "The Software Development Life Cycle (SDLC) follows: Planning → Analysis → Design → Implementation → Testing → Maintenance."
            },
            {
              id: 4,
              type: "true_false",
              question: "A Tourism Management System (TMS) can help manage bookings, customer data, and tour packages.",
              correctAnswer: true,
              explanation: "True. A TMS is designed to handle all aspects of tourism business operations including reservations, customer databases, and tour management."
            },
            {
              id: 5,
              type: "short_answer",
              question: "Describe the workflow for student enrollment at a school. Include at least 4 steps.",
              sampleAnswer: "1. Student submits application form (Input)\n2. Office staff verifies documents (Process)\n3. Application is reviewed and approved (Process)\n4. Student is registered in the SIS (Process)\n5. Class assignment is made (Process)\n6. Student receives timetable and welcome pack (Output)",
              marks: 4
            }
          ]
        },
        {
          id: "test-1.3",
          type: "Test",
          title: "Unit 1.3 - ICT Infrastructure & Networking Test",
          totalMarks: 20,
          timeLimit: "30 minutes",
          questions: [
            {
              id: 1,
              type: "multiple_choice",
              question: "Which type of network covers the smallest geographical area?",
              options: ["WAN", "MAN", "LAN", "Internet"],
              correctAnswer: "LAN",
              explanation: "LAN (Local Area Network) covers the smallest area, typically a building or campus, such as a school computer lab."
            },
            {
              id: 2,
              type: "multiple_choice",
              question: "Which of the following is an example of open-source software?",
              options: ["Microsoft Windows", "Adobe Photoshop", "Linux", "Microsoft Office"],
              correctAnswer: "Linux",
              explanation: "Linux is open-source software - its source code is freely available and can be modified and distributed."
            },
            {
              id: 3,
              type: "matching",
              question: "Match the network type with its description:",
              pairs: [
                { term: "LAN", definition: "Covers a building or campus" },
                { term: "MAN", definition: "Covers a city or metropolitan area" },
                { term: "WAN", definition: "Covers countries or the entire world" },
                { term: "Wi-Fi", definition: "Wireless network using radio waves" }
              ]
            },
            {
              id: 4,
              type: "short_answer",
              question: "Compare open-source and proprietary software. Give TWO advantages of each.",
              sampleAnswer: "Open-Source Advantages:\n1. Usually free to use\n2. Can be customized and modified\n3. Community support available\n4. Transparent security (code can be reviewed)\n\nProprietary Advantages:\n1. Professional vendor support\n2. Regular updates and patches\n3. Usually more user-friendly\n4. Better documentation",
              marks: 6
            },
            {
              id: 5,
              type: "short_answer",
              question: "Explain TWO challenges Vanuatu faces in implementing ICT infrastructure and suggest ONE solution for each.",
              sampleAnswer: "Challenge 1: Limited internet connectivity in remote areas\nSolution: Satellite internet or mobile data towers\n\nChallenge 2: Unreliable power supply\nSolution: Solar-powered systems\n\nChallenge 3: Shortage of trained IT professionals\nSolution: Local IT training programs\n\nChallenge 4: High cost of equipment\nSolution: Use open-source software to reduce costs",
              marks: 6
            }
          ]
        },
        {
          id: "exercise-1.1",
          type: "Exercise",
          title: "Unit 1.1 - ICT Trends Exercise",
          totalMarks: 20,
          timeLimit: "25 minutes",
          questions: [
            {
              id: 1,
              type: "fill_blank",
              question: "Complete the sentence: __________ is a technology that allows devices to connect to the internet and communicate with each other.",
              correctAnswer: "IoT (Internet of Things)",
              explanation: "IoT enables everyday devices like refrigerators, lights, and thermostats to connect to the internet."
            },
            {
              id: 2,
              type: "fill_blank",
              question: "__________ uses video calls to allow doctors to consult with patients remotely.",
              correctAnswer: "Telemedicine",
              explanation: "Telemedicine is especially useful in Vanuatu where many islands are remote and far from hospitals."
            },
            {
              id: 3,
              type: "matching",
              question: "Match the ICT trend with its application:",
              pairs: [
                { term: "AI", definition: "Personalized learning platforms" },
                { term: "VR", definition: "Virtual field trips for students" },
                { term: "Cloud Computing", definition: "Storing files online (Google Drive)" },
                { term: "5G", definition: "Faster mobile internet speeds" }
              ]
            },
            {
              id: 4,
              type: "short_answer",
              question: "List THREE ways ICT trends are changing education in schools.",
              sampleAnswer: "1. Online learning platforms allow students to learn from home\n2. Virtual Reality enables virtual field trips to places like museums\n3. AI-powered tools provide personalized learning experiences\n4. Cloud storage allows easy sharing of documents and assignments\n5. Video conferencing connects students with experts worldwide",
              marks: 6
            },
            {
              id: 5,
              type: "short_answer",
              question: "Describe how smart home technology works and give TWO examples of smart home devices.",
              sampleAnswer: "Smart home technology uses IoT to connect household devices to the internet, allowing them to be controlled remotely via smartphone or voice commands.\n\nExamples:\n1. Smart thermostat - automatically adjusts temperature based on preferences\n2. Smart lights - can be turned on/off remotely or by voice\n3. Smart security cameras - monitor home from anywhere\n4. Smart speakers (Alexa, Google Home) - control other devices by voice",
              marks: 6
            }
          ]
        },
        {
          id: "assignment-1.1",
          type: "Assignment",
          title: "Unit 1.1 - ICT Trends Research Assignment",
          totalMarks: 20,
          timeLimit: "1 week",
          questions: [
            {
              id: 1,
              type: "research",
              question: "Research and write a report (300-400 words) on ONE ICT trend and how it could benefit Vanuatu. Include:\n- What is the technology?\n- How does it work?\n- Current use in other countries\n- How it could help Vanuatu\n- Challenges to implementation",
              sampleAnswer: "Example Report on Telemedicine in Vanuatu:\n\n1. What is Telemedicine?\nTelemedicine is the use of digital technology to provide healthcare services remotely through video calls, phone consultations, and digital health monitoring.\n\n2. How it Works:\n- Patients connect with doctors via video call\n- Medical records are shared electronically\n- Prescriptions can be sent digitally to pharmacies\n- Wearable devices can monitor vital signs remotely\n\n3. Current Use:\n- Australia uses telehealth for rural communities\n- India connects village health centers to city specialists\n- USA saw 154% increase in telehealth during COVID-19\n\n4. Benefits for Vanuatu:\n- Connect outer island residents with specialists in Port Vila\n- Reduce travel costs for patients\n- Provide 24/7 access to medical advice\n- Support during natural disasters when travel is impossible\n\n5. Challenges:\n- Limited internet connectivity on outer islands\n- Need for reliable electricity\n- Training healthcare workers to use technology\n- Cost of equipment",
              marks: 15
            },
            {
              id: 2,
              type: "short_answer",
              question: "Create a simple diagram or flowchart showing how your chosen ICT trend works.",
              sampleAnswer: "Diagram should show:\n- Input/Source (e.g., patient at home)\n- Technology used (e.g., smartphone, camera)\n- Connection method (e.g., internet, 4G)\n- Output/Destination (e.g., doctor at hospital)\n- Feedback loop (e.g., prescription sent back)",
              marks: 5
            }
          ]
        }
      ]
    },
    {
      id: "strand2",
      number: 2,
      title: "Digital Media & Web Development",
      color: "green",
      icon: FileImage,
      assessments: [
        {
          id: "quiz-2.1",
          type: "Quiz",
          title: "Unit 2.1 - Digital Media Fundamentals Quiz",
          totalMarks: 15,
          timeLimit: "15 minutes",
          questions: [
            {
              id: 1,
              type: "multiple_choice",
              question: "Which image format supports transparency?",
              options: ["JPEG", "PNG", "BMP", "TIFF"],
              correctAnswer: "PNG",
              explanation: "PNG (Portable Network Graphics) supports transparency (alpha channel), making it ideal for logos and graphics that need transparent backgrounds."
            },
            {
              id: 2,
              type: "multiple_choice",
              question: "What does 'resolution' refer to in digital images?",
              options: ["File size", "Color depth", "Number of pixels", "Compression type"],
              correctAnswer: "Number of pixels",
              explanation: "Resolution refers to the number of pixels in an image, typically expressed as width × height (e.g., 1920×1080)."
            },
            {
              id: 3,
              type: "multiple_choice",
              question: "Which audio format provides the best quality but largest file size?",
              options: ["MP3", "AAC", "WAV", "OGG"],
              correctAnswer: "WAV",
              explanation: "WAV is a lossless format that preserves full audio quality but results in much larger file sizes compared to compressed formats like MP3."
            },
            {
              id: 4,
              type: "true_false",
              question: "JPEG format is best for images with sharp edges and text.",
              correctAnswer: false,
              explanation: "False. JPEG uses lossy compression which creates artifacts around sharp edges. PNG is better for images with text and sharp edges."
            },
            {
              id: 5,
              type: "short_answer",
              question: "Explain the difference between lossy and lossless compression. Give one example of each.",
              sampleAnswer: "Lossy Compression:\n- Permanently removes some data to reduce file size\n- Results in smaller files but some quality loss\n- Example: MP3 (audio), JPEG (images)\n\nLossless Compression:\n- Reduces file size without losing any data\n- Original quality can be perfectly restored\n- Example: FLAC (audio), PNG (images)",
              marks: 4
            }
          ]
        },
        {
          id: "practical-2.2",
          type: "Practical",
          title: "Unit 2.2 - CSS Styling Practical",
          totalMarks: 25,
          timeLimit: "45 minutes",
          questions: [
            {
              id: 1,
              type: "practical",
              question: "Write CSS code to style a heading with the following requirements:\n- Blue color\n- Font size 24 pixels\n- Center aligned\n- Bold font weight",
              sampleAnswer: `h1 {
    color: blue;
    font-size: 24px;
    text-align: center;
    font-weight: bold;
}`,
              marks: 5
            },
            {
              id: 2,
              type: "practical",
              question: "Create a CSS class called 'card' with:\n- White background\n- 10px padding\n- 1px solid gray border\n- 5px border radius",
              sampleAnswer: `.card {
    background-color: white;
    padding: 10px;
    border: 1px solid gray;
    border-radius: 5px;
}`,
              marks: 5
            },
            {
              id: 3,
              type: "multiple_choice",
              question: "Which CSS property is used to change the background color?",
              options: ["color", "bg-color", "background-color", "fill-color"],
              correctAnswer: "background-color",
              explanation: "The background-color property sets the background color of an element. The 'color' property is for text color."
            },
            {
              id: 4,
              type: "short_answer",
              question: "Explain the CSS Box Model and name its four components.",
              sampleAnswer: "The CSS Box Model describes how elements are rendered:\n\n1. Content - The actual content (text, images)\n2. Padding - Space between content and border\n3. Border - Line around the padding\n4. Margin - Space outside the border\n\nTotal element width = content + padding + border + margin",
              marks: 6
            },
            {
              id: 5,
              type: "practical",
              question: "Write the HTML link tag to connect an external CSS file called 'styles.css' to your HTML document.",
              sampleAnswer: `<link rel="stylesheet" href="styles.css">`,
              marks: 4
            }
          ]
        },
        {
          id: "exercise-2.1",
          type: "Exercise",
          title: "Unit 2.1 - Digital Media Exercise",
          totalMarks: 20,
          timeLimit: "30 minutes",
          questions: [
            {
              id: 1,
              type: "fill_blank",
              question: "The image format __________ supports transparency and is best for logos and graphics.",
              correctAnswer: "PNG",
              explanation: "PNG (Portable Network Graphics) supports alpha transparency, making it ideal for logos and images that need transparent backgrounds."
            },
            {
              id: 2,
              type: "fill_blank",
              question: "__________ compression permanently removes data to reduce file size, while __________ compression preserves all original data.",
              correctAnswer: "Lossy, Lossless",
              explanation: "Lossy compression (like JPEG, MP3) removes data permanently. Lossless compression (like PNG, FLAC) preserves all data."
            },
            {
              id: 3,
              type: "multiple_choice",
              question: "Which file format would you use for a photograph on a website?",
              options: ["PNG", "JPEG", "GIF", "BMP"],
              correctAnswer: "JPEG",
              explanation: "JPEG is best for photographs because it handles gradients and many colors well, and produces smaller file sizes for photos."
            },
            {
              id: 4,
              type: "short_answer",
              question: "A school wants to create a logo. Explain why PNG would be better than JPEG for this purpose.",
              sampleAnswer: "PNG is better for logos because:\n1. Supports transparency - logo can be placed on any background\n2. Lossless compression - sharp edges remain crisp\n3. No compression artifacts around text and lines\n4. Better for images with flat colors and sharp contrasts\n\nJPEG would create blurry edges around the logo and doesn't support transparency.",
              marks: 5
            },
            {
              id: 5,
              type: "practical",
              question: "List the steps to reduce the file size of an image for use on a website.",
              sampleAnswer: "Steps to reduce image file size:\n1. Choose the right format (JPEG for photos, PNG for graphics)\n2. Resize the image to needed dimensions (don't use larger than necessary)\n3. Reduce resolution to 72 DPI for web\n4. Use compression tools (TinyPNG, Photoshop Save for Web)\n5. Remove unnecessary metadata\n6. Consider using modern formats like WebP",
              marks: 5
            }
          ]
        },
        {
          id: "assignment-2.1",
          type: "Assignment",
          title: "Unit 2.2 - Web Page Design Assignment",
          totalMarks: 20,
          timeLimit: "1 week",
          questions: [
            {
              id: 1,
              type: "practical",
              question: "Create a simple HTML webpage about Vanuatu with the following:\n- A heading with the page title\n- At least 2 paragraphs of text\n- One image\n- A list of 5 facts about Vanuatu\n- Basic CSS styling (colors, fonts, layout)",
              sampleAnswer: `<!DOCTYPE html>
<html>
<head>
    <title>Welcome to Vanuatu</title>
    <style>
        body { font-family: Arial; background-color: #f0f8ff; padding: 20px; }
        h1 { color: #006400; text-align: center; }
        p { line-height: 1.6; }
        ul { background-color: #fff; padding: 20px; border-radius: 5px; }
        img { max-width: 100%; border-radius: 10px; }
    </style>
</head>
<body>
    <h1>Welcome to Vanuatu</h1>
    <img src="vanuatu.jpg" alt="Vanuatu Beach">
    <p>Vanuatu is a beautiful island nation in the South Pacific Ocean...</p>
    <p>The country is known for its diverse culture and stunning natural beauty...</p>
    <h2>5 Facts About Vanuatu</h2>
    <ul>
        <li>Vanuatu has over 80 islands</li>
        <li>There are over 100 local languages</li>
        <li>Port Vila is the capital city</li>
        <li>Vanuatu has active volcanoes</li>
        <li>The national sport is football</li>
    </ul>
</body>
</html>`,
              marks: 15
            },
            {
              id: 2,
              type: "short_answer",
              question: "Explain what CSS properties you used and why.",
              sampleAnswer: "CSS Properties Used:\n1. font-family: Arial - makes text easier to read\n2. background-color - adds visual interest to the page\n3. color - makes heading stand out\n4. text-align: center - centers the main heading\n5. padding - adds space around content\n6. border-radius - rounds corners for modern look\n7. max-width: 100% - makes image responsive\n8. line-height - improves text readability",
              marks: 5
            }
          ]
        }
      ]
    },
    {
      id: "strand3",
      number: 3,
      title: "Programming",
      color: "purple",
      icon: Code,
      assessments: [
        {
          id: "quiz-3.1",
          type: "Quiz",
          title: "Unit 3.1 - Problem Solving Quiz",
          totalMarks: 15,
          timeLimit: "15 minutes",
          questions: [
            {
              id: 1,
              type: "multiple_choice",
              question: "What is the first step in the problem-solving process?",
              options: ["Plan the solution", "Understand the problem", "Implement the solution", "Test the solution"],
              correctAnswer: "Understand the problem",
              explanation: "You must first understand what the problem is asking before you can plan or implement a solution."
            },
            {
              id: 2,
              type: "multiple_choice",
              question: "What is 'decomposition' in computational thinking?",
              options: [
                "Finding patterns in data",
                "Breaking a complex problem into smaller parts",
                "Removing unnecessary details",
                "Creating step-by-step instructions"
              ],
              correctAnswer: "Breaking a complex problem into smaller parts",
              explanation: "Decomposition means breaking down a complex problem into smaller, more manageable sub-problems."
            },
            {
              id: 3,
              type: "true_false",
              question: "Abstraction means including all details when solving a problem.",
              correctAnswer: false,
              explanation: "False. Abstraction means focusing on important details while ignoring unnecessary ones."
            },
            {
              id: 4,
              type: "short_answer",
              question: "List the four steps of the problem-solving process.",
              sampleAnswer: "1. Understand the problem\n2. Plan the solution\n3. Implement the solution\n4. Test and evaluate",
              marks: 4
            },
            {
              id: 5,
              type: "short_answer",
              question: "Write an algorithm (in pseudocode or steps) to calculate the average of three numbers.",
              sampleAnswer: "BEGIN\n    INPUT number1\n    INPUT number2\n    INPUT number3\n    sum = number1 + number2 + number3\n    average = sum / 3\n    OUTPUT average\nEND",
              marks: 4
            }
          ]
        },
        {
          id: "test-3.2",
          type: "Test",
          title: "Unit 3.2 - Programming Concepts Test",
          totalMarks: 25,
          timeLimit: "40 minutes",
          questions: [
            {
              id: 1,
              type: "multiple_choice",
              question: "Which data type would you use to store a person's name?",
              options: ["Integer", "Float", "String", "Boolean"],
              correctAnswer: "String",
              explanation: "String is used for text data like names. Integer is for whole numbers, Float for decimals, Boolean for true/false."
            },
            {
              id: 2,
              type: "multiple_choice",
              question: "What is the result of: 15 % 4?",
              options: ["3", "3.75", "4", "1"],
              correctAnswer: "3",
              explanation: "The modulus operator (%) returns the remainder. 15 ÷ 4 = 3 remainder 3, so 15 % 4 = 3."
            },
            {
              id: 3,
              type: "multiple_choice",
              question: "In a flowchart, which shape represents a decision?",
              options: ["Rectangle", "Oval", "Diamond", "Parallelogram"],
              correctAnswer: "Diamond",
              explanation: "Diamond shape represents a decision point (Yes/No question). Rectangle is for process, Oval for start/end, Parallelogram for input/output."
            },
            {
              id: 4,
              type: "short_answer",
              question: "Write pseudocode to check if a number is positive, negative, or zero.",
              sampleAnswer: "BEGIN\n    INPUT number\n    IF number > 0 THEN\n        OUTPUT \"Positive\"\n    ELSE IF number < 0 THEN\n        OUTPUT \"Negative\"\n    ELSE\n        OUTPUT \"Zero\"\n    ENDIF\nEND",
              marks: 6
            },
            {
              id: 5,
              type: "short_answer",
              question: "Write pseudocode using a FOR loop to print numbers 1 to 5.",
              sampleAnswer: "BEGIN\n    FOR counter = 1 TO 5\n        OUTPUT counter\n    ENDFOR\nEND",
              marks: 4
            },
            {
              id: 6,
              type: "short_answer",
              question: "Explain the difference between a WHILE loop and a FOR loop. When would you use each?",
              sampleAnswer: "FOR Loop:\n- Used when you know exactly how many times to repeat\n- Has a counter that automatically increments\n- Example: Print numbers 1 to 10\n\nWHILE Loop:\n- Used when you don't know how many times to repeat\n- Continues while a condition is true\n- Example: Keep asking for input until user enters 'quit'",
              marks: 6
            }
          ]
        },
        {
          id: "exercise-3.1",
          type: "Exercise",
          title: "Unit 3.1 - Problem Solving Exercise",
          totalMarks: 20,
          timeLimit: "30 minutes",
          questions: [
            {
              id: 1,
              type: "fill_blank",
              question: "The first step in problem solving is to __________ the problem.",
              correctAnswer: "Understand",
              explanation: "You must first understand what the problem is asking before you can plan or implement a solution."
            },
            {
              id: 2,
              type: "fill_blank",
              question: "Breaking a complex problem into smaller parts is called __________.",
              correctAnswer: "Decomposition",
              explanation: "Decomposition is a key computational thinking skill that helps manage complex problems."
            },
            {
              id: 3,
              type: "short_answer",
              question: "Write an algorithm (steps) to make a cup of tea.",
              sampleAnswer: "Algorithm to Make Tea:\n1. START\n2. Fill kettle with water\n3. Turn on kettle\n4. WHILE water is not boiling\n   - Wait\n5. Put tea bag in cup\n6. Pour hot water into cup\n7. Wait 3 minutes\n8. Remove tea bag\n9. Add milk and sugar (optional)\n10. Stir\n11. END",
              marks: 5
            },
            {
              id: 4,
              type: "short_answer",
              question: "A student needs to calculate the average of 5 test scores. Write the algorithm using pseudocode.",
              sampleAnswer: "BEGIN\n    total = 0\n    FOR i = 1 TO 5\n        INPUT score\n        total = total + score\n    ENDFOR\n    average = total / 5\n    OUTPUT average\nEND",
              marks: 5
            },
            {
              id: 5,
              type: "practical",
              question: "Draw a flowchart to determine if a number is odd or even.",
              sampleAnswer: "Flowchart elements:\n1. START (oval)\n2. INPUT number (parallelogram)\n3. Is number % 2 = 0? (diamond)\n4. YES → OUTPUT 'Even' (parallelogram)\n5. NO → OUTPUT 'Odd' (parallelogram)\n6. END (oval)",
              marks: 5
            }
          ]
        },
        {
          id: "assignment-3.1",
          type: "Assignment",
          title: "Unit 3.2 - Programming Assignment",
          totalMarks: 20,
          timeLimit: "1 week",
          questions: [
            {
              id: 1,
              type: "practical",
              question: "Create a program (in pseudocode) for a simple calculator that:\n- Asks user for two numbers\n- Asks which operation (+, -, *, /)\n- Performs the calculation\n- Displays the result\n- Handles division by zero error",
              sampleAnswer: "BEGIN\n    OUTPUT 'Enter first number:'\n    INPUT num1\n    OUTPUT 'Enter second number:'\n    INPUT num2\n    OUTPUT 'Enter operation (+, -, *, /):'\n    INPUT operation\n    \n    IF operation = '+' THEN\n        result = num1 + num2\n    ELSE IF operation = '-' THEN\n        result = num1 - num2\n    ELSE IF operation = '*' THEN\n        result = num1 * num2\n    ELSE IF operation = '/' THEN\n        IF num2 = 0 THEN\n            OUTPUT 'Error: Cannot divide by zero'\n            EXIT\n        ELSE\n            result = num1 / num2\n        ENDIF\n    ELSE\n        OUTPUT 'Invalid operation'\n        EXIT\n    ENDIF\n    \n    OUTPUT 'Result: ' + result\nEND",
              marks: 12
            },
            {
              id: 2,
              type: "short_answer",
              question: "Explain the purpose of each variable in your program.",
              sampleAnswer: "Variables used:\n1. num1 - stores the first number entered by user (Float/Integer)\n2. num2 - stores the second number entered by user (Float/Integer)\n3. operation - stores the mathematical operation symbol (String/Character)\n4. result - stores the calculated answer (Float/Integer)\n\nEach variable has a specific purpose and appropriate data type to store its value.",
              marks: 4
            },
            {
              id: 3,
              type: "short_answer",
              question: "What testing would you do to make sure your calculator works correctly?",
              sampleAnswer: "Test cases:\n1. Addition: 5 + 3 = 8 ✓\n2. Subtraction: 10 - 4 = 6 ✓\n3. Multiplication: 6 * 7 = 42 ✓\n4. Division: 20 / 4 = 5 ✓\n5. Division by zero: 10 / 0 = Error message ✓\n6. Invalid operation: 5 ? 3 = Error message ✓\n7. Decimal numbers: 3.5 + 2.5 = 6 ✓\n8. Negative numbers: -5 + 3 = -2 ✓",
              marks: 4
            }
          ]
        }
      ]
    },
    {
      id: "strand4",
      number: 4,
      title: "Microprocessors & Ethics",
      color: "red",
      icon: Cpu,
      assessments: [
        {
          id: "quiz-4.1",
          type: "Quiz",
          title: "Unit 4.1 - Microprocessors Quiz",
          totalMarks: 15,
          timeLimit: "15 minutes",
          questions: [
            {
              id: 1,
              type: "multiple_choice",
              question: "Which component of the CPU performs mathematical calculations?",
              options: ["Control Unit", "ALU", "Register", "Bus"],
              correctAnswer: "ALU",
              explanation: "The ALU (Arithmetic Logic Unit) performs all mathematical operations and logical comparisons in the CPU."
            },
            {
              id: 2,
              type: "multiple_choice",
              question: "What is the correct order of the CPU cycle?",
              options: ["Execute, Fetch, Decode", "Decode, Fetch, Execute", "Fetch, Decode, Execute", "Fetch, Execute, Decode"],
              correctAnswer: "Fetch, Decode, Execute",
              explanation: "The CPU cycle is: Fetch (get instruction) → Decode (interpret it) → Execute (perform the operation)."
            },
            {
              id: 3,
              type: "multiple_choice",
              question: "Which device converts physical conditions into electrical signals?",
              options: ["Actuator", "Sensor", "Processor", "Memory"],
              correctAnswer: "Sensor",
              explanation: "Sensors detect physical conditions (temperature, light, motion) and convert them to electrical signals for processing."
            },
            {
              id: 4,
              type: "true_false",
              question: "An interpreter translates the entire program at once before execution.",
              correctAnswer: false,
              explanation: "False. An interpreter translates and executes code line by line. A compiler translates the entire program at once."
            },
            {
              id: 5,
              type: "short_answer",
              question: "Give THREE examples of embedded systems and describe their function.",
              sampleAnswer: "1. Washing Machine - Controls wash cycles, water temperature, and spin speed\n2. Microwave Oven - Controls cooking time, power level, and safety features\n3. Car Engine Management - Controls fuel injection, ignition timing, and emissions\n4. Digital Watch - Keeps time, manages alarms and displays\n5. ATM - Handles banking transactions and security",
              marks: 6
            }
          ]
        },
        {
          id: "test-4.2",
          type: "Test",
          title: "Unit 4.2 - ICT Ethics Test",
          totalMarks: 20,
          timeLimit: "30 minutes",
          questions: [
            {
              id: 1,
              type: "multiple_choice",
              question: "What is copyright?",
              options: [
                "The right to copy any content freely",
                "Legal protection for original creative works",
                "A type of software license",
                "Permission to use the internet"
              ],
              correctAnswer: "Legal protection for original creative works",
              explanation: "Copyright is legal protection that gives creators exclusive rights to their original works (books, music, software, etc.)."
            },
            {
              id: 2,
              type: "multiple_choice",
              question: "What is the 'Digital Divide'?",
              options: [
                "The gap between old and new technology",
                "The gap between those with and without technology access",
                "The difference between digital and analog signals",
                "The separation of work and personal devices"
              ],
              correctAnswer: "The gap between those with and without technology access",
              explanation: "The Digital Divide refers to the inequality between people who have access to technology and those who don't."
            },
            {
              id: 3,
              type: "true_false",
              question: "Downloading movies from unauthorized websites without paying is legal.",
              correctAnswer: false,
              explanation: "False. This is piracy - illegal copying and distribution of copyrighted material."
            },
            {
              id: 4,
              type: "short_answer",
              question: "Explain THREE ways to protect your privacy online.",
              sampleAnswer: "1. Use strong, unique passwords for each account\n2. Enable two-factor authentication\n3. Be careful what personal information you share on social media\n4. Check privacy settings on apps and websites\n5. Use secure websites (https) for transactions\n6. Don't click on suspicious links or emails\n7. Use a VPN for sensitive activities",
              marks: 6
            },
            {
              id: 5,
              type: "short_answer",
              question: "What is e-waste? Explain TWO environmental problems it causes and TWO solutions.",
              sampleAnswer: "E-waste: Discarded electronic devices and components\n\nProblems:\n1. Toxic materials (lead, mercury, cadmium) can leak into soil and water\n2. E-waste often ends up in landfills or is exported to developing countries\n3. Burning e-waste releases harmful chemicals into the air\n\nSolutions:\n1. Recycle electronics at proper e-waste facilities\n2. Donate or sell working devices instead of throwing away\n3. Buy from companies with take-back programs\n4. Repair devices instead of replacing them",
              marks: 6
            },
            {
              id: 6,
              type: "short_answer",
              question: "Describe TWO health issues caused by excessive ICT use and suggest a solution for each.",
              sampleAnswer: "1. Eye Strain\n- Caused by: Long hours staring at screens\n- Solution: Follow 20-20-20 rule (every 20 minutes, look at something 20 feet away for 20 seconds)\n\n2. Repetitive Strain Injury (RSI)\n- Caused by: Repeated movements like typing or using a mouse\n- Solution: Take regular breaks, use ergonomic equipment\n\n3. Poor Posture/Back Pain\n- Caused by: Sitting incorrectly at desk\n- Solution: Use proper chair, keep screen at eye level",
              marks: 6
            }
          ]
        },
        {
          id: "exercise-4.1",
          type: "Exercise",
          title: "Unit 4.1 - Microprocessors Exercise",
          totalMarks: 20,
          timeLimit: "25 minutes",
          questions: [
            {
              id: 1,
              type: "fill_blank",
              question: "The CPU cycle consists of three steps: __________, __________, and __________.",
              correctAnswer: "Fetch, Decode, Execute",
              explanation: "The CPU fetches instructions from memory, decodes them to understand what to do, and then executes the operation."
            },
            {
              id: 2,
              type: "fill_blank",
              question: "The __________ performs mathematical calculations and logical operations in the CPU.",
              correctAnswer: "ALU (Arithmetic Logic Unit)",
              explanation: "The ALU is the part of the CPU that handles all mathematical operations like addition and logical comparisons."
            },
            {
              id: 3,
              type: "matching",
              question: "Match the component with its function:",
              pairs: [
                { term: "CPU", definition: "Processes instructions and data" },
                { term: "RAM", definition: "Temporary storage for running programs" },
                { term: "ROM", definition: "Permanent storage for startup instructions" },
                { term: "Hard Drive", definition: "Long-term data storage" }
              ]
            },
            {
              id: 4,
              type: "short_answer",
              question: "Explain the difference between a sensor and an actuator. Give one example of each.",
              sampleAnswer: "Sensor:\n- Detects physical conditions and converts them to electrical signals\n- INPUT device - sends data TO the processor\n- Examples: Temperature sensor, motion detector, light sensor, pressure sensor\n\nActuator:\n- Receives signals and performs physical actions\n- OUTPUT device - receives data FROM the processor\n- Examples: Motor, LED light, buzzer, servo",
              marks: 5
            },
            {
              id: 5,
              type: "short_answer",
              question: "Give THREE examples of embedded systems found in everyday life and explain what each does.",
              sampleAnswer: "1. Microwave Oven:\n- Controls cooking time and power level\n- Monitors door sensor for safety\n- Displays time and settings\n\n2. Washing Machine:\n- Controls water temperature and spin speed\n- Monitors water level sensor\n- Runs different wash cycle programs\n\n3. Traffic Light System:\n- Controls timing of red, yellow, green lights\n- May have sensors to detect traffic\n- Coordinates with other traffic lights",
              marks: 6
            }
          ]
        },
        {
          id: "assignment-4.1",
          type: "Assignment",
          title: "Unit 4.2 - ICT Ethics Assignment",
          totalMarks: 20,
          timeLimit: "1 week",
          questions: [
            {
              id: 1,
              type: "research",
              question: "Research and write a report (300-400 words) on ONE of the following topics:\n- Cyberbullying and its effects on young people\n- Privacy concerns with social media\n- The environmental impact of e-waste\n- Digital divide in Pacific Island countries\n\nInclude: Definition, causes, effects, and solutions.",
              sampleAnswer: "Example Report on Digital Divide in Pacific Islands:\n\n1. Definition:\nThe digital divide refers to the gap between people who have access to digital technology (computers, internet) and those who don't.\n\n2. Causes in Pacific Islands:\n- Geographic isolation - islands far from main infrastructure\n- High cost of internet and devices\n- Limited electricity in remote areas\n- Lack of ICT training and education\n- Small population spread across many islands\n\n3. Effects:\n- Students in remote areas miss out on online learning\n- Businesses cannot compete in global market\n- Limited access to government services\n- Reduced opportunities for youth\n- Healthcare information not accessible\n\n4. Solutions:\n- Satellite internet for remote islands\n- Solar-powered computer labs in schools\n- Mobile phone-based services\n- Government subsidies for internet access\n- Community ICT training programs\n- Shared computer facilities in villages",
              marks: 12
            },
            {
              id: 2,
              type: "short_answer",
              question: "Create a poster or infographic showing 5 tips for staying safe online. Describe what each tip means.",
              sampleAnswer: "5 Online Safety Tips:\n\n1. USE STRONG PASSWORDS\n- At least 12 characters\n- Mix of letters, numbers, symbols\n- Different password for each account\n\n2. THINK BEFORE YOU POST\n- Once online, always online\n- Don't share personal information\n- Would you want your grandma to see it?\n\n3. VERIFY BEFORE YOU CLICK\n- Check sender's email address\n- Hover over links before clicking\n- Don't download from unknown sources\n\n4. PROTECT YOUR PRIVACY\n- Check privacy settings on social media\n- Don't share location publicly\n- Be careful with personal photos\n\n5. REPORT SUSPICIOUS ACTIVITY\n- Tell a trusted adult\n- Report cyberbullying\n- Block and report suspicious accounts",
              marks: 8
            }
          ]
        }
      ]
    }
  ]
};

// ==================== YEAR 12 ASSESSMENTS DATA ====================
const year12AssessmentsData = {
  subject: "Year 12 ICT",
  strands: [
    {
      id: "strand1",
      number: 1,
      title: "Word Processing & Spreadsheets",
      color: "blue",
      icon: FileText,
      assessments: [
        {
          id: "1.1-assessment",
          unitId: "1.1",
          title: "Unit 1.1 Assessment: Word Processing",
          type: "Written Test",
          duration: "45 minutes",
          totalMarks: 30,
          questions: [
            { id: "q1", question: "Explain the mail merge process and list 4 applications where it is useful.", marks: 6 },
            { id: "q2", question: "What are styles in word processing? Give 3 benefits of using styles.", marks: 5 },
            { id: "q3", question: "Describe how to create an automatic table of contents.", marks: 5 },
            { id: "q4", question: "Explain the difference between section breaks and page breaks.", marks: 4 },
            { id: "q5", question: "Practical: Create a business letter template with header, footer, and merge fields.", marks: 10 }
          ],
          answers: [
            { id: "q1", answer: "Mail merge combines a main document with a data source to create personalized documents. Applications: (1) Form letters, (2) Certificates, (3) Mailing labels, (4) Envelopes, (5) Personalized emails." },
            { id: "q2", answer: "Styles are predefined formatting sets applied to text. Benefits: (1) Consistent formatting throughout document, (2) Easy to update all similar text at once, (3) Enables automatic table of contents generation." },
            { id: "q3", answer: "Apply heading styles (Heading 1, 2, 3) to headings → Position cursor where TOC should appear → Insert → Table of Contents → Choose style → Click OK. Update by right-clicking and selecting Update Field." },
            { id: "q4", answer: "Page break starts a new page while continuing the same formatting. Section break creates a new section allowing different formatting (margins, orientation, headers) for different parts of the document." },
            { id: "q5", answer: "Practical assessment - marking rubric: Header with company logo (2), Footer with page number (2), Proper business letter format (2), Merge fields correctly placed (2), Professional appearance (2)." }
          ]
        },
        {
          id: "1.2-assessment",
          unitId: "1.2",
          title: "Unit 1.2 Assessment: Spreadsheets",
          type: "Practical Test",
          duration: "60 minutes",
          totalMarks: 40,
          questions: [
            { id: "q1", question: "Write the VLOOKUP formula to find a student's grade from a table. Explain each parameter.", marks: 6 },
            { id: "q2", question: "Create a nested IF formula to assign letter grades (A: 80+, B: 65-79, C: 50-64, F: <50).", marks: 6 },
            { id: "q3", question: "What is conditional formatting? Give 3 practical uses.", marks: 5 },
            { id: "q4", question: "Explain the difference between absolute and relative cell references with examples.", marks: 5 },
            { id: "q5", question: "Practical: Create a sales report with formulas, chart, and conditional formatting.", marks: 18 }
          ],
          answers: [
            { id: "q1", answer: "=VLOOKUP(A2, $B$2:$C$10, 2, FALSE). Parameters: (1) Lookup_value - value to find, (2) Table_array - range to search, (3) Col_index_num - column number to return, (4) Range_lookup - FALSE for exact match." },
            { id: "q2", answer: "=IF(A1>=80,\"A\",IF(A1>=65,\"B\",IF(A1>=50,\"C\",\"F\"))). Tests conditions in order: first checks if >=80 returns A, else checks >=65 returns B, else checks >=50 returns C, else returns F." },
            { id: "q3", answer: "Conditional formatting automatically formats cells based on their values. Uses: (1) Highlight cells above/below average, (2) Create data bars to visualize values, (3) Use icon sets for quick status indicators." },
            { id: "q4", answer: "Relative (A1): Changes when copied - if copied down, A1 becomes A2. Absolute ($A$1): Stays fixed when copied - always refers to A1. Mixed ($A1 or A$1): One part fixed, one changes." },
            { id: "q5", answer: "Practical rubric: Correct formulas (SUM, AVERAGE, etc.) (5), Appropriate chart type with labels (4), Conditional formatting applied (3), Professional layout (3), Data accuracy (3)." }
          ]
        }
      ]
    },
    {
      id: "strand2",
      number: 2,
      title: "Database Management",
      color: "green",
      icon: Cpu,
      assessments: [
        {
          id: "2.1-assessment",
          unitId: "2.1",
          title: "Unit 2.1 Assessment: Database Fundamentals",
          type: "Written Test",
          duration: "45 minutes",
          totalMarks: 35,
          questions: [
            { id: "q1", question: "Define: (a) Database, (b) Table, (c) Record, (d) Field, (e) Primary Key", marks: 10 },
            { id: "q2", question: "Explain the three types of relationships in databases with examples.", marks: 6 },
            { id: "q3", question: "What is normalization? Why is it important?", marks: 5 },
            { id: "q4", question: "Write SQL queries to: (a) Select all students, (b) Find students with grade > 75, (c) Insert a new student", marks: 9 },
            { id: "q5", question: "Draw an ERD for a simple library system with Books, Members, and Loans tables.", marks: 5 }
          ],
          answers: [
            { id: "q1", answer: "(a) Database: Organized collection of related data. (b) Table: Collection of related records (rows). (c) Record: Single entry/row containing related data. (d) Field: Single piece of data/column. (e) Primary Key: Unique identifier for each record." },
            { id: "q2", answer: "One-to-One: One record in Table A relates to one record in Table B (Person-Passport). One-to-Many: One record relates to many records (Teacher-Students). Many-to-Many: Many records relate to many records (Students-Courses)." },
            { id: "q3", answer: "Normalization is organizing database to reduce redundancy and improve data integrity. Important because: (1) Eliminates duplicate data, (2) Ensures data consistency, (3) Makes updates easier, (4) Reduces storage space." },
            { id: "q4", answer: "(a) SELECT * FROM Students; (b) SELECT * FROM Students WHERE Grade > 75; (c) INSERT INTO Students (StudentID, Name, Grade) VALUES (101, 'John Doe', 85);" },
            { id: "q5", answer: "ERD should show: Books (BookID PK, Title, Author), Members (MemberID PK, Name, Phone), Loans (LoanID PK, BookID FK, MemberID FK, DateBorrowed, DateReturned). One-to-Many relationships between Books-Loans and Members-Loans." }
          ]
        }
      ]
    },
    {
      id: "strand3",
      number: 3,
      title: "Web Development Basics",
      color: "purple",
      icon: Globe,
      assessments: [
        {
          id: "3.1-assessment",
          unitId: "3.1",
          title: "Unit 3.1 Assessment: HTML & CSS",
          type: "Practical Test",
          duration: "60 minutes",
          totalMarks: 40,
          questions: [
            { id: "q1", question: "Write the basic HTML5 document structure with all required elements.", marks: 6 },
            { id: "q2", question: "Explain the three ways to add CSS to an HTML document. Which is preferred and why?", marks: 6 },
            { id: "q3", question: "Describe the CSS box model and its four components.", marks: 6 },
            { id: "q4", question: "Create HTML for a navigation menu with 4 links and style it horizontally using CSS.", marks: 10 },
            { id: "q5", question: "Create a simple webpage with header, navigation, main content area, and footer.", marks: 12 }
          ],
          answers: [
            { id: "q1", answer: "<!DOCTYPE html><html lang=\"en\"><head><meta charset=\"UTF-8\"><meta name=\"viewport\" content=\"width=device-width, initial-scale=1.0\"><title>Title</title></head><body></body></html>" },
            { id: "q2", answer: "Inline: style attribute on element. Internal: <style> tag in <head>. External: <link> to separate .css file. External is preferred because: (1) Separates content from presentation, (2) Can be reused across pages, (3) Browser caching improves performance." },
            { id: "q3", answer: "Box model: Content (actual content/text), Padding (space between content and border), Border (edge around padding), Margin (space outside border separating elements). Total width = content + padding + border + margin." },
            { id: "q4", answer: "HTML: <nav><ul><li><a href=\"#\">Home</a></li><li><a href=\"#\">About</a></li><li><a href=\"#\">Services</a></li><li><a href=\"#\">Contact</a></li></ul></nav>. CSS: nav ul { list-style: none; display: flex; } nav li { margin: 0 15px; } nav a { text-decoration: none; }" },
            { id: "q5", answer: "Practical rubric: Valid HTML structure (3), Semantic elements used (header, nav, main, footer) (3), CSS styling applied (3), Proper layout (2), Clean, readable code (1)." }
          ]
        }
      ]
    },
    {
      id: "strand4",
      number: 4,
      title: "Digital Citizenship & Safety",
      color: "red",
      icon: Shield,
      assessments: [
        {
          id: "4.1-assessment",
          unitId: "4.1",
          title: "Unit 4.1 Assessment: Online Safety",
          type: "Written Test",
          duration: "40 minutes",
          totalMarks: 30,
          questions: [
            { id: "q1", question: "List 5 characteristics of a strong password and explain why each is important.", marks: 5 },
            { id: "q2", question: "What is phishing? Describe 4 warning signs of a phishing attempt.", marks: 6 },
            { id: "q3", question: "Explain what digital footprint means and give 3 ways to manage it responsibly.", marks: 5 },
            { id: "q4", question: "What is copyright? Explain how it applies to using content from the internet.", marks: 6 },
            { id: "q5", question: "Describe 4 steps you should take if you encounter cyberbullying.", marks: 8 }
          ],
          answers: [
            { id: "q1", answer: "Strong password: (1) 12+ characters - harder to crack, (2) Mix of uppercase/lowercase - increases combinations, (3) Numbers included - adds complexity, (4) Special symbols - harder to guess, (5) Unique for each account - limits damage if compromised." },
            { id: "q2", answer: "Phishing is a fraudulent attempt to obtain sensitive information by posing as a trustworthy entity. Warning signs: (1) Urgent/threatening tone, (2) Suspicious email address/links, (3) Spelling/grammar errors, (4) Requests for personal information, (5) Too good to be true offers." },
            { id: "q3", answer: "Digital footprint is the trail of data you leave online through your activities. Manage it by: (1) Check privacy settings on all accounts, (2) Think before posting - assume it's permanent, (3) Google yourself periodically, (4) Use private browsing when appropriate." },
            { id: "q4", answer: "Copyright is legal protection for creators' original works. Online: (1) Cannot use copyrighted content without permission, (2) Must cite sources properly, (3) Use Creative Commons or royalty-free content, (4) Fair use allows limited use for education/commentary." },
            { id: "q5", answer: "Cyberbullying response: (1) Don't respond or retaliate, (2) Save evidence (screenshots), (3) Block the person, (4) Report to platform/school, (5) Tell a trusted adult, (6) Support the victim if you witness it." }
          ]
        }
      ]
    }
  ]
};

// Chemistry Assessment Data
const year11ChemistryAssessmentsData = {
  subject: "Year 11 Chemistry",
  strands: [
    {
      id: "strand1",
      number: 1,
      title: "Atomic Structure, Bonding and Related Properties",
      color: "green",
      icon: Beaker,
      assessments: [
        {
          id: "chem11-1.1",
          unitId: "1.1",
          title: "Atomic Structure Assessment",
          type: "Written Test",
          duration: "45 minutes",
          totalMarks: 30,
          questions: [
            { id: "q1", question: "Draw and label the structure of an atom showing protons, neutrons, and electrons.", marks: 5 },
            { id: "q2", question: "Define: (a) Atomic number, (b) Mass number, (c) Isotope", marks: 6 },
            { id: "q3", question: "Write the electronic configuration for: (a) Sodium (11), (b) Chlorine (17), (c) Calcium (20)", marks: 6 },
            { id: "q4", question: "Explain why elements in the same group have similar chemical properties.", marks: 5 },
            { id: "q5", question: "Calculate the number of protons, neutrons, and electrons in ³⁵Cl⁻", marks: 8 }
          ],
          answers: [
            { id: "q1", answer: "Diagram should show: nucleus in center with protons (+) and neutrons (0), electrons (-) in shells around nucleus. Labels for each particle required." },
            { id: "q2", answer: "(a) Atomic number: Number of protons in the nucleus, defines the element. (b) Mass number: Total number of protons and neutrons. (c) Isotope: Atoms of same element with different numbers of neutrons." },
            { id: "q3", answer: "(a) Sodium: 2,8,1 (b) Chlorine: 2,8,7 (c) Calcium: 2,8,8,2" },
            { id: "q4", answer: "Elements in the same group have the same number of valence (outer shell) electrons. Chemical properties depend on valence electrons, so they react similarly." },
            { id: "q5", answer: "³⁵Cl⁻: Protons = 17 (atomic number), Neutrons = 35-17 = 18, Electrons = 17+1 = 18 (extra electron due to negative charge)" }
          ]
        }
      ]
    },
    {
      id: "strand2",
      number: 2,
      title: "Quantitative and Aqueous Chemistry",
      color: "teal",
      icon: Beaker,
      assessments: [
        {
          id: "chem11-2.1",
          unitId: "2.1",
          title: "Mole Calculations Assessment",
          type: "Written Test",
          duration: "50 minutes",
          totalMarks: 35,
          questions: [
            { id: "q1", question: "Calculate the number of moles in 44g of CO₂. (Mr of CO₂ = 44)", marks: 4 },
            { id: "q2", question: "How many molecules are in 2 moles of water? (Nₐ = 6.022 × 10²³)", marks: 4 },
            { id: "q3", question: "Balance the equation: Fe + O₂ → Fe₂O₃", marks: 4 },
            { id: "q4", question: "In the reaction 2H₂ + O₂ → 2H₂O, calculate the mass of water produced from 4g of H₂.", marks: 8 },
            { id: "q5", question: "Define limiting reagent. In the reaction above, if 4g H₂ reacts with 16g O₂, which is the limiting reagent?", marks: 8 },
            { id: "q6", question: "Calculate the percentage composition of carbon in glucose (C₆H₁₂O₆).", marks: 7 }
          ],
          answers: [
            { id: "q1", answer: "n = m/M = 44/44 = 1 mole" },
            { id: "q2", answer: "N = n × Nₐ = 2 × 6.022 × 10²³ = 1.2044 × 10²⁴ molecules" },
            { id: "q3", answer: "4Fe + 3O₂ → 2Fe₂O₃" },
            { id: "q4", answer: "Moles H₂ = 4/2 = 2 mol. From equation, 2 mol H₂ → 2 mol H₂O. Mass H₂O = 2 × 18 = 36g" },
            { id: "q5", answer: "Limiting reagent is the reactant that is completely consumed first. Moles H₂ = 4/2 = 2 mol, Moles O₂ = 16/32 = 0.5 mol. Ratio needed is 2:1, but we have 2:0.5 = 4:1. O₂ is limiting." },
            { id: "q6", answer: "Mr of C₆H₁₂O₆ = 6(12) + 12(1) + 6(16) = 180. % C = (72/180) × 100 = 40%" }
          ]
        }
      ]
    },
    {
      id: "strand3",
      number: 3,
      title: "Physical Chemistry",
      color: "purple",
      icon: Beaker,
      assessments: []
    },
    {
      id: "strand4",
      number: 4,
      title: "Oxidation and Reduction",
      color: "orange",
      icon: Beaker,
      assessments: []
    },
    {
      id: "strand5",
      number: 5,
      title: "Organic Chemistry",
      color: "red",
      icon: Beaker,
      assessments: []
    }
  ]
};

const year12ChemistryAssessmentsData = {
  subject: "Year 12 Chemistry",
  strands: [
    {
      id: "strand1",
      number: 1,
      title: "Atomic Structure, Bonding and Related Properties",
      color: "green",
      icon: Beaker,
      assessments: [
        {
          id: "chem12-1.1",
          unitId: "1.1",
          title: "Advanced Bonding Assessment",
          type: "Written Test",
          duration: "50 minutes",
          totalMarks: 40,
          questions: [
            { id: "q1", question: "Explain the difference between ionic and covalent bonding with examples.", marks: 8 },
            { id: "q2", question: "Draw the Lewis structure for: (a) H₂O, (b) CO₂, (c) NH₃", marks: 9 },
            { id: "q3", question: "Explain why NaCl has a high melting point but CH₄ has a low melting point.", marks: 8 },
            { id: "q4", question: "Describe metallic bonding and explain why metals conduct electricity.", marks: 7 },
            { id: "q5", question: "Predict the shape of BF₃ and H₂O molecules using VSEPR theory.", marks: 8 }
          ],
          answers: [
            { id: "q1", answer: "Ionic bonding: electron transfer between metal and non-metal, e.g., NaCl. Covalent bonding: electron sharing between non-metals, e.g., H₂O." },
            { id: "q2", answer: "(a) H₂O: O in center with 2 bonding pairs to H and 2 lone pairs. (b) CO₂: C in center with double bonds to each O. (c) NH₃: N in center with 3 bonding pairs to H and 1 lone pair." },
            { id: "q3", answer: "NaCl has strong ionic bonds between Na⁺ and Cl⁻ ions in a lattice, requiring much energy to break. CH₄ has weak van der Waals forces between molecules." },
            { id: "q4", answer: "Metallic bonding: positive metal ions in a 'sea' of delocalized electrons. Conducts electricity because free electrons can move through the structure." },
            { id: "q5", answer: "BF₃: 3 bonding pairs, no lone pairs → trigonal planar (120°). H₂O: 2 bonding pairs, 2 lone pairs → bent/V-shaped (104.5°)." }
          ]
        }
      ]
    },
    {
      id: "strand2",
      number: 2,
      title: "Quantitative and Aqueous Chemistry",
      color: "teal",
      icon: Beaker,
      assessments: [
        {
          id: "chem12-2.1",
          unitId: "2.1",
          title: "Acids and Bases Assessment",
          type: "Written Test",
          duration: "45 minutes",
          totalMarks: 35,
          questions: [
            { id: "q1", question: "Define acid and base according to Brønsted-Lowry theory.", marks: 4 },
            { id: "q2", question: "Calculate the pH of a 0.01 M HCl solution.", marks: 5 },
            { id: "q3", question: "Write the equation for the neutralization of HCl with NaOH and name the products.", marks: 5 },
            { id: "q4", question: "In a titration, 25 mL of 0.1 M NaOH neutralizes 20 mL of HCl. Calculate the concentration of HCl.", marks: 8 },
            { id: "q5", question: "Explain what a buffer solution is and give one example.", marks: 5 },
            { id: "q6", question: "Identify the conjugate acid-base pairs in: NH₃ + H₂O ⇌ NH₄⁺ + OH⁻", marks: 8 }
          ],
          answers: [
            { id: "q1", answer: "Acid: proton (H⁺) donor. Base: proton (H⁺) acceptor." },
            { id: "q2", answer: "pH = -log[H⁺] = -log(0.01) = -log(10⁻²) = 2" },
            { id: "q3", answer: "HCl + NaOH → NaCl + H₂O. Products: sodium chloride (salt) and water." },
            { id: "q4", answer: "At equivalence: moles NaOH = moles HCl. n(NaOH) = 0.1 × 0.025 = 0.0025 mol. c(HCl) = 0.0025/0.020 = 0.125 M" },
            { id: "q5", answer: "Buffer: solution that resists pH change when small amounts of acid/base added. Example: CH₃COOH/CH₃COO⁻ (acetic acid/acetate)." },
            { id: "q6", answer: "NH₃/NH₄⁺ (base/conjugate acid), H₂O/OH⁻ (acid/conjugate base)" }
          ]
        }
      ]
    },
    {
      id: "strand3",
      number: 3,
      title: "Physical Chemistry",
      color: "purple",
      icon: Beaker,
      assessments: [
        {
          id: "chem12-3.1",
          unitId: "3.1",
          title: "Thermochemistry Assessment",
          type: "Written Test",
          duration: "45 minutes",
          totalMarks: 35,
          questions: [
            { id: "q1", question: "Define enthalpy and explain the difference between exothermic and endothermic reactions.", marks: 6 },
            { id: "q2", question: "Calculate the heat released when 100g of water cools from 80°C to 25°C. (c = 4.18 J/g°C)", marks: 6 },
            { id: "q3", question: "Draw an enthalpy diagram for an exothermic reaction.", marks: 5 },
            { id: "q4", question: "State Hess's Law and explain its importance.", marks: 5 },
            { id: "q5", question: "Using bond energies, calculate ΔH for: H₂ + Cl₂ → 2HCl. (H-H: 436, Cl-Cl: 242, H-Cl: 431 kJ/mol)", marks: 8 },
            { id: "q6", question: "Explain why breaking bonds is endothermic and forming bonds is exothermic.", marks: 5 }
          ],
          answers: [
            { id: "q1", answer: "Enthalpy: total heat content of a system. Exothermic: releases heat, ΔH < 0. Endothermic: absorbs heat, ΔH > 0." },
            { id: "q2", answer: "q = mcΔT = 100 × 4.18 × (80-25) = 100 × 4.18 × 55 = 22,990 J = 22.99 kJ" },
            { id: "q3", answer: "Diagram shows: reactants at higher energy level, products at lower level, arrow pointing down showing ΔH < 0." },
            { id: "q4", answer: "Hess's Law: Total enthalpy change is independent of the pathway. Important for calculating ΔH of reactions that can't be measured directly." },
            { id: "q5", answer: "Bonds broken: H-H (436) + Cl-Cl (242) = 678 kJ. Bonds formed: 2×H-Cl = 2×431 = 862 kJ. ΔH = 678 - 862 = -184 kJ/mol" },
            { id: "q6", answer: "Breaking bonds requires energy input (endothermic). Forming bonds releases energy as electrons reach more stable configuration (exothermic)." }
          ]
        }
      ]
    },
    {
      id: "strand4",
      number: 4,
      title: "Oxidation and Reduction",
      color: "orange",
      icon: Beaker,
      assessments: []
    },
    {
      id: "strand5",
      number: 5,
      title: "Organic Chemistry",
      color: "red",
      icon: Beaker,
      assessments: []
    }
  ]
};

const year13ChemistryAssessmentsData = {
  subject: "Year 13 Chemistry",
  strands: [
    {
      id: "strand1",
      number: 1,
      title: "Atomic Structure, Bonding and Related Properties",
      color: "green",
      icon: Beaker,
      assessments: [
        {
          id: "chem13-1.1",
          unitId: "1.1",
          title: "Chemical Equilibrium Assessment",
          type: "Written Test",
          duration: "60 minutes",
          totalMarks: 50,
          questions: [
            { id: "q1", question: "Define dynamic equilibrium and state two characteristics.", marks: 5 },
            { id: "q2", question: "Write the Kc expression for: N₂(g) + 3H₂(g) ⇌ 2NH₃(g)", marks: 4 },
            { id: "q3", question: "At equilibrium, [N₂] = 0.1M, [H₂] = 0.3M, [NH₃] = 0.2M. Calculate Kc.", marks: 6 },
            { id: "q4", question: "State Le Chatelier's Principle.", marks: 4 },
            { id: "q5", question: "For 2SO₂(g) + O₂(g) ⇌ 2SO₃(g) (ΔH < 0), predict the effect of: (a) increasing pressure, (b) increasing temperature, (c) adding a catalyst", marks: 12 },
            { id: "q6", question: "If Kc = 4.0 × 10⁻², is the equilibrium position to the left or right? Explain.", marks: 5 },
            { id: "q7", question: "Explain why Kc only changes with temperature.", marks: 6 },
            { id: "q8", question: "Calculate Kp if Kc = 0.5 and Δn = -2 at 298K. (R = 8.314 J/mol·K)", marks: 8 }
          ],
          answers: [
            { id: "q1", answer: "Dynamic equilibrium: state where forward and reverse reaction rates are equal. Characteristics: (1) Closed system, (2) Concentrations constant, (3) Both reactions still occurring." },
            { id: "q2", answer: "Kc = [NH₃]²/([N₂][H₂]³)" },
            { id: "q3", answer: "Kc = (0.2)²/((0.1)(0.3)³) = 0.04/(0.1 × 0.027) = 0.04/0.0027 = 14.8" },
            { id: "q4", answer: "When a system at equilibrium is disturbed, it shifts to counteract the change and restore equilibrium." },
            { id: "q5", answer: "(a) Increase pressure: shifts right (fewer moles). (b) Increase temp: shifts left (exothermic, opposes heat). (c) Catalyst: no effect on position, reaches equilibrium faster." },
            { id: "q6", answer: "To the left (reactants favored). Kc < 1 means products concentration is lower than reactants at equilibrium." },
            { id: "q7", answer: "Kc represents the ratio of rate constants (kf/kr). Only temperature affects rate constants (Arrhenius equation), so only temperature changes Kc." },
            { id: "q8", answer: "Kp = Kc(RT)^Δn = 0.5 × (8.314 × 298)^(-2) = 0.5 × (2477.6)^(-2) = 0.5 × 1.63×10⁻⁷ = 8.15×10⁻⁸" }
          ]
        }
      ]
    },
    {
      id: "strand2",
      number: 2,
      title: "Quantitative and Aqueous Chemistry",
      color: "teal",
      icon: Beaker,
      assessments: [
        {
          id: "chem13-2.1",
          unitId: "2.1",
          title: "Chemical Kinetics Assessment",
          type: "Written Test",
          duration: "50 minutes",
          totalMarks: 40,
          questions: [
            { id: "q1", question: "Define reaction rate and give its units.", marks: 4 },
            { id: "q2", question: "List four factors that affect reaction rate and explain one.", marks: 8 },
            { id: "q3", question: "For Rate = k[A]²[B], what is the overall order? If [A] doubles, what happens to rate?", marks: 6 },
            { id: "q4", question: "A first-order reaction has k = 0.02 s⁻¹. Calculate the half-life.", marks: 5 },
            { id: "q5", question: "Explain how a catalyst increases reaction rate using collision theory.", marks: 7 },
            { id: "q6", question: "Sketch graphs of [A] vs time for zero-order and first-order reactions.", marks: 5 },
            { id: "q7", question: "What is activation energy? How does temperature affect it?", marks: 5 }
          ],
          answers: [
            { id: "q1", answer: "Reaction rate: change in concentration per unit time. Units: mol dm⁻³ s⁻¹ or M/s" },
            { id: "q2", answer: "Factors: concentration, temperature, surface area, catalyst. Temperature: increases kinetic energy, more particles exceed activation energy, more successful collisions." },
            { id: "q3", answer: "Overall order = 2 + 1 = 3. If [A] doubles: rate = k(2[A])²[B] = 4k[A]²[B], rate increases 4 times." },
            { id: "q4", answer: "t½ = ln(2)/k = 0.693/0.02 = 34.65 seconds" },
            { id: "q5", answer: "Catalyst provides alternative pathway with lower activation energy. More particles have sufficient energy to react, increasing collision frequency of successful reactions." },
            { id: "q6", answer: "Zero-order: straight line decreasing. First-order: exponential decay curve." },
            { id: "q7", answer: "Activation energy: minimum energy needed for successful collision. Temperature doesn't change Ea but increases proportion of molecules with energy ≥ Ea." }
          ]
        }
      ]
    },
    {
      id: "strand3",
      number: 3,
      title: "Physical Chemistry",
      color: "purple",
      icon: Beaker,
      assessments: []
    },
    {
      id: "strand4",
      number: 4,
      title: "Oxidation and Reduction",
      color: "orange",
      icon: Beaker,
      assessments: [
        {
          id: "chem13-4.1",
          unitId: "4.1",
          title: "Electrochemistry Assessment",
          type: "Written Test",
          duration: "50 minutes",
          totalMarks: 40,
          questions: [
            { id: "q1", question: "Define oxidation and reduction in terms of electron transfer.", marks: 4 },
            { id: "q2", question: "Assign oxidation states to each element in: (a) H₂SO₄, (b) KMnO₄, (c) Fe₂O₃", marks: 6 },
            { id: "q3", question: "Balance in acidic solution: MnO₄⁻ + Fe²⁺ → Mn²⁺ + Fe³⁺", marks: 8 },
            { id: "q4", question: "Calculate E°cell for: Zn(s) + Cu²⁺(aq) → Zn²⁺(aq) + Cu(s). (E°Zn = -0.76V, E°Cu = +0.34V)", marks: 6 },
            { id: "q5", question: "Is the reaction in Q4 spontaneous? Explain.", marks: 4 },
            { id: "q6", question: "In electrolysis of CuSO₄ with copper electrodes, describe what happens at each electrode.", marks: 6 },
            { id: "q7", question: "How long must a current of 2A flow to deposit 3.2g of Cu? (Cu=64, F=96485 C/mol)", marks: 6 }
          ],
          answers: [
            { id: "q1", answer: "Oxidation: loss of electrons. Reduction: gain of electrons. (OIL RIG)" },
            { id: "q2", answer: "(a) H: +1, S: +6, O: -2. (b) K: +1, Mn: +7, O: -2. (c) Fe: +3, O: -2" },
            { id: "q3", answer: "MnO₄⁻ + 8H⁺ + 5Fe²⁺ → Mn²⁺ + 5Fe³⁺ + 4H₂O" },
            { id: "q4", answer: "E°cell = E°cathode - E°anode = E°Cu - E°Zn = 0.34 - (-0.76) = +1.10V" },
            { id: "q5", answer: "Yes, spontaneous. E°cell > 0 indicates a spontaneous reaction." },
            { id: "q6", answer: "Cathode (-): Cu²⁺ + 2e⁻ → Cu (copper deposited). Anode (+): Cu → Cu²⁺ + 2e⁻ (copper dissolves). Net effect: Cu transfers from anode to cathode." },
            { id: "q7", answer: "Moles Cu = 3.2/64 = 0.05 mol. Cu²⁺ + 2e⁻ → Cu, so moles e⁻ = 0.1 mol. Q = nF = 0.1 × 96485 = 9648.5 C. t = Q/I = 9648.5/2 = 4824 seconds = 80.4 minutes" }
          ]
        }
      ]
    },
    {
      id: "strand5",
      number: 5,
      title: "Organic Chemistry",
      color: "red",
      icon: Beaker,
      assessments: []
    }
  ]
};

// Get strand colors helper
const getStrandColors = (color) => {
  const colors = {
    blue: { bg: 'bg-blue-500', light: 'bg-blue-50', border: 'border-blue-500', text: 'text-blue-700', gradient: 'from-blue-500 to-blue-600' },
    green: { bg: 'bg-green-500', light: 'bg-green-50', border: 'border-green-500', text: 'text-green-700', gradient: 'from-green-500 to-green-600' },
    purple: { bg: 'bg-purple-500', light: 'bg-purple-50', border: 'border-purple-500', text: 'text-purple-700', gradient: 'from-purple-500 to-purple-600' },
    red: { bg: 'bg-red-500', light: 'bg-red-50', border: 'border-red-500', text: 'text-red-700', gradient: 'from-red-500 to-red-600' }
  };
  return colors[color] || colors.blue;
};

// Get resource icon
const getResourceIcon = (type) => {
  const icons = {
    ppt: Presentation,
    pdf: FileText,
    video: Video,
    link: LinkIcon
  };
  return icons[type] || File;
};

// ============ Term Exam Generator Component ============
const TermExamGenerator = ({ subject, yearLevel }) => {
  const [selectedTerm, setSelectedTerm] = useState(1);
  const [totalMarks, setTotalMarks] = useState(100);
  const [duration, setDuration] = useState(120);
  const [topics, setTopics] = useState('');
  const [blueprint, setBlueprint] = useState(null);
  const [generatedExam, setGeneratedExam] = useState(null);
  const [isLoadingBlueprint, setIsLoadingBlueprint] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [error, setError] = useState(null);

  const API_URL = '';

  // Fetch blueprint when subject/year/term changes
  const fetchBlueprint = async () => {
    setIsLoadingBlueprint(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_URL}/api/ai-lessons/exam-blueprint?subject=${subject}&year_level=${yearLevel}&term=${selectedTerm}&total_marks=${totalMarks}`
      );
      const data = await response.json();
      if (data.success) {
        setBlueprint(data.blueprint);
      } else {
        setError('Failed to load exam blueprint');
      }
    } catch (err) {
      setError('Error loading blueprint: ' + err.message);
    } finally {
      setIsLoadingBlueprint(false);
    }
  };

  useEffect(() => {
    fetchBlueprint();
  }, [subject, yearLevel, selectedTerm, totalMarks]);

  // Generate the exam using AI
  const generateExam = async () => {
    setIsGenerating(true);
    setError(null);
    setGeneratedExam(null);
    
    try {
      const requestBody = {
        subject: subject,
        year_level: yearLevel,
        term: selectedTerm,
        school_type: yearLevel >= 11 ? 'secondary' : 'junior_secondary',
        duration_minutes: duration,
        total_marks: totalMarks,
        topics_covered: topics ? topics.split(',').map(t => t.trim()) : null
      };

      const response = await fetch(`${API_URL}/api/ai-lessons/generate-term-exam`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setGeneratedExam(data.exam);
      } else {
        setError(data.detail || 'Failed to generate exam');
      }
    } catch (err) {
      setError('Error generating exam: ' + err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  // Print exam
  const printExam = () => {
    const printContent = document.getElementById('exam-print-content');
    if (printContent) {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>${generatedExam?.title || 'Term Exam'}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.6; }
              h1 { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; }
              h2 { background: #f0f0f0; padding: 8px; margin-top: 20px; }
              .header-info { display: flex; justify-content: space-between; margin: 10px 0; }
              .instructions { background: #f9f9f9; padding: 10px; border-left: 3px solid #333; margin: 10px 0; }
              .question { margin: 15px 0; padding: 10px 0; border-bottom: 1px dashed #ccc; }
              .options { margin-left: 20px; }
              .answer-key { background: #e8f5e9; padding: 10px; margin-top: 5px; }
              @media print { .no-print { display: none; } }
            </style>
          </head>
          <body>${printContent.innerHTML}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const subjectName = subject === 'ict' ? 'ICT' : subject === 'esl' ? 'English (ESL)' : subject.charAt(0).toUpperCase() + subject.slice(1);

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-2xl">
            <FileEdit className="h-7 w-7" />
            End of Term Examination Generator
          </CardTitle>
          <CardDescription className="text-indigo-100">
            Generate AI-powered term examinations with customizable blueprints
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Configuration Panel */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-indigo-600" />
            Exam Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Subject & Year Display */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600">Subject</label>
              <div className="p-3 bg-gray-100 rounded-md font-semibold">
                {subjectName} - Year {yearLevel}
              </div>
            </div>

            {/* Term Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600">Term</label>
              <select 
                value={selectedTerm} 
                onChange={(e) => setSelectedTerm(parseInt(e.target.value))}
                className="w-full p-3 border rounded-md"
              >
                <option value={1}>Term 1</option>
                <option value={2}>Term 2</option>
                <option value={3}>Term 3</option>
                <option value={4}>Term 4</option>
              </select>
            </div>

            {/* Duration */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600">Duration (minutes)</label>
              <select 
                value={duration} 
                onChange={(e) => setDuration(parseInt(e.target.value))}
                className="w-full p-3 border rounded-md"
              >
                <option value={60}>60 minutes (1 hour)</option>
                <option value={90}>90 minutes (1.5 hours)</option>
                <option value={120}>120 minutes (2 hours)</option>
                <option value={150}>150 minutes (2.5 hours)</option>
                <option value={180}>180 minutes (3 hours)</option>
              </select>
            </div>

            {/* Total Marks */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600">Total Marks</label>
              <select 
                value={totalMarks} 
                onChange={(e) => setTotalMarks(parseInt(e.target.value))}
                className="w-full p-3 border rounded-md"
              >
                <option value={50}>50 marks</option>
                <option value={75}>75 marks</option>
                <option value={100}>100 marks</option>
                <option value={150}>150 marks</option>
              </select>
            </div>
          </div>

          {/* Topics Input */}
          <div className="mt-4 space-y-2">
            <label className="text-sm font-medium text-gray-600">
              Specific Topics (optional, comma-separated)
            </label>
            <input
              type="text"
              value={topics}
              onChange={(e) => setTopics(e.target.value)}
              placeholder="e.g., World War II, Independence Movement, Colonial Period"
              className="w-full p-3 border rounded-md"
            />
          </div>
        </CardContent>
      </Card>

      {/* Blueprint Display */}
      {blueprint && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <List className="h-5 w-5 text-blue-600" />
              Exam Blueprint
            </CardTitle>
            <CardDescription>
              Standard examination structure for {subjectName}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-gray-500" />
                <span className="font-medium">Duration:</span> {blueprint.duration}
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Award className="h-4 w-4 text-gray-500" />
                <span className="font-medium">Total Marks:</span> {blueprint.total_marks}
              </div>
            </div>

            <div className="space-y-3">
              {blueprint.sections?.map((section, idx) => (
                <div key={idx} className="p-4 bg-gray-50 rounded-lg border-l-4 border-indigo-500">
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold text-gray-800">{section.name}</h4>
                    <Badge variant="secondary">{section.marks} marks</Badge>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {section.num_questions} questions • {section.question_type.replace('_', ' ')}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{section.instructions}</p>
                </div>
              ))}
            </div>

            {/* Generate Button */}
            <div className="mt-6 flex gap-3">
              <Button 
                onClick={generateExam}
                disabled={isGenerating}
                className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Generating Exam...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate Term Exam with AI
                  </>
                )}
              </Button>
              <Button 
                variant="outline" 
                onClick={fetchBlueprint}
                disabled={isLoadingBlueprint}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingBlueprint ? 'animate-spin' : ''}`} />
                Refresh Blueprint
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error Display */}
      {error && (
        <Card className="border-red-300 bg-red-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Generated Exam Display */}
      {generatedExam && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-green-600" />
                Generated Examination
              </CardTitle>
              <CardDescription>
                Review, print, or regenerate your exam
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowAnswers(!showAnswers)}
              >
                {showAnswers ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                {showAnswers ? 'Hide Answers' : 'Show Answers'}
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={printExam}
              >
                <Printer className="h-4 w-4 mr-1" />
                Print
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div id="exam-print-content" className="space-y-6">
              {/* Exam Header */}
              <div className="text-center border-b-2 border-gray-800 pb-4">
                <h1 className="text-2xl font-bold">{generatedExam.title}</h1>
                <div className="flex justify-center gap-6 mt-2 text-sm text-gray-600">
                  <span><strong>Duration:</strong> {generatedExam.duration}</span>
                  <span><strong>Total Marks:</strong> {generatedExam.total_marks}</span>
                </div>
              </div>

              {/* Instructions */}
              {generatedExam.instructions && (
                <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-gray-400">
                  <h3 className="font-semibold mb-2">Instructions:</h3>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    {generatedExam.instructions.map((instruction, idx) => (
                      <li key={idx}>{instruction}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Sections */}
              {generatedExam.sections?.map((section, sIdx) => (
                <div key={sIdx} className="space-y-4">
                  <div className="bg-indigo-50 p-3 rounded-lg">
                    <h2 className="font-bold text-lg text-indigo-800">{section.name}</h2>
                    <p className="text-sm text-indigo-600">{section.marks} marks</p>
                    {section.instructions && (
                      <p className="text-sm text-gray-600 mt-1">{section.instructions}</p>
                    )}
                  </div>

                  {/* Questions */}
                  <div className="space-y-4 pl-4">
                    {section.questions?.map((q, qIdx) => (
                      <div key={qIdx} className="p-3 border rounded-lg bg-white">
                        <div className="flex justify-between items-start">
                          <p className="font-medium">
                            <span className="text-indigo-600">{q.number || qIdx + 1}.</span> {q.question}
                          </p>
                          <Badge variant="outline" className="ml-2 shrink-0">
                            {q.marks || Math.floor(section.marks / section.questions.length)} marks
                          </Badge>
                        </div>

                        {/* Multiple Choice Options */}
                        {q.options && (
                          <div className="mt-2 ml-4 space-y-1">
                            {q.options.map((opt, oIdx) => (
                              <div key={oIdx} className="flex items-center gap-2 text-sm">
                                <span className="font-medium text-gray-500">
                                  {String.fromCharCode(65 + oIdx)}.
                                </span>
                                <span>{opt}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Answer (if showing) */}
                        {showAnswers && q.answer && (
                          <div className="mt-2 p-2 bg-green-50 rounded text-sm text-green-700 border border-green-200">
                            <strong>Answer:</strong> {q.answer}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default function ResourceManagementPage() {
  const currentUser = getCurrentUser();
  const [mainTab, setMainTab] = useState('notes');
  // Determine initial subject based on user's department
  const getInitialSubject = () => {
    const userDept = getCurrentUser()?.department?.toLowerCase() || '';
    if (userDept.includes('french') || userDept.includes('languages')) return 'french';
    if (userDept.includes('english') || userDept.includes('esl') || userDept.includes('literacy')) return 'esl';
    if (userDept.includes('accounting') || userDept.includes('accounts')) return 'accounting';
    if (userDept.includes('agriculture') || userDept.includes('agri')) return 'agriculture';
    if (userDept.includes('math') || userDept.includes('mathematics')) return 'mathematics';
    if (userDept.includes('geography') || userDept.includes('social science') || userDept.includes('humanities')) return 'geography';
    if (userDept.includes('physics')) return 'physics';
    if (userDept.includes('economics') || userDept.includes('commerce')) return 'economics';
    if (userDept.includes('chemistry')) return 'chemistry';
    if (userDept.includes('ict') || userDept.includes('computer') || userDept.includes('technology')) return 'ict';
    return 'ict';
  };
  const [selectedSubject, setSelectedSubject] = useState(getInitialSubject);
  // Determine initial year based on school type
  const getInitialYear = () => {
    const schoolType = getCurrentUser()?.school_type || '';
    const dept = getCurrentUser()?.department?.toLowerCase() || '';
    // For junior secondary or French teachers (7-10), start at year7
    if (schoolType === 'junior_secondary' || dept.includes('french') || dept.includes('languages')) {
      return 'year7';
    }
    return 'year11'; // Default to year11 for senior secondary
  };
  const [selectedYear, setSelectedYear] = useState(getInitialYear);
  const [selectedStrand, setSelectedStrand] = useState('strand1');
  const [selectedAssessmentStrand, setSelectedAssessmentStrand] = useState('strand1');
  const [expandedUnits, setExpandedUnits] = useState({});
  const [expandedAssessments, setExpandedAssessments] = useState({});
  const [showAnswers, setShowAnswers] = useState({});
  const [isAllowedTeacher, setIsAllowedTeacher] = useState(false);
  const [teachesICT, setTeachesICT] = useState(false);
  const [teachesChemistry, setTeachesChemistry] = useState(false);
  const [teachesPhysics, setTeachesPhysics] = useState(false);
  const [teachesEconomics, setTeachesEconomics] = useState(false);
  const [teachesFrench, setTeachesFrench] = useState(false);
  const [teachesGeography, setTeachesGeography] = useState(false);
  const [teachesMathematics, setTeachesMathematics] = useState(false);
  const [teachesAgriculture, setTeachesAgriculture] = useState(false);
  const [teachesAccounting, setTeachesAccounting] = useState(false);
  const [teachesESL, setTeachesESL] = useState(false);
  const [teachesHistory, setTeachesHistory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showExpandedNotes, setShowExpandedNotes] = useState(false); // Toggle for expanded notes view
  
  // Primary school assessment states
  const [primarySubject, setPrimarySubject] = useState('english');
  const [primaryYear, setPrimaryYear] = useState('1');
  const [primaryStrand, setPrimaryStrand] = useState('');
  const [expandedPrimaryAssessment, setExpandedPrimaryAssessment] = useState({});
  const [showPrimaryAnswers, setShowPrimaryAnswers] = useState({});
  const [teacherPrimaryYears, setTeacherPrimaryYears] = useState([]); // Years the teacher is assigned to
  
  // Check if primary school
  const isPrimarySchool = currentUser?.school_type === 'primary';

  // Get expanded notes based on subject and year selection
  const getExpandedNotes = () => {
    if (selectedSubject === 'ict') {
      if (selectedYear === 'year11') return year11ICTStudentNotes;
      if (selectedYear === 'year12') return year12ICTStudentNotes;
      return year13ICTStudentNotes;
    } else if (selectedSubject === 'chemistry') {
      if (selectedYear === 'year11') return year11ChemistryStudentNotes;
      if (selectedYear === 'year12') return year12ChemistryStudentNotes;
      return year13ChemistryStudentNotes;
    } else if (selectedSubject === 'physics') {
      if (selectedYear === 'year11') return year11PhysicsStudentNotes;
      if (selectedYear === 'year12') return year12PhysicsStudentNotes;
      return year13PhysicsStudentNotes;
    } else if (selectedSubject === 'economics') {
      if (selectedYear === 'year11') return year11EconomicsStudentNotes;
      if (selectedYear === 'year12') return year12EconomicsStudentNotes;
      return year13EconomicsStudentNotes;
    } else if (selectedSubject === 'french') {
      // French is for Years 7-10 (Junior Secondary)
      if (selectedYear === 'year7') return year7FrenchStudentNotes;
      if (selectedYear === 'year8') return year8FrenchStudentNotes;
      if (selectedYear === 'year9') return year9FrenchStudentNotes;
      if (selectedYear === 'year10') return year10FrenchStudentNotes;
      return year7FrenchStudentNotes; // Default to year 7
    } else if (selectedSubject === 'geography') {
      if (selectedYear === 'year11') return year11GeographyStudentNotes;
      if (selectedYear === 'year12') return year12GeographyStudentNotes;
      return year13GeographyStudentNotes;
    } else if (selectedSubject === 'mathematics') {
      if (selectedYear === 'year11') return year11MathematicsStudentNotes;
      if (selectedYear === 'year12') return year12MathematicsStudentNotes;
      return year13MathematicsStudentNotes;
    } else if (selectedSubject === 'agriculture') {
      if (selectedYear === 'year11') return year11AgricultureStudentNotes;
      if (selectedYear === 'year12') return year12AgricultureStudentNotes;
      return year13AgricultureStudentNotes;
    } else if (selectedSubject === 'accounting') {
      if (selectedYear === 'year11') return year11AccountingStudentNotes;
      if (selectedYear === 'year12') return year12AccountingStudentNotes;
      return year13AccountingStudentNotes;
    } else if (selectedSubject === 'esl') {
      if (selectedYear === 'year11') return year11ESLStudentNotes;
      if (selectedYear === 'year12') return year12ESLStudentNotes;
      return year13ESLStudentNotes;
    } else if (selectedSubject === 'history') {
      if (selectedYear === 'year11') return year11HistoryStudentNotes;
      if (selectedYear === 'year12') return year12HistoryStudentNotes;
      return year13HistoryStudentNotes;
    }
    return null;
  };

  // Check if teacher teaches any curriculum subject (ICT, Chemistry, Physics, Economics, etc.)
  useEffect(() => {
    const checkTeacherAccess = async () => {
      try {
        // Primary school teachers have access to primary resources
        if (isPrimarySchool) {
          // Fetch teacher's classes to determine which years they teach
          try {
            const response = await apiService.getTeacherClasses(true);
            const classes = response.data || response || [];
            
            // Extract unique years from teacher's classes
            const years = [...new Set(
              classes
                .map(cls => {
                  const yearMatch = cls.name?.match(/Year\s*(\d+)/i) || cls.year?.toString().match(/(\d+)/);
                  return yearMatch ? yearMatch[1] : null;
                })
                .filter(year => year !== null && parseInt(year) >= 1 && parseInt(year) <= 6)
            )].sort((a, b) => parseInt(a) - parseInt(b));
            
            if (years.length > 0) {
              setTeacherPrimaryYears(years);
              setPrimaryYear(years[0]); // Set to first available year
            } else {
              // Fallback to all years if no classes found (admin/principal)
              setTeacherPrimaryYears(['1', '2', '3', '4', '5', '6']);
            }
          } catch (err) {
            console.log('Could not fetch teacher classes, showing all years');
            setTeacherPrimaryYears(['1', '2', '3', '4', '5', '6']);
          }
          
          setIsAllowedTeacher(true); // Grant access for primary schools
          setTeachesICT(true);
          setTeachesChemistry(true);
          setLoading(false);
          return;
        }
        
        // Get teacher's classes
        const response = await apiService.getTeacherClasses();
        const classes = response.data || response || [];
        
        // Check which subjects the teacher teaches
        const hasICT = classes.some(cls => 
          cls.subject?.toLowerCase().includes('ict') || 
          cls.subject?.toLowerCase().includes('computer')
        );
        const hasChemistry = classes.some(cls => 
          cls.subject?.toLowerCase().includes('chemistry')
        );
        
        // Check for Physics, Economics, French, and Geography as well
        const hasPhysics = classes.some(cls => 
          cls.subject?.toLowerCase().includes('physics')
        );
        const hasEconomics = classes.some(cls => 
          cls.subject?.toLowerCase().includes('economics')
        );
        const hasFrench = classes.some(cls => 
          cls.subject?.toLowerCase().includes('french')
        );
        const hasGeography = classes.some(cls => 
          cls.subject?.toLowerCase().includes('geography')
        );
        const hasMathematics = classes.some(cls => 
          cls.subject?.toLowerCase().includes('math') ||
          cls.subject?.toLowerCase().includes('maths') ||
          cls.subject?.toLowerCase().includes('mathematics')
        );
        const hasAgriculture = classes.some(cls => 
          cls.subject?.toLowerCase().includes('agriculture') ||
          cls.subject?.toLowerCase().includes('agri')
        );
        const hasAccounting = classes.some(cls => 
          cls.subject?.toLowerCase().includes('accounting') ||
          cls.subject?.toLowerCase().includes('accounts') ||
          cls.subject?.toLowerCase().includes('bookkeeping')
        );
        
        // Check for History in classes
        const hasHistory = classes.some(cls => 
          cls.subject?.toLowerCase().includes('history')
        );
        
        // Check for ESL/English in classes
        const hasESLFromClasses = classes.some(cls => 
          cls.subject?.toLowerCase().includes('english') ||
          cls.subject?.toLowerCase().includes('esl') ||
          cls.subject?.toLowerCase().includes('literacy')
        );
        
        // Also check user's department for curriculum access
        const userDept = currentUser?.department?.toLowerCase() || '';
        const hasCurriculumDept = ['ict', 'chemistry', 'physics', 'economics', 'french', 'geography', 'mathematics', 'math', 'maths', 'science', 'computer', 'languages', 'social science', 'humanities', 'agriculture', 'agri', 'accounting', 'accounts', 'bookkeeping', 'english', 'esl', 'literacy', 'history'].some(
          subj => userDept.includes(subj)
        );
        
        // Check if user teaches ESL based on department OR classes
        const hasESLFromDept = userDept.includes('english') || userDept.includes('esl') || userDept.includes('literacy');
        const hasESL = hasESLFromClasses || hasESLFromDept;
        
        setTeachesICT(hasICT);
        setTeachesChemistry(hasChemistry);
        setTeachesPhysics(hasPhysics);
        setTeachesEconomics(hasEconomics);
        setTeachesFrench(hasFrench);
        setTeachesGeography(hasGeography);
        setTeachesMathematics(hasMathematics);
        setTeachesAgriculture(hasAgriculture);
        setTeachesAccounting(hasAccounting);
        setTeachesHistory(hasHistory);
        
        // Set teachesESL state
        if (hasESL) {
          setTeachesESL(true);
        }
        
        // Allow access for any teacher with curriculum subjects
        const hasAnyCurriculumSubject = hasICT || hasChemistry || hasPhysics || hasEconomics || hasFrench || hasGeography || hasMathematics || hasAgriculture || hasAccounting || hasESL || hasHistory || hasCurriculumDept;
        setIsAllowedTeacher(hasAnyCurriculumSubject);
        
        // Set default subject based on what teacher teaches (priority: their main subject)
        if (hasHistory && !hasICT && !hasChemistry && !hasPhysics && !hasEconomics && !hasFrench && !hasGeography && !hasMathematics && !hasAgriculture && !hasAccounting && !hasESL) {
          setSelectedSubject('history');
        } else if (hasESL && !hasICT && !hasChemistry && !hasPhysics && !hasEconomics && !hasFrench && !hasGeography && !hasMathematics && !hasAgriculture && !hasAccounting) {
          setSelectedSubject('esl');
        } else if (hasAccounting && !hasICT && !hasChemistry && !hasPhysics && !hasEconomics && !hasFrench && !hasGeography && !hasMathematics && !hasAgriculture) {
          setSelectedSubject('accounting');
        } else if (hasAgriculture && !hasICT && !hasChemistry && !hasPhysics && !hasEconomics && !hasFrench && !hasGeography && !hasMathematics) {
          setSelectedSubject('agriculture');
        } else if (hasMathematics && !hasICT && !hasChemistry && !hasPhysics && !hasEconomics && !hasFrench && !hasGeography) {
          setSelectedSubject('mathematics');
        } else if (hasGeography && !hasICT && !hasChemistry && !hasPhysics && !hasEconomics && !hasFrench) {
          setSelectedSubject('geography');
        } else if (hasFrench && !hasICT && !hasChemistry && !hasPhysics && !hasEconomics) {
          setSelectedSubject('french');
          setSelectedYear('year7'); // French is for Years 7-10
        } else if (hasPhysics && !hasICT && !hasChemistry) {
          setSelectedSubject('physics');
        } else if (hasEconomics && !hasICT && !hasChemistry && !hasPhysics) {
          setSelectedSubject('economics');
        } else if (hasChemistry && !hasICT) {
          setSelectedSubject('chemistry');
        }
        
        // Also check department and set subject if no classes were found
        if (!hasICT && !hasChemistry && !hasPhysics && !hasEconomics && !hasFrench && !hasGeography && !hasMathematics && !hasAgriculture && !hasAccounting && !hasESL && !hasHistory && hasCurriculumDept) {
          // Set subject based on department
          if (userDept.includes('history')) {
            setTeachesHistory(true);
            setSelectedSubject('history');
          } else if (userDept.includes('english') || userDept.includes('esl') || userDept.includes('literacy')) {
            setTeachesESL(true);
            setSelectedSubject('esl');
          } else if (userDept.includes('accounting') || userDept.includes('accounts') || userDept.includes('bookkeeping')) {
            setTeachesAccounting(true);
            setSelectedSubject('accounting');
          } else if (userDept.includes('agriculture') || userDept.includes('agri')) {
            setTeachesAgriculture(true);
            setSelectedSubject('agriculture');
          } else if (userDept.includes('math') || userDept.includes('maths') || userDept.includes('mathematics')) {
            setTeachesMathematics(true);
            setSelectedSubject('mathematics');
          } else if (userDept.includes('geography') || userDept.includes('social science') || userDept.includes('humanities')) {
            setTeachesGeography(true);
            setSelectedSubject('geography');
          } else if (userDept.includes('french') || userDept.includes('languages')) {
            setTeachesFrench(true);
            setSelectedSubject('french');
            setSelectedYear('year7');
          } else if (userDept.includes('physics')) {
            setTeachesPhysics(true);
            setSelectedSubject('physics');
          } else if (userDept.includes('economics')) {
            setTeachesEconomics(true);
            setSelectedSubject('economics');
          } else if (userDept.includes('chemistry')) {
            setTeachesChemistry(true);
            setSelectedSubject('chemistry');
          } else if (userDept.includes('ict') || userDept.includes('computer')) {
            setTeachesICT(true);
            setSelectedSubject('ict');
          }
        }
      } catch (error) {
        console.error('Error checking teacher access:', error);
        setIsAllowedTeacher(isPrimarySchool); // Primary schools still get access
        if (isPrimarySchool) {
          setTeachesICT(true);
          setTeachesChemistry(true);
        }
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      checkTeacherAccess();
    } else {
      setLoading(false);
    }
  }, [currentUser, isPrimarySchool]);

  const toggleUnit = (unitId) => {
    setExpandedUnits(prev => ({
      ...prev,
      [unitId]: !prev[unitId]
    }));
  };

  const toggleAssessment = (assessmentId) => {
    setExpandedAssessments(prev => ({
      ...prev,
      [assessmentId]: !prev[assessmentId]
    }));
  };

  const toggleShowAnswer = (questionKey) => {
    setShowAnswers(prev => ({
      ...prev,
      [questionKey]: !prev[questionKey]
    }));
  };

  // Print assessment function
  const printAssessment = (assessment, strand, includeAnswers = false) => {
    const printWindow = window.open('', '_blank');
    const version = includeAnswers ? 'Teacher Version (With Answers)' : 'Student Version';
    
    let questionsHtml = '';
    assessment.questions.forEach((q, idx) => {
      questionsHtml += `
        <div style="margin-bottom: 25px; page-break-inside: avoid;">
          <p style="font-weight: bold; margin-bottom: 10px;">
            Q${q.id}. ${q.question} 
            ${q.marks ? `<span style="font-weight: normal; color: #666;">(${q.marks} marks)</span>` : ''}
          </p>
      `;
      
      // Multiple choice options
      if (q.type === 'multiple_choice' && q.options) {
        q.options.forEach((opt, i) => {
          const isCorrect = includeAnswers && opt === q.correctAnswer;
          questionsHtml += `
            <div style="margin-left: 20px; margin-bottom: 5px; ${isCorrect ? 'background: #d4edda; padding: 5px; border-radius: 3px;' : ''}">
              ○ ${opt} ${isCorrect ? '✓' : ''}
            </div>
          `;
        });
      }
      
      // True/False options
      if (q.type === 'true_false') {
        const correctAnswer = q.correctAnswer ? 'True' : 'False';
        ['True', 'False'].forEach(opt => {
          const isCorrect = includeAnswers && opt === correctAnswer;
          questionsHtml += `
            <div style="margin-left: 20px; margin-bottom: 5px; ${isCorrect ? 'background: #d4edda; padding: 5px; border-radius: 3px;' : ''}">
              ○ ${opt} ${isCorrect ? '✓' : ''}
            </div>
          `;
        });
      }
      
      // Matching pairs
      if (q.type === 'matching' && q.pairs) {
        questionsHtml += '<div style="margin-left: 20px; display: flex; gap: 40px;">';
        questionsHtml += '<div><strong>Terms:</strong>';
        q.pairs.forEach(p => {
          questionsHtml += `<div style="margin: 5px 0; padding: 5px; background: #f0f0f0; border-radius: 3px;">• ${p.term}</div>`;
        });
        questionsHtml += '</div>';
        questionsHtml += '<div><strong>Definitions:</strong>';
        q.pairs.forEach(p => {
          questionsHtml += `<div style="margin: 5px 0; padding: 5px; background: #f0f0f0; border-radius: 3px;">• ${includeAnswers ? p.term + ' → ' : ''}${p.definition}</div>`;
        });
        questionsHtml += '</div></div>';
      }
      
      // Fill in blank
      if (q.type === 'fill_blank') {
        questionsHtml += `<div style="margin-left: 20px; margin-top: 10px; border-bottom: 2px solid #333; width: 300px; height: 30px;"></div>`;
      }
      
      // Short answer / Practical / Research - answer lines
      if (q.type === 'short_answer' || q.type === 'practical' || q.type === 'research') {
        const lines = q.marks ? Math.max(q.marks, 4) : 6;
        for (let i = 0; i < lines; i++) {
          questionsHtml += `<div style="margin-left: 20px; margin-top: 15px; border-bottom: 1px solid #ccc; height: 25px;"></div>`;
        }
      }
      
      // Include answer if teacher version
      if (includeAnswers) {
        if (q.correctAnswer && (q.type === 'fill_blank')) {
          questionsHtml += `
            <div style="margin-top: 10px; margin-left: 20px; padding: 10px; background: #d4edda; border-radius: 5px; border-left: 4px solid #28a745;">
              <strong>Answer:</strong> ${q.correctAnswer}
            </div>
          `;
        }
        if (q.sampleAnswer) {
          questionsHtml += `
            <div style="margin-top: 10px; margin-left: 20px; padding: 10px; background: #d4edda; border-radius: 5px; border-left: 4px solid #28a745;">
              <strong>Sample Answer:</strong><br>
              <pre style="white-space: pre-wrap; font-family: inherit; margin: 5px 0;">${q.sampleAnswer}</pre>
            </div>
          `;
        }
        if (q.explanation) {
          questionsHtml += `
            <div style="margin-top: 10px; margin-left: 20px; padding: 10px; background: #cce5ff; border-radius: 5px; border-left: 4px solid #007bff;">
              <strong>Explanation:</strong> ${q.explanation}
            </div>
          `;
        }
      }
      
      questionsHtml += '</div>';
    });
    
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${assessment.title} - ${version}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
          .header { border-bottom: 3px solid #333; padding-bottom: 15px; margin-bottom: 20px; }
          .school-name { font-size: 14px; color: #666; }
          .assessment-title { font-size: 22px; font-weight: bold; margin: 10px 0; }
          .meta { display: flex; justify-content: space-between; color: #666; font-size: 14px; }
          .student-info { margin: 20px 0; padding: 15px; background: #f5f5f5; border-radius: 5px; }
          .student-info-row { display: flex; gap: 30px; margin-bottom: 10px; }
          .student-info-field { flex: 1; }
          .student-info-field label { font-weight: bold; }
          .student-info-field .line { border-bottom: 1px solid #333; height: 25px; }
          .version-badge { display: inline-block; padding: 5px 10px; border-radius: 15px; font-size: 12px; font-weight: bold; }
          .student-version { background: #e3f2fd; color: #1565c0; }
          .teacher-version { background: #fff3e0; color: #e65100; }
          .questions { margin-top: 20px; }
          .instructions { background: #fff3cd; padding: 10px; border-radius: 5px; margin-bottom: 20px; font-size: 14px; }
          @media print {
            body { padding: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="school-name">Year 13 ICT - ${strand.title}</div>
          <div class="assessment-title">${assessment.title}</div>
          <div class="meta">
            <span>${assessment.type} • ${assessment.totalMarks} marks • ${assessment.timeLimit}</span>
            <span class="version-badge ${includeAnswers ? 'teacher-version' : 'student-version'}">${version}</span>
          </div>
        </div>
        
        ${!includeAnswers ? `
        <div class="student-info">
          <div class="student-info-row">
            <div class="student-info-field">
              <label>Name:</label>
              <div class="line"></div>
            </div>
            <div class="student-info-field">
              <label>Class:</label>
              <div class="line"></div>
            </div>
            <div class="student-info-field">
              <label>Date:</label>
              <div class="line"></div>
            </div>
          </div>
        </div>
        
        <div class="instructions">
          <strong>Instructions:</strong> Answer ALL questions. Write clearly and show your working where applicable.
        </div>
        ` : ''}
        
        <div class="questions">
          ${questionsHtml}
        </div>
        
        <div style="margin-top: 30px; text-align: center; color: #999; font-size: 12px;">
          --- End of ${assessment.type} ---
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const data = getNotesData(selectedYear, selectedSubject);
  
  // Get assessment data based on subject and year
  const getAssessmentData = () => {
    if (selectedSubject === 'french') {
      // Return French assessment data for Years 7-10
      return getFrenchAssessmentData(selectedYear);
    }
    if (selectedSubject === 'chemistry') {
      if (selectedYear === 'year11') return year11ChemistryAssessmentsData;
      if (selectedYear === 'year12') return year12ChemistryAssessmentsData;
      return year13ChemistryAssessmentsData;
    }
    if (selectedSubject === 'mathematics') {
      // Return Mathematics assessment data
      return getMathematicsAssessmentData(selectedYear);
    }
    if (selectedSubject === 'physics') {
      // Return Physics assessment data (placeholder)
      return getPhysicsAssessmentData(selectedYear);
    }
    if (selectedSubject === 'economics') {
      // Return Economics assessment data (placeholder)
      return getEconomicsAssessmentData(selectedYear);
    }
    if (selectedSubject === 'geography') {
      // Return Geography assessment data (placeholder)
      return getGeographyAssessmentData(selectedYear);
    }
    if (selectedSubject === 'agriculture') {
      // Return Agriculture assessment data (placeholder)
      return getAgricultureAssessmentData(selectedYear);
    }
    if (selectedSubject === 'accounting') {
      // Return Accounting assessment data (placeholder)
      return getAccountingAssessmentData(selectedYear);
    }
    if (selectedSubject === 'esl') {
      // Return ESL assessment data
      return getESLAssessmentData(selectedYear);
    }
    // Default to ICT
    return selectedYear === 'year12' ? year12AssessmentsData : year13AssessmentsData;
  };
  
  // Mathematics Assessment Data Generator
  const getMathematicsAssessmentData = (year) => {
    const yearNum = year.replace('year', '');
    const tests = year === 'year11' ? year11MathematicsUnitTests : 
                  year === 'year12' ? year12MathematicsUnitTests : year13MathematicsUnitTests;
    
    return {
      subject: `Year ${yearNum} Mathematics`,
      strands: [
        {
          id: "algebra",
          number: 1,
          title: "Algebra & Number",
          color: "blue",
          icon: Calculator,
          assessments: tests?.term1?.tests?.map((test, idx) => ({
            id: `math-test-${idx + 1}`,
            type: "Unit Test",
            title: test.title,
            totalMarks: test.totalMarks,
            timeLimit: test.duration,
            sections: test.sections
          })) || []
        },
        {
          id: "geometry",
          number: 2,
          title: "Geometry & Trigonometry",
          color: "green",
          icon: Target,
          assessments: tests?.term2?.tests?.map((test, idx) => ({
            id: `math-test-${idx + 4}`,
            type: "Unit Test",
            title: test.title,
            totalMarks: test.totalMarks,
            timeLimit: test.duration,
            sections: test.sections
          })) || []
        },
        {
          id: "calculus",
          number: 3,
          title: "Calculus & Statistics",
          color: "purple",
          icon: BarChart,
          assessments: tests?.term3?.tests?.map((test, idx) => ({
            id: `math-test-${idx + 7}`,
            type: "Unit Test",
            title: test.title,
            totalMarks: test.totalMarks,
            timeLimit: test.duration,
            sections: test.sections
          })) || []
        }
      ]
    };
  };
  
  // Physics Assessment Data Generator
  const getPhysicsAssessmentData = (year) => {
    const yearNum = year.replace('year', '');
    return {
      subject: `Year ${yearNum} Physics`,
      strands: [
        { id: "mechanics", number: 1, title: "Mechanics", color: "blue", icon: Atom, assessments: [] },
        { id: "waves", number: 2, title: "Waves & Optics", color: "green", icon: Zap, assessments: [] },
        { id: "electricity", number: 3, title: "Electricity", color: "yellow", icon: Lightbulb, assessments: [] }
      ]
    };
  };
  
  // Economics Assessment Data Generator
  const getEconomicsAssessmentData = (year) => {
    const yearNum = year.replace('year', '');
    return {
      subject: `Year ${yearNum} Economics`,
      strands: [
        { id: "microeconomics", number: 1, title: "Microeconomics", color: "blue", icon: TrendingUp, assessments: [] },
        { id: "macroeconomics", number: 2, title: "Macroeconomics", color: "green", icon: Globe, assessments: [] },
        { id: "international", number: 3, title: "International Trade", color: "purple", icon: Building, assessments: [] }
      ]
    };
  };
  
  // Geography Assessment Data Generator
  const getGeographyAssessmentData = (year) => {
    const yearNum = year.replace('year', '');
    return {
      subject: `Year ${yearNum} Geography`,
      strands: [
        { id: "physical", number: 1, title: "Physical Geography", color: "green", icon: Map, assessments: [] },
        { id: "human", number: 2, title: "Human Geography", color: "blue", icon: Users, assessments: [] },
        { id: "skills", number: 3, title: "Geographical Skills", color: "purple", icon: Compass, assessments: [] }
      ]
    };
  };
  
  // Agriculture Assessment Data Generator
  const getAgricultureAssessmentData = (year) => {
    const yearNum = year.replace('year', '');
    return {
      subject: `Year ${yearNum} Agriculture`,
      strands: [
        { id: "crops", number: 1, title: "Crop Production", color: "green", icon: Leaf, assessments: [] },
        { id: "livestock", number: 2, title: "Animal Husbandry", color: "yellow", icon: Bug, assessments: [] },
        { id: "business", number: 3, title: "Farm Business", color: "blue", icon: DollarSign, assessments: [] }
      ]
    };
  };
  
  // Accounting Assessment Data Generator
  const getAccountingAssessmentData = (year) => {
    const yearNum = year.replace('year', '');
    return {
      subject: `Year ${yearNum} Accounting`,
      strands: [
        { id: "financial", number: 1, title: "Financial Accounting", color: "green", icon: Calculator, assessments: [] },
        { id: "reports", number: 2, title: "Financial Reports", color: "blue", icon: FileText, assessments: [] },
        { id: "analysis", number: 3, title: "Financial Analysis", color: "purple", icon: BarChart, assessments: [] }
      ]
    };
  };

  // French Assessment Data Generator (Years 7-10)
  const getFrenchAssessmentData = (year) => {
    const yearNum = year.replace('year', '');
    const tests = year === 'year7' ? year7FrenchUnitTests : 
                  year === 'year8' ? year8FrenchUnitTests : 
                  year === 'year9' ? year9FrenchUnitTests : year10FrenchUnitTests;
    
    return {
      subject: `Year ${yearNum} French`,
      strands: [
        { 
          id: "grammar", 
          number: 1, 
          title: tests?.term1?.title || "Grammar & Foundations", 
          color: "blue", 
          icon: BookOpen, 
          assessments: tests?.term1?.tests?.map((test, idx) => ({
            id: `french-t1-${idx + 1}`,
            type: "Unit Test",
            title: test.title,
            totalMarks: test.totalMarks,
            timeLimit: test.duration || "30 minutes",
            sections: test.sections || []
          })) || []
        },
        { 
          id: "conjugation", 
          number: 2, 
          title: tests?.term2?.title || "Conjugation & Verbs", 
          color: "green", 
          icon: FileText, 
          assessments: tests?.term2?.tests?.map((test, idx) => ({
            id: `french-t2-${idx + 1}`,
            type: "Unit Test",
            title: test.title,
            totalMarks: test.totalMarks,
            timeLimit: test.duration || "30 minutes",
            sections: test.sections || []
          })) || []
        },
        { 
          id: "vocabulary", 
          number: 3, 
          title: tests?.term3?.title || "Vocabulary & Communication", 
          color: "purple", 
          icon: MessageCircle, 
          assessments: tests?.term3?.tests?.map((test, idx) => ({
            id: `french-t3-${idx + 1}`,
            type: "Unit Test",
            title: test.title,
            totalMarks: test.totalMarks,
            timeLimit: test.duration || "30 minutes",
            sections: test.sections || []
          })) || []
        }
      ]
    };
  };

  const getESLAssessmentData = (year) => {
    const yearNum = year.replace('year', '');
    const tests = year === 'year11' ? year11ESLUnitTests : 
                  year === 'year12' ? year12ESLUnitTests : year13ESLUnitTests;
    
    return {
      subject: `Year ${yearNum} English (ESL)`,
      strands: [
        { 
          id: "literature", 
          number: 1, 
          title: "Literature", 
          color: "purple", 
          icon: BookOpen, 
          assessments: tests?.strand1?.tests?.map((test, idx) => ({
            id: `esl-lit-${idx + 1}`,
            type: "Unit Test",
            title: test.title,
            totalMarks: test.totalMarks,
            timeLimit: test.duration,
            questions: test.questions
          })) || []
        },
        { 
          id: "reading", 
          number: 2, 
          title: "Reading", 
          color: "blue", 
          icon: FileText, 
          assessments: tests?.strand2?.tests?.map((test, idx) => ({
            id: `esl-read-${idx + 1}`,
            type: "Unit Test",
            title: test.title,
            totalMarks: test.totalMarks,
            timeLimit: test.duration,
            questions: test.questions
          })) || []
        },
        { 
          id: "writing", 
          number: 3, 
          title: "Writing", 
          color: "green", 
          icon: PenTool, 
          assessments: tests?.strand3?.tests?.map((test, idx) => ({
            id: `esl-write-${idx + 1}`,
            type: "Unit Test",
            title: test.title,
            totalMarks: test.totalMarks,
            timeLimit: test.duration,
            questions: test.questions
          })) || []
        },
        { 
          id: "speaking", 
          number: 4, 
          title: "Listening & Speaking", 
          color: "orange", 
          icon: MessageCircle, 
          assessments: tests?.strand4?.tests?.map((test, idx) => ({
            id: `esl-speak-${idx + 1}`,
            type: "Unit Test",
            title: test.title,
            totalMarks: test.totalMarks,
            timeLimit: test.duration,
            questions: test.questions
          })) || []
        }
      ]
    };
  };
  
  const assessmentData = getAssessmentData();
  
  // Add null checks for data
  const currentStrand = data?.strands?.find(s => s.id === selectedStrand);
  const currentAssessmentStrand = assessmentData?.strands?.find(s => s.id === selectedAssessmentStrand);
  const colors = getStrandColors(currentStrand?.color || 'blue');
  const assessmentColors = getStrandColors(currentAssessmentStrand?.color || 'blue');

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking access...</p>
        </div>
      </div>
    );
  }

  // Show access denied if not an allowed teacher (curriculum subjects)
  if (!isAllowedTeacher) {
    return (
      <div className="space-y-6 p-4">
        <Card className="bg-gradient-to-r from-gray-600 to-gray-700 text-white">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Lock className="h-6 w-6" />
              Access Restricted
            </CardTitle>
          </CardHeader>
        </Card>
        
        <Card className="p-8 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="p-4 bg-red-100 rounded-full">
              <Lock className="h-12 w-12 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-800">Student Resources</h2>
            <p className="text-gray-600 max-w-md">
              This page contains curriculum materials and is only accessible to subject teachers.
            </p>
            <p className="text-sm text-gray-500 mt-4">
              If you believe you should have access, please contact the school administrator.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // Full Primary School Assessment Interface
  if (isPrimarySchool) {
    const primarySubjects = [
      { id: 'english', name: 'English', icon: MessageCircle, color: 'blue' },
      { id: 'mathematics', name: 'Mathematics', icon: Calculator, color: 'green' },
      { id: 'science', name: 'Science', icon: FlaskConical, color: 'purple' },
      { id: 'socialScience', name: 'Social Science', icon: Heart, color: 'red' }
    ];
    
    const currentSubjectData = primaryAssessments[primarySubject];
    const currentStrands = currentSubjectData?.strands || [];
    const currentYearData = currentSubjectData?.years?.[`year${primaryYear}`];
    const currentAssessments = currentYearData?.assessments || [];
    
    // Filter assessments by strand if selected
    const filteredAssessments = primaryStrand 
      ? currentAssessments.filter(a => a.strand === primaryStrand)
      : currentAssessments;
    
    const togglePrimaryAssessment = (id) => {
      setExpandedPrimaryAssessment(prev => ({ ...prev, [id]: !prev[id] }));
    };
    
    const togglePrimaryAnswers = (id) => {
      setShowPrimaryAnswers(prev => ({ ...prev, [id]: !prev[id] }));
    };
    
    const printPrimaryAssessment = (assessment, withAnswers = false) => {
      const printWindow = window.open('', '_blank');
      const subjectInfo = primarySubjects.find(s => s.id === primarySubject);
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${assessment.title} - ${withAnswers ? 'Teacher' : 'Student'} Version</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
            h1 { color: #1e40af; border-bottom: 2px solid #1e40af; padding-bottom: 10px; }
            h2 { color: #374151; margin-top: 20px; }
            .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
            .info { background: #f3f4f6; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
            .question { margin: 20px 0; padding: 15px; border-left: 4px solid #3b82f6; background: #f9fafb; }
            .question-num { font-weight: bold; color: #1e40af; }
            .marks { color: #6b7280; font-size: 14px; }
            .answer { margin-top: 10px; padding: 10px; background: ${withAnswers ? '#dcfce7' : '#fff'}; border-radius: 4px; }
            .answer-label { font-weight: bold; color: #16a34a; }
            .student-info { margin-top: 30px; border-top: 1px solid #e5e7eb; padding-top: 15px; }
            .line { border-bottom: 1px solid #000; display: inline-block; width: 200px; margin-left: 10px; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <h1>${assessment.title}</h1>
              <p><strong>Subject:</strong> ${subjectInfo?.name} | <strong>Year:</strong> ${primaryYear} | <strong>Strand:</strong> ${assessment.strand}</p>
            </div>
            <div style="text-align: right;">
              <p><strong>${withAnswers ? '🔑 TEACHER VERSION' : '📝 STUDENT VERSION'}</strong></p>
              <p style="color: #6b7280;">${assessment.term}</p>
            </div>
          </div>
          
          <div class="info">
            <p><strong>Type:</strong> ${assessment.type} | <strong>Total Marks:</strong> ${assessment.totalMarks} | <strong>Time:</strong> ${assessment.timeLimit}</p>
          </div>
          
          ${!withAnswers ? `
            <div class="student-info">
              <p><strong>Name:</strong> <span class="line"></span> <strong>Class:</strong> <span class="line"></span> <strong>Date:</strong> <span class="line"></span></p>
            </div>
          ` : ''}
          
          <h2>Questions</h2>
          ${assessment.questions?.map((q, idx) => `
            <div class="question">
              <p><span class="question-num">Question ${idx + 1}.</span> ${q.question} <span class="marks">(${q.marks} marks)</span></p>
              ${q.options ? `<ul>${q.options.map(opt => `<li>${opt}</li>`).join('')}</ul>` : ''}
              ${q.instructions ? `<p style="color: #6b7280; font-style: italic;">${q.instructions}</p>` : ''}
              ${withAnswers && q.correctAnswer ? `
                <div class="answer">
                  <span class="answer-label">Answer:</span> ${q.correctAnswer}
                </div>
              ` : ''}
            </div>
          `).join('') || '<p>Questions not yet available for this assessment.</p>'}
          
          <script>window.onload = function() { window.print(); }</script>
        </body>
        </html>
      `);
      printWindow.document.close();
    };
    
    return (
      <div className="space-y-6 p-4">
        {/* Header */}
        <Card className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <GraduationCap className="h-6 w-6" />
              Primary School Assessments
            </CardTitle>
            <CardDescription className="text-teal-100 mt-1">
              Year 1-6 Quizzes & Tests - Synced with Grade Book
            </CardDescription>
          </CardHeader>
        </Card>
        
        {/* Selectors */}
        <Card className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Subject Selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Subject:</span>
              <Select value={primarySubject} onValueChange={(val) => { setPrimarySubject(val); setPrimaryStrand(''); }}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select Subject" />
                </SelectTrigger>
                <SelectContent>
                  {primarySubjects.map(subj => (
                    <SelectItem key={subj.id} value={subj.id}>
                      <div className="flex items-center gap-2">
                        <subj.icon className="h-4 w-4" />
                        {subj.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Year Selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Year:</span>
              <Select value={primaryYear} onValueChange={setPrimaryYear}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {(teacherPrimaryYears.length > 0 ? teacherPrimaryYears : [1, 2, 3, 4, 5, 6]).map(year => (
                    <SelectItem key={year} value={String(year)}>Year {year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Strand Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Strand:</span>
              <Select value={primaryStrand || 'all'} onValueChange={(val) => setPrimaryStrand(val === 'all' ? '' : val)}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="All Strands" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Strands</SelectItem>
                  {currentStrands.map((strand, idx) => (
                    <SelectItem key={idx} value={strand}>{strand}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>
        
        {/* Assessment Info */}
        <Alert className="bg-teal-50 border-teal-200">
          <ClipboardCheck className="h-4 w-4 text-teal-600" />
          <AlertDescription className="text-teal-800">
            <strong>{filteredAssessments.length} assessments</strong> available for Year {primaryYear} {currentSubjectData?.subject}
            {primaryStrand && <span> - {primaryStrand}</span>}. 
            Print student or teacher versions. Grades sync automatically with the Grade Book.
          </AlertDescription>
        </Alert>
        
        {/* Strand Quick Tabs */}
        {currentStrands.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant={primaryStrand === '' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPrimaryStrand('')}
            >
              All
            </Button>
            {currentStrands.map((strand, idx) => (
              <Button
                key={idx}
                variant={primaryStrand === strand ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPrimaryStrand(strand)}
              >
                {strand}
              </Button>
            ))}
          </div>
        )}
        
        {/* Assessments List */}
        <div className="space-y-4">
          {filteredAssessments.length === 0 ? (
            <Card className="p-8 text-center">
              <div className="flex flex-col items-center gap-4">
                <ClipboardCheck className="h-12 w-12 text-gray-300" />
                <p className="text-gray-500">No assessments available for this selection.</p>
                <p className="text-sm text-gray-400">Try selecting a different year or strand.</p>
              </div>
            </Card>
          ) : (
            filteredAssessments.map((assessment) => {
              const isExpanded = expandedPrimaryAssessment[assessment.id];
              const showAnswers = showPrimaryAnswers[assessment.id];
              const typeColor = assessment.type === 'Quiz' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700';
              
              return (
                <Card key={assessment.id} className="overflow-hidden">
                  <CardHeader 
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => togglePrimaryAssessment(assessment.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Badge className={typeColor}>{assessment.type}</Badge>
                        <div>
                          <CardTitle className="text-lg">{assessment.title}</CardTitle>
                          <CardDescription>
                            {assessment.totalMarks} marks • {assessment.timeLimit} • {assessment.strand}
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Print Buttons */}
                        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => printPrimaryAssessment(assessment, false)}
                            className="flex items-center gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                            title="Print Student Version"
                          >
                            <Users className="h-3 w-3" />
                            <Printer className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => printPrimaryAssessment(assessment, true)}
                            className="flex items-center gap-1 text-orange-600 border-orange-200 hover:bg-orange-50"
                            title="Print Teacher Version"
                          >
                            <UserCheck className="h-3 w-3" />
                            <Printer className="h-3 w-3" />
                          </Button>
                        </div>
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  
                  {isExpanded && (
                    <CardContent className="pt-0">
                      <div className="border-t pt-4">
                        {/* Show/Hide Answers Toggle */}
                        <div className="flex justify-end mb-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => togglePrimaryAnswers(assessment.id)}
                            className={showAnswers ? 'bg-green-50 text-green-700' : ''}
                          >
                            {showAnswers ? <Eye className="h-4 w-4 mr-1" /> : <EyeOff className="h-4 w-4 mr-1" />}
                            {showAnswers ? 'Hide Answers' : 'Show Answers'}
                          </Button>
                        </div>
                        
                        {/* Questions */}
                        {assessment.questions?.length > 0 ? (
                          <div className="space-y-4">
                            {assessment.questions.map((q, idx) => (
                              <div key={q.id || idx} className="p-4 bg-gray-50 rounded-lg border-l-4 border-blue-400">
                                <div className="flex justify-between items-start">
                                  <p className="font-medium">
                                    <span className="text-blue-600">Q{idx + 1}.</span> {q.question}
                                  </p>
                                  <Badge variant="outline">{q.marks} marks</Badge>
                                </div>
                                {q.instructions && (
                                  <p className="text-sm text-gray-500 italic mt-1">{q.instructions}</p>
                                )}
                                {q.options && (
                                  <ul className="mt-2 ml-4 space-y-1">
                                    {q.options.map((opt, optIdx) => (
                                      <li key={optIdx} className={`text-sm ${showAnswers && q.correctAnswer === opt ? 'text-green-600 font-bold' : 'text-gray-600'}`}>
                                        {String.fromCharCode(65 + optIdx)}. {opt}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                                {showAnswers && q.correctAnswer && (
                                  <div className="mt-2 p-2 bg-green-50 rounded border border-green-200">
                                    <span className="font-semibold text-green-700">Answer: </span>
                                    <span className="text-green-600">{q.correctAnswer}</span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <FileQuestion className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                            <p>Questions not yet available for this assessment.</p>
                            <p className="text-sm">Check back later or contact your administrator.</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })
          )}
        </div>
        
        {/* Quick Links */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg">Quick Links</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Button variant="outline" className="h-20 flex-col" onClick={() => window.location.href = '/dashboard/gradebook'}>
                <ClipboardCheck className="h-6 w-6 mb-1 text-teal-600" />
                <span className="text-xs">Grade Book</span>
              </Button>
              <Button variant="outline" className="h-20 flex-col" onClick={() => window.location.href = '/dashboard/primary-curriculum'}>
                <BookOpen className="h-6 w-6 mb-1 text-blue-600" />
                <span className="text-xs">Curriculum</span>
              </Button>
              <Button variant="outline" className="h-20 flex-col" onClick={() => window.location.href = '/dashboard/attendance'}>
                <Users className="h-6 w-6 mb-1 text-green-600" />
                <span className="text-xs">Attendance</span>
              </Button>
              <Button variant="outline" className="h-20 flex-col" onClick={() => window.location.href = '/dashboard/reports'}>
                <FileText className="h-6 w-6 mb-1 text-purple-600" />
                <span className="text-xs">Reports</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <Card className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <GraduationCap className="h-6 w-6" />
                Student Resources
              </CardTitle>
              <CardDescription className="text-indigo-100 mt-1">
                {selectedSubject === 'ict' ? 'ICT / Computer Studies' : selectedSubject === 'chemistry' ? 'Chemistry' : selectedSubject === 'physics' ? 'Physics' : selectedSubject === 'french' ? 'French' : selectedSubject === 'geography' ? 'Geography' : selectedSubject === 'mathematics' ? 'Mathematics' : 'Economics'} - Notes, Assessments & Downloadable Materials
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Subject and Year Selector */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
          {/* Subject Selector - Only show subjects the teacher teaches */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">Subject:</span>
            {/* Show dropdown if teacher teaches multiple subjects, otherwise show static label */}
            {([teachesICT, teachesChemistry, teachesPhysics, teachesEconomics, teachesFrench, teachesGeography, teachesMathematics, teachesAgriculture, teachesAccounting, teachesESL, teachesHistory].filter(Boolean).length > 1) ? (
              <Select value={selectedSubject} onValueChange={(val) => { 
                setSelectedSubject(val); 
                setSelectedStrand('strand1');
                // Adjust year level when switching to/from French (French is Years 7-10, others are 11-13)
                if (val === 'french') {
                  setSelectedYear('year7');
                } else if (selectedSubject === 'french') {
                  setSelectedYear('year11');
                }
              }}>
                <SelectTrigger className="w-[180px]" data-testid="subject-selector">
                  <SelectValue placeholder="Select Subject" />
                </SelectTrigger>
                <SelectContent>
                  {teachesICT && <SelectItem value="ict">ICT / Computer Studies</SelectItem>}
                  {teachesChemistry && <SelectItem value="chemistry">Chemistry</SelectItem>}
                  {teachesPhysics && <SelectItem value="physics">Physics</SelectItem>}
                  {teachesEconomics && <SelectItem value="economics">Economics</SelectItem>}
                  {teachesFrench && <SelectItem value="french">French</SelectItem>}
                  {teachesGeography && <SelectItem value="geography">Geography</SelectItem>}
                  {teachesMathematics && <SelectItem value="mathematics">Mathematics</SelectItem>}
                  {teachesAgriculture && <SelectItem value="agriculture">Agriculture</SelectItem>}
                  {teachesAccounting && <SelectItem value="accounting">Accounting</SelectItem>}
                  {teachesESL && <SelectItem value="esl">English (ESL)</SelectItem>}
                  {teachesHistory && <SelectItem value="history">History</SelectItem>}
                </SelectContent>
              </Select>
            ) : (
              <span className="text-sm font-semibold px-3 py-2 bg-gray-100 rounded-md">
                {teachesHistory ? 'History' : teachesESL ? 'English (ESL)' : teachesAccounting ? 'Accounting' : teachesAgriculture ? 'Agriculture' : teachesMathematics ? 'Mathematics' : teachesGeography ? 'Geography' : teachesFrench ? 'French' : teachesPhysics ? 'Physics' : teachesEconomics ? 'Economics' : teachesChemistry ? 'Chemistry' : 'ICT / Computer Studies'}
              </span>
            )}
          </div>
          
          {/* Year Selector - Show different years based on subject */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">Year Level:</span>
            {selectedSubject === 'french' ? (
              // French is for Junior Secondary (Years 7-10)
              <Tabs value={selectedYear} onValueChange={(val) => { setSelectedYear(val); setSelectedStrand('strand1'); }}>
                <TabsList>
                  <TabsTrigger value="year7" className="text-sm" data-testid="year7-tab">
                    Year 7
                  </TabsTrigger>
                  <TabsTrigger value="year8" className="text-sm" data-testid="year8-tab">
                    Year 8
                  </TabsTrigger>
                  <TabsTrigger value="year9" className="text-sm" data-testid="year9-tab">
                    Year 9
                  </TabsTrigger>
                  <TabsTrigger value="year10" className="text-sm" data-testid="year10-tab">
                    Year 10
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            ) : (
              // Other subjects are for Senior Secondary (Years 11-13)
              <Tabs value={selectedYear} onValueChange={(val) => { setSelectedYear(val); setSelectedStrand('strand1'); }}>
                <TabsList>
                  <TabsTrigger value="year11" className="text-sm" data-testid="year11-tab">
                    Year 11
                  </TabsTrigger>
                  <TabsTrigger value="year12" className="text-sm" data-testid="year12-tab">
                    Year 12
                  </TabsTrigger>
                  <TabsTrigger value="year13" className="text-sm" data-testid="year13-tab">
                    Year 13
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            )}
          </div>
          
          {/* Toggle Expanded Notes */}
          <div className="flex items-center gap-2 ml-auto">
            <Button 
              variant={showExpandedNotes ? "default" : "outline"} 
              size="sm"
              onClick={() => setShowExpandedNotes(!showExpandedNotes)}
              className={showExpandedNotes ? "bg-emerald-600 hover:bg-emerald-700" : ""}
            >
              <BookOpen className="w-4 h-4 mr-2" />
              {showExpandedNotes ? "Detailed Notes Mode" : "Show Detailed Notes"}
            </Button>
          </div>
        </div>
      </Card>

      {/* Expanded Notes View (from studentNotes.js) */}
      {showExpandedNotes && (
        <Card className="border-2 border-emerald-200">
          <CardHeader className="bg-emerald-50">
            <CardTitle className="flex items-center gap-2 text-emerald-800">
              <BookOpen className="h-5 w-5" />
              Detailed Study Notes - {selectedSubject === 'ict' ? 'ICT' : selectedSubject === 'chemistry' ? 'Chemistry' : selectedSubject === 'physics' ? 'Physics' : selectedSubject === 'french' ? 'French' : selectedSubject === 'geography' ? 'Geography' : selectedSubject === 'mathematics' ? 'Mathematics' : selectedSubject === 'agriculture' ? 'Agriculture' : selectedSubject === 'accounting' ? 'Accounting' : selectedSubject === 'esl' ? 'English (ESL)' : 'Economics'} {selectedYear.replace('year', 'Year ')}
            </CardTitle>
            <CardDescription>
              Comprehensive study materials with key terms, study guides, and practice questions
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {(() => {
              const expandedNotes = getExpandedNotes();
              if (!expandedNotes) {
                return (
                  <div className="text-center py-8">
                    <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg mb-2">Detailed study notes for {selectedSubject === 'physics' ? 'Physics' : 'Economics'} are coming soon!</p>
                    <p className="text-gray-400 text-sm">In the meantime, use the Unit Tests and Exercises tabs for practice materials.</p>
                  </div>
                );
              }
              
              return (
                <Accordion type="single" collapsible className="space-y-4">
                  {Object.entries(expandedNotes).map(([strandKey, strand]) => {
                    // Skip if strand is null/undefined or doesn't have required properties
                    if (!strand || typeof strand !== 'object') return null;
                    
                    return (
                    <AccordionItem key={strandKey} value={strandKey} className="border rounded-lg">
                      <AccordionTrigger className="px-4 hover:no-underline hover:bg-emerald-50">
                        <div className="flex items-center gap-3">
                          <Badge className={
                            selectedSubject === 'ict' ? "bg-blue-100 text-blue-700" : 
                            selectedSubject === 'chemistry' ? "bg-green-100 text-green-700" :
                            selectedSubject === 'physics' ? "bg-purple-100 text-purple-700" :
                            selectedSubject === 'french' ? "bg-sky-100 text-sky-700" :
                            selectedSubject === 'mathematics' ? "bg-indigo-100 text-indigo-700" :
                            selectedSubject === 'geography' ? "bg-teal-100 text-teal-700" :
                            selectedSubject === 'agriculture' ? "bg-lime-100 text-lime-700" :
                            selectedSubject === 'accounting' ? "bg-emerald-100 text-emerald-700" :
                            "bg-amber-100 text-amber-700"
                          }>
                            {strand.units?.length || 0} Units
                          </Badge>
                          <span className="font-semibold">{strand.title || strandKey}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        {strand.image && (
                          <div className="mb-4 rounded-lg overflow-hidden h-32 max-w-md">
                            <img src={strand.image} alt={strand.title || strandKey} className="w-full h-full object-cover" loading="lazy" />
                          </div>
                        )}
                        <p className="text-sm text-gray-600 mb-4">{strand.overview || 'Study materials for this strand.'}</p>
                        
                        {strand.units && Array.isArray(strand.units) && strand.units.map((unit, idx) => (
                          <div key={idx} className="mb-6 p-4 bg-white border rounded-lg shadow-sm">
                            {/* Unit Image */}
                            {unit.image && (
                              <div className="mb-4 rounded-lg overflow-hidden h-40">
                                <img src={unit.image} alt={unit.title} className="w-full h-full object-cover" loading="lazy" />
                              </div>
                            )}
                            
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-semibold text-gray-800">
                                {unit.id}. {unit.title}
                              </h4>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  const subjectName = selectedSubject === 'ict' ? 'ICT' : selectedSubject === 'chemistry' ? 'Chemistry' : selectedSubject === 'physics' ? 'Physics' : selectedSubject === 'french' ? 'French' : selectedSubject === 'geography' ? 'Geography' : selectedSubject === 'mathematics' ? 'Mathematics' : selectedSubject === 'agriculture' ? 'Agriculture' : selectedSubject === 'accounting' ? 'Accounting' : 'Economics';
                                  const yearName = selectedYear.replace('year', 'Year ');
                                  const getThemeColors = () => {
                                    if (selectedSubject === 'ict') return { h1: '#1e40af', h2: '#2563eb', h3: '#3b82f6', bg: '#dbeafe', bgLight: '#eff6ff' };
                                    if (selectedSubject === 'chemistry') return { h1: '#166534', h2: '#15803d', h3: '#16a34a', bg: '#dcfce7', bgLight: '#f0fdf4' };
                                    if (selectedSubject === 'physics') return { h1: '#5b21b6', h2: '#6d28d9', h3: '#7c3aed', bg: '#ddd6fe', bgLight: '#f5f3ff' };
                                    if (selectedSubject === 'french') return { h1: '#0369a1', h2: '#0284c7', h3: '#0ea5e9', bg: '#e0f2fe', bgLight: '#f0f9ff' };
                                    if (selectedSubject === 'accounting') return { h1: '#047857', h2: '#059669', h3: '#10b981', bg: '#d1fae5', bgLight: '#ecfdf5' };
                                    return { h1: '#b45309', h2: '#d97706', h3: '#f59e0b', bg: '#fef3c7', bgLight: '#fffbeb' };
                                  };
                                  const colors = getThemeColors();
                                  const printWindow = window.open('', '_blank');
                                  printWindow.document.write(`
                                    <html>
                                    <head>
                                      <title>${yearName} ${subjectName} - ${unit.title}</title>
                                      <style>
                                        body { font-family: Arial, sans-serif; padding: 40px; line-height: 1.8; max-width: 800px; margin: 0 auto; }
                                        h1 { color: ${colors.h1}; border-bottom: 3px solid currentColor; padding-bottom: 10px; }
                                        h2 { color: ${colors.h2}; margin-top: 30px; }
                                        h3 { color: ${colors.h3}; margin-top: 20px; }
                                        .key-term { background: ${colors.bg}; padding: 8px 12px; margin: 5px 0; border-radius: 5px; }
                                        .objectives { background: ${colors.bgLight}; padding: 15px; border-radius: 8px; margin: 15px 0; }
                                        .questions { background: #fef3c7; padding: 15px; border-radius: 8px; margin: 15px 0; }
                                        .exam-tips { background: #dbeafe; padding: 15px; border-radius: 8px; margin: 15px 0; }
                                        pre { background: #f3f4f6; padding: 15px; border-radius: 5px; overflow-x: auto; white-space: pre-wrap; }
                                        table { border-collapse: collapse; width: 100%; margin: 15px 0; }
                                        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                                        th { background: ${colors.bgLight}; }
                                        img { max-width: 100%; height: auto; margin: 15px 0; border-radius: 8px; }
                                        @media print { body { padding: 20px; } img { max-height: 200px; } }
                                      </style>
                                    </head>
                                    <body>
                                      ${unit.image ? '<img src="' + unit.image + '" alt="' + unit.title + '">' : ''}
                                      <h1>${yearName} ${subjectName} - Unit ${unit.id}</h1>
                                      <h2>${unit.title}</h2>
                                      
                                      <div class="objectives">
                                        <h3>Learning Objectives</h3>
                                        <ul>${unit.objectives?.map(obj => '<li>' + obj + '</li>').join('') || ''}</ul>
                                      </div>
                                      
                                      <h3>Key Terms</h3>
                                      ${unit.keyTerms?.map(term => '<div class="key-term"><strong>' + term.term + ':</strong> ' + term.definition + '</div>').join('') || ''}
                                      
                                      <h3>Study Notes</h3>
                                      <div>${(unit.studyNotes || '').replace(/\n/g, '<br>')}</div>
                                      
                                      <div class="questions">
                                        <h3>Practice Questions</h3>
                                        <ol>${unit.practiceQuestions?.map(q => '<li>' + (typeof q === 'object' ? q.question : q) + '</li>').join('') || ''}</ol>
                                      </div>
                                      
                                      ${unit.examTips ? '<div class="exam-tips"><h3>Exam Tips</h3><ul>' + unit.examTips.map(tip => '<li>' + tip + '</li>').join('') + '</ul></div>' : ''}
                                    </body>
                                    </html>
                                  `);
                                  printWindow.document.close();
                                  printWindow.onload = () => printWindow.print();
                                }}
                              >
                                <Printer className="w-4 h-4 mr-1" />
                                Print Notes
                              </Button>
                            </div>
                            
                            {/* Learning Objectives */}
                            {unit.objectives && (
                              <div className="mb-4">
                                <h5 className="text-sm font-medium text-gray-700 mb-2">Learning Objectives:</h5>
                                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                                  {unit.objectives.slice(0, 4).map((obj, oIdx) => <li key={oIdx}>{obj}</li>)}
                                </ul>
                                {unit.objectives.length > 4 && <p className="text-xs text-gray-500 mt-1">+ {unit.objectives.length - 4} more...</p>}
                              </div>
                            )}
                            
                            {/* Key Terms */}
                            {unit.keyTerms && unit.keyTerms.length > 0 && (
                              <div className="mb-4">
                                <h5 className="text-sm font-medium text-gray-700 mb-2">Key Terms ({unit.keyTerms.length}):</h5>
                                <div className="grid md:grid-cols-2 gap-2">
                                  {unit.keyTerms.slice(0, 4).map((term, tIdx) => (
                                    <div key={tIdx} className={`p-2 rounded border ${
                                      selectedSubject === 'ict' ? 'bg-blue-50 border-blue-100' : 
                                      selectedSubject === 'chemistry' ? 'bg-green-50 border-green-100' :
                                      selectedSubject === 'physics' ? 'bg-purple-50 border-purple-100' :
                                      selectedSubject === 'french' ? 'bg-sky-50 border-sky-100' :
                                      selectedSubject === 'geography' ? 'bg-emerald-50 border-emerald-100' :
                                      selectedSubject === 'mathematics' ? 'bg-blue-50 border-blue-100' :
                                      'bg-amber-50 border-amber-100'
                                    }`}>
                                      <span className={`font-medium ${
                                        selectedSubject === 'ict' ? 'text-blue-700' : 
                                        selectedSubject === 'chemistry' ? 'text-green-700' :
                                        selectedSubject === 'physics' ? 'text-purple-700' :
                                        selectedSubject === 'french' ? 'text-sky-700' :
                                        selectedSubject === 'geography' ? 'text-emerald-700' :
                                        selectedSubject === 'mathematics' ? 'text-blue-700' :
                                        'text-amber-700'
                                      }`}>{term.term}:</span>
                                      <span className="text-sm text-gray-600 ml-1">{term.definition.substring(0, 80)}...</span>
                                    </div>
                                  ))}
                                </div>
                                {unit.keyTerms.length > 4 && <p className="text-xs text-gray-500 mt-2">+ {unit.keyTerms.length - 4} more terms in full notes</p>}
                              </div>
                            )}
                            
                            {/* Practice Questions Preview */}
                            {unit.practiceQuestions && unit.practiceQuestions.length > 0 && (
                              <div className="mb-4">
                                <h5 className="text-sm font-medium text-gray-700 mb-2">Practice Questions ({unit.practiceQuestions.length}):</h5>
                                <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
                                  {unit.practiceQuestions.slice(0, 3).map((q, qIdx) => (
                                    <li key={qIdx}>{typeof q === 'object' ? q.question : q}</li>
                                  ))}
                                </ol>
                                {unit.practiceQuestions.length > 3 && <p className="text-xs text-gray-500 mt-1">+ {unit.practiceQuestions.length - 3} more questions...</p>}
                              </div>
                            )}
                            
                            {/* Exam Tips */}
                            {unit.examTips && unit.examTips.length > 0 && (
                              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <h5 className="text-sm font-medium text-amber-800 mb-2">💡 Exam Tips</h5>
                                <ul className="list-disc list-inside text-sm text-amber-700 space-y-1">
                                  {unit.examTips.slice(0, 2).map((tip, tIdx) => <li key={tIdx}>{tip}</li>)}
                                </ul>
                              </div>
                            )}
                          </div>
                        ))}
                        
                        {(!strand.units || strand.units.length === 0) && (
                          <p className="text-sm text-gray-500 italic">Notes for this strand are being developed. Check back soon!</p>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                    );
                  })}
                </Accordion>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Main Tabs: Notes, Unit Tests, Exercises, Assessments, Term Exam */}
      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="notes" className="flex items-center gap-2">
            <ScrollText className="h-4 w-4" />
            Study Notes
          </TabsTrigger>
          <TabsTrigger value="tests" className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4" />
            Unit Tests
          </TabsTrigger>
          <TabsTrigger value="exercises" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Exercises
          </TabsTrigger>
          <TabsTrigger value="assessments" className="flex items-center gap-2">
            <Award className="h-4 w-4" />
            Assessments
          </TabsTrigger>
          <TabsTrigger value="termexam" className="flex items-center gap-2">
            <FileEdit className="h-4 w-4" />
            Term Exam
          </TabsTrigger>
        </TabsList>

        {/* UNIT TESTS TAB */}
        <TabsContent value="tests" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5 text-purple-600" />
                Unit Tests - {selectedSubject === 'ict' ? 'ICT' : selectedSubject === 'chemistry' ? 'Chemistry' : selectedSubject === 'physics' ? 'Physics' : selectedSubject === 'french' ? 'French' : selectedSubject === 'geography' ? 'Geography' : selectedSubject === 'mathematics' ? 'Mathematics' : selectedSubject === 'agriculture' ? 'Agriculture' : selectedSubject === 'accounting' ? 'Accounting' : 'Economics'} {selectedYear.replace('year', 'Year ')}
              </CardTitle>
              <CardDescription>
                Practice tests and quizzes for each unit
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(() => {
                // Get unit tests based on subject and year
                let unitTests = null;
                if (selectedSubject === 'ict') {
                  if (selectedYear === 'year11') unitTests = year11ICTUnitTests;
                  else if (selectedYear === 'year12') unitTests = year12ICTUnitTests;
                  else unitTests = year13ICTUnitTests;
                } else if (selectedSubject === 'chemistry') {
                  if (selectedYear === 'year11') unitTests = year11ChemistryUnitTests;
                  else if (selectedYear === 'year12') unitTests = year12ChemistryUnitTests;
                  else unitTests = year13ChemistryUnitTests;
                } else if (selectedSubject === 'physics') {
                  if (selectedYear === 'year11') unitTests = year11PhysicsUnitTests;
                  else if (selectedYear === 'year12') unitTests = year12PhysicsUnitTests;
                  else unitTests = year13PhysicsUnitTests;
                } else if (selectedSubject === 'economics') {
                  if (selectedYear === 'year11') unitTests = year11EconomicsUnitTests;
                  else if (selectedYear === 'year12') unitTests = year12EconomicsUnitTests;
                  else unitTests = year13EconomicsUnitTests;
                } else if (selectedSubject === 'french') {
                  // French is for Years 7-10
                  if (selectedYear === 'year7') unitTests = year7FrenchUnitTests;
                  else if (selectedYear === 'year8') unitTests = year8FrenchUnitTests;
                  else if (selectedYear === 'year9') unitTests = year9FrenchUnitTests;
                  else unitTests = year10FrenchUnitTests;
                } else if (selectedSubject === 'geography') {
                  if (selectedYear === 'year11') unitTests = year11GeographyUnitTests;
                  else if (selectedYear === 'year12') unitTests = year12GeographyUnitTests;
                  else unitTests = year13GeographyUnitTests;
                } else if (selectedSubject === 'mathematics') {
                  if (selectedYear === 'year11') unitTests = year11MathematicsUnitTests;
                  else if (selectedYear === 'year12') unitTests = year12MathematicsUnitTests;
                  else unitTests = year13MathematicsUnitTests;
                } else if (selectedSubject === 'agriculture') {
                  if (selectedYear === 'year11') unitTests = year11AgricultureUnitTests;
                  else if (selectedYear === 'year12') unitTests = year12AgricultureUnitTests;
                  else unitTests = year13AgricultureUnitTests;
                } else if (selectedSubject === 'accounting') {
                  if (selectedYear === 'year11') unitTests = year11AccountingUnitTests;
                  else if (selectedYear === 'year12') unitTests = year12AccountingUnitTests;
                  else unitTests = year13AccountingUnitTests;
                }

                if (!unitTests || Object.keys(unitTests).length === 0) {
                  return <p className="text-gray-500 italic">Unit tests for this subject/year are being developed.</p>;
                }

                return (
                  <Accordion type="multiple" className="space-y-2">
                    {Object.entries(unitTests).map(([key, test]) => {
                      // Handle French structure: { term1: { title: "...", tests: [...] } }
                      // vs other structures: { unit1: [...] } or { unit1: { ... } }
                      let testData;
                      let unitTitle;
                      
                      if (test && test.tests && Array.isArray(test.tests)) {
                        // French structure: { title: "Term 1 - ...", tests: [...] }
                        testData = test.tests;
                        unitTitle = test.title || key.replace('term', 'Term ');
                      } else if (Array.isArray(test)) {
                        testData = test;
                        unitTitle = key.replace('unit', 'Unit ').replace('term', 'Term ');
                      } else {
                        testData = [test];
                        unitTitle = test?.unitTitle || test?.title || key.replace('unit', 'Unit ').replace('term', 'Term ');
                      }
                      
                      return (
                        <AccordionItem key={key} value={key} className="border rounded-lg">
                          <AccordionTrigger className="px-4 hover:no-underline hover:bg-purple-50">
                            <div className="flex items-center gap-3">
                              <Badge className="bg-purple-100 text-purple-700">
                                {testData.length || 1} Test{testData.length !== 1 ? 's' : ''}
                              </Badge>
                              <span className="font-medium">{unitTitle}</span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-4">
                            <div className="space-y-4">
                              {testData.map((t, idx) => {
                                // Get questions from either direct questions array or from sections
                                const allQuestions = t?.questions || (t?.sections ? t.sections.flatMap(s => s.questions || []) : []);
                                
                                return (
                                <div key={idx} className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                                  <div className="flex items-center justify-between mb-2">
                                    <h5 className="font-medium text-purple-800">{t?.title || t?.testTitle || `Test ${idx + 1}`}</h5>
                                    <div className="flex items-center gap-2">
                                      {t?.strand && <Badge variant="outline" className="bg-blue-50">{t.strand}</Badge>}
                                      {t?.duration && <Badge variant="outline">{t.duration}</Badge>}
                                      {t?.totalMarks && <Badge variant="outline">{t.totalMarks} marks</Badge>}
                                    </div>
                                  </div>
                                  {t?.topics && <p className="text-sm text-gray-600 mb-2">Topics: {t.topics.join(', ')}</p>}
                                  {t?.description && <p className="text-sm text-gray-600 mb-3">{t.description}</p>}
                                  
                                  {/* Sections display for Physics-style tests */}
                                  {t?.sections && t.sections.length > 0 && (
                                    <div className="mt-3 space-y-3">
                                      <p className="text-sm font-medium text-purple-700">Sections:</p>
                                      {t.sections.map((section, sIdx) => (
                                        <div key={sIdx} className="p-2 bg-white rounded border">
                                          <div className="flex justify-between items-center">
                                            <span className="font-medium text-sm">{section.name}</span>
                                            <Badge variant="outline" className="text-xs">{section.marks} marks</Badge>
                                          </div>
                                          {section.questions && section.questions.length > 0 && (
                                            <p className="text-xs text-gray-500 mt-1">{section.questions.length} questions</p>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  
                                  {/* Questions Preview for ICT/Chemistry style */}
                                  {!t?.sections && allQuestions.length > 0 && (
                                    <div className="mt-3">
                                      <p className="text-sm font-medium text-purple-700 mb-2">Sample Questions ({allQuestions.length} total):</p>
                                      <ol className="list-decimal list-inside text-sm text-gray-600 space-y-2">
                                        {allQuestions.slice(0, 3).map((q, qIdx) => (
                                          <li key={qIdx} className="p-2 bg-white rounded">
                                            {typeof q === 'string' ? q : (q.question || q.text || 'Question')}
                                            {q.marks && <span className="text-purple-600 ml-2">({q.marks} marks)</span>}
                                          </li>
                                        ))}
                                      </ol>
                                      {allQuestions.length > 3 && (
                                        <p className="text-xs text-gray-500 mt-2 italic">+ {allQuestions.length - 3} more questions</p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                              })}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* EXERCISES TAB */}
        <TabsContent value="exercises" className="mt-4">
          <Alert className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Practice exercises and worksheets. Use &quot;Print Student&quot; for blank worksheets or &quot;Print Teacher&quot; for answer keys.
            </AlertDescription>
          </Alert>
          
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                Exercises - {selectedSubject === 'ict' ? 'ICT' : selectedSubject === 'chemistry' ? 'Chemistry' : selectedSubject === 'physics' ? 'Physics' : selectedSubject === 'french' ? 'French' : selectedSubject === 'geography' ? 'Geography' : selectedSubject === 'mathematics' ? 'Mathematics' : 'Economics'} {selectedYear.replace('year', 'Year ')}
              </CardTitle>
              <CardDescription>
                Practice activities and worksheets for each unit
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(() => {
                // Get exercises based on subject and year
                let exercises = null;
                if (selectedSubject === 'ict') {
                  if (selectedYear === 'year11') exercises = year11ICTExercises;
                  else if (selectedYear === 'year12') exercises = year12ICTExercises;
                  else exercises = year13ICTExercises;
                } else if (selectedSubject === 'chemistry') {
                  if (selectedYear === 'year11') exercises = year11ChemistryExercises;
                  else if (selectedYear === 'year12') exercises = year12ChemistryExercises;
                  else exercises = year13ChemistryExercises;
                } else if (selectedSubject === 'physics') {
                  if (selectedYear === 'year11') exercises = year11PhysicsExercises;
                  else if (selectedYear === 'year12') exercises = year12PhysicsExercises;
                  else exercises = year13PhysicsExercises;
                } else if (selectedSubject === 'economics') {
                  if (selectedYear === 'year11') exercises = year11EconomicsExercises;
                  else if (selectedYear === 'year12') exercises = year12EconomicsExercises;
                  else exercises = year13EconomicsExercises;
                } else if (selectedSubject === 'french') {
                  // French is for Years 7-10
                  if (selectedYear === 'year7') exercises = year7FrenchExercises;
                  else if (selectedYear === 'year8') exercises = year8FrenchExercises;
                  else if (selectedYear === 'year9') exercises = year9FrenchExercises;
                  else exercises = year10FrenchExercises;
                }

                if (!exercises || Object.keys(exercises).length === 0) {
                  return <p className="text-gray-500 italic">Exercises for this subject/year are being developed.</p>;
                }

                // Helper function to print exercise
                const printExercise = (exercise, unitTitle, unitNumber, withAnswers) => {
                  const subjectName = selectedSubject === 'ict' ? 'ICT' : selectedSubject === 'chemistry' ? 'Chemistry' : selectedSubject === 'physics' ? 'Physics' : selectedSubject === 'french' ? 'French' : selectedSubject === 'geography' ? 'Geography' : selectedSubject === 'mathematics' ? 'Mathematics' : selectedSubject === 'agriculture' ? 'Agriculture' : selectedSubject === 'accounting' ? 'Accounting' : 'Economics';
                  const yearName = selectedYear.replace('year', 'Year ');
                  const themeColor = selectedSubject === 'ict' ? '#1e40af' : selectedSubject === 'chemistry' ? '#166534' : selectedSubject === 'physics' ? '#7c3aed' : selectedSubject === 'french' ? '#0284c7' : selectedSubject === 'geography' ? '#059669' : selectedSubject === 'mathematics' ? '#1d4ed8' : selectedSubject === 'accounting' ? '#059669' : '#d97706';
                  const printWindow = window.open('', '_blank');
                  printWindow.document.write(`
                    <html>
                    <head>
                      <title>${yearName} ${subjectName} - ${exercise.title}${withAnswers ? ' (Answer Key)' : ''}</title>
                      <style>
                        body { font-family: Arial, sans-serif; padding: 40px; line-height: 1.8; max-width: 800px; margin: 0 auto; }
                        h1 { color: ${themeColor}; border-bottom: 3px solid ${themeColor}; padding-bottom: 10px; font-size: 24px; }
                        h2 { color: ${themeColor}; margin-top: 20px; font-size: 18px; }
                        .header { background: #f8fafc; padding: 15px; border-radius: 8px; margin-bottom: 20px; border-left: 4px solid ${themeColor}; }
                        .task { background: #f8fafc; padding: 12px; margin: 10px 0; border-radius: 5px; }
                        .task-number { font-weight: bold; color: ${themeColor}; }
                        .answer { background: #fef3c7; padding: 10px; margin-top: 8px; border-radius: 5px; border-left: 3px solid #f59e0b; }
                        .answer-label { font-weight: bold; color: #b45309; }
                        .answer-line { border-bottom: 1px solid #ccc; height: 25px; margin: 5px 0; }
                        .materials { background: #ecfdf5; padding: 10px; border-radius: 5px; margin-top: 15px; }
                        @media print { body { padding: 20px; } .answer { break-inside: avoid; } }
                      </style>
                    </head>
                    <body>
                      <div class="header">
                        <h1>${yearName} ${subjectName} - ${unitTitle}</h1>
                        <h2>${exercise.title}${withAnswers ? ' - ANSWER KEY' : ''}</h2>
                        ${!withAnswers ? '<p><strong>Name:</strong> _________________________ <strong>Date:</strong> _____________</p>' : '<p><strong>Teacher Copy</strong></p>'}
                      </div>
                      
                      ${exercise.description || exercise.instructions ? `
                        <h3>Instructions</h3>
                        <p>${exercise.description || exercise.instructions}</p>
                      ` : ''}
                      
                      ${exercise.tasks && exercise.tasks.length > 0 ? `
                        <h3>Tasks</h3>
                        ${exercise.tasks.map((task, idx) => `
                          <div class="task">
                            <span class="task-number">${idx + 1}.</span> ${task}
                            ${!withAnswers ? '<div class="answer-line"></div><div class="answer-line"></div>' : ''}
                            ${withAnswers && exercise.answers && exercise.answers[idx] ? `
                              <div class="answer">
                                <span class="answer-label">Answer:</span> ${exercise.answers[idx]}
                              </div>
                            ` : ''}
                          </div>
                        `).join('')}
                      ` : ''}
                      
                      ${exercise.materials ? `
                        <div class="materials">
                          <strong>Materials Needed:</strong> ${exercise.materials}
                        </div>
                      ` : ''}
                      
                      <p style="margin-top: 30px; font-size: 12px; color: #666;">
                        ${yearName} ${subjectName} Curriculum - St Patrick's College
                      </p>
                    </body>
                    </html>
                  `);
                  printWindow.document.close();
                  printWindow.onload = () => printWindow.print();
                };

                return (
                  <div className="space-y-3">
                    {Object.entries(exercises).map(([key, unitExercises]) => {
                      const exercisesArray = Array.isArray(unitExercises) ? unitExercises : (unitExercises.exercises || [unitExercises]);
                      const unitTitle = unitExercises.unitTitle || `Unit ${key.replace('unit', '')}`;
                      const unitNumber = key.replace('unit', '');

                      return (
                        <Card key={key} className="border border-gray-200">
                          <CardHeader className="py-3 px-4 bg-gray-50">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Badge className="bg-blue-100 text-blue-700">
                                  {exercisesArray.length} Exercise{exercisesArray.length !== 1 ? 's' : ''}
                                </Badge>
                                <span className="font-medium text-gray-800">{unitTitle}</span>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="py-2">
                            <div className="divide-y">
                              {exercisesArray.map((exercise, idx) => (
                                <div key={idx} className="py-3 flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-medium text-sm">
                                      {idx + 1}
                                    </div>
                                    <div>
                                      <p className="font-medium text-gray-800">{exercise.title}</p>
                                      <p className="text-xs text-gray-500">
                                        {exercise.type || 'Practice'} • {exercise.tasks?.length || exercise.questionCount || 0} tasks
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex gap-1">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => printExercise(exercise, unitTitle, unitNumber, false)}
                                      className="flex items-center gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                                      title="Print Student Version (Blank)"
                                    >
                                      <Users className="h-3 w-3" />
                                      <Printer className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => printExercise(exercise, unitTitle, unitNumber, true)}
                                      className="flex items-center gap-1 text-orange-600 border-orange-200 hover:bg-orange-50"
                                      title="Print Teacher Version (With Answers)"
                                    >
                                      <UserCheck className="h-3 w-3" />
                                      <Printer className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* NOTES TAB */}
        <TabsContent value="notes" className="mt-4">
          {/* Show coming soon message for subjects without detailed notes */}
          {!data ? (
            <Card className="p-8 text-center">
              <div className="flex flex-col items-center gap-4">
                <div className="p-4 bg-purple-100 rounded-full">
                  <BookOpen className="h-12 w-12 text-purple-500" />
                </div>
                <h2 className="text-xl font-bold text-gray-800">Study Notes Coming Soon!</h2>
                <p className="text-gray-600 max-w-md">
                  Detailed study notes for {selectedSubject === 'physics' ? 'Physics' : selectedSubject === 'economics' ? 'Economics' : selectedSubject} are being developed.
                </p>
                <p className="text-sm text-gray-500">
                  In the meantime, check out the <strong>Unit Tests</strong> and <strong>Exercises</strong> tabs for practice materials.
                </p>
              </div>
            </Card>
          ) : (
          <>
          {/* Strand Selector - Dynamic based on available strands */}
          <Tabs value={selectedStrand} onValueChange={setSelectedStrand}>
            <TabsList className={`grid w-full mb-4`} style={{ gridTemplateColumns: `repeat(${data?.strands?.length || 2}, 1fr)` }}>
              {data?.strands?.map((strand, idx) => (
                <TabsTrigger key={strand.id} value={strand.id} className="text-xs md:text-sm">
                  Strand {strand.number || idx + 1}
                </TabsTrigger>
              ))}
            </TabsList>

            {data?.strands?.map((strand) => {
              const strandColors = getStrandColors(strand.color);
              const StrandIcon = strand.icon || BookOpen; // Fallback to BookOpen if no icon defined
              
              return (
                <TabsContent key={strand.id} value={strand.id} className="space-y-4">
                  {/* Strand Header */}
                  <Card className={`border-l-4 ${strandColors.border} ${strandColors.light}`}>
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${strandColors.bg}`}>
                          {StrandIcon && <StrandIcon className="h-6 w-6 text-white" />}
                        </div>
                        <div>
                          <Badge className={strandColors.bg}>Strand {strand.number}</Badge>
                          <CardTitle className="text-xl mt-1">{strand.title}</CardTitle>
                          <CardDescription>{strand.units?.length || 0} units with study materials</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>

                  {/* Units */}
                  <div className="space-y-4">
                    {(strand.units || []).map((unit) => (
                      <Card key={unit.id} className="overflow-hidden">
                        <CardHeader 
                          className={`cursor-pointer hover:bg-gray-50 ${strandColors.light}`}
                          onClick={() => toggleUnit(unit.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Badge className={strandColors.bg}>Unit {unit.id}</Badge>
                              <div>
                                <CardTitle className="text-lg">{unit.title}</CardTitle>
                                <CardDescription>{unit.overview}</CardDescription>
                              </div>
                            </div>
                            {expandedUnits[unit.id] ? (
                              <ChevronDown className="h-5 w-5 text-gray-400" />
                            ) : (
                              <ChevronRight className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                        </CardHeader>

                        {expandedUnits[unit.id] && (
                          <CardContent className="pt-4 space-y-6">
                            {/* Key Terms */}
                            {unit.keyTerms && unit.keyTerms.length > 0 && (
                              <div>
                                <h4 className="font-semibold text-sm flex items-center gap-2 mb-2">
                                  <List className="h-4 w-4" />
                                  Key Terms
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {unit.keyTerms.map((term, idx) => (
                                    <Badge key={idx} variant="outline">
                                      {typeof term === 'object' ? term.term : term}
                                    </Badge>
                                  ))}
                                </div>
                                {/* Show definitions if terms are objects */}
                                {unit.keyTerms.some(t => typeof t === 'object' && t.definition) && (
                                  <div className="mt-3 space-y-2">
                                    {unit.keyTerms.filter(t => typeof t === 'object' && t.definition).map((term, idx) => (
                                      <div key={idx} className="text-sm">
                                        <span className="font-medium text-gray-800">{term.term}:</span>{' '}
                                        <span className="text-gray-600">{term.definition}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}

                            <Separator />

                            {/* Notes Content - Handle both formats */}
                            <div className="space-y-6">
                              {unit.notes && unit.notes.length > 0 ? (
                                unit.notes.map((note, idx) => (
                                  <div key={idx} className="space-y-2">
                                    <h4 className={`font-bold ${strandColors.text}`}>{note.heading}</h4>
                                    <div className="prose prose-sm max-w-none">
                                      <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 bg-gray-50 p-4 rounded-lg overflow-x-auto">
                                        {note.content}
                                      </pre>
                                    </div>
                                  </div>
                                ))
                              ) : unit.studyNotes ? (
                                <div className="space-y-2">
                                  <h4 className={`font-bold ${strandColors.text}`}>Study Notes</h4>
                                  <div className="prose prose-sm max-w-none">
                                    <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 bg-gray-50 p-4 rounded-lg overflow-x-auto">
                                      {unit.studyNotes}
                                    </pre>
                                  </div>
                                </div>
                              ) : null}
                            </div>

                            <Separator />

                            {/* Summary / Exam Tips - Handle both formats */}
                            {(unit.summary || unit.examTips) && (unit.summary?.length > 0 || unit.examTips?.length > 0) && (
                              <div className={`p-4 rounded-lg ${strandColors.light}`}>
                                <h4 className="font-semibold flex items-center gap-2 mb-3">
                                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                                  {unit.examTips ? '💡 Exam Tips' : 'Key Takeaways'}
                                </h4>
                                <ul className="space-y-2">
                                  {(unit.summary || unit.examTips || []).map((point, idx) => (
                                    <li key={idx} className="flex items-start gap-2 text-sm">
                                      <span className="font-bold text-green-600">✓</span>
                                      {point}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Practice Questions - Handle object format */}
                            {unit.practiceQuestions && unit.practiceQuestions.length > 0 && (
                              <>
                                <Separator />
                                <div>
                                  <h4 className="font-semibold flex items-center gap-2 mb-3">
                                    <HelpCircle className="h-4 w-4" />
                                    Practice Questions
                                  </h4>
                                  <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                                    {unit.practiceQuestions.map((q, idx) => (
                                      <li key={idx}>
                                        <span className="font-medium">{typeof q === 'object' ? q.question : q}</span>
                                        {typeof q === 'object' && q.answer && (
                                          <div className="ml-6 mt-1 text-green-600 text-xs">
                                            Answer: {q.answer}
                                          </div>
                                        )}
                                      </li>
                                    ))}
                                  </ol>
                                </div>
                              </>
                            )}

                            <Separator />

                            {/* Downloadable Resources */}
                            {unit.resources && unit.resources.length > 0 && (
                              <div>
                                <h4 className="font-semibold flex items-center gap-2 mb-3">
                                  <Download className="h-4 w-4" />
                                  Downloadable Resources
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {unit.resources.map((resource, idx) => {
                                    const ResourceIcon = getResourceIcon(resource.type);
                                    return (
                                      <Card key={idx} className="hover:shadow-md transition-shadow">
                                        <CardContent className="p-3 flex items-center justify-between">
                                          <div className="flex items-center gap-3">
                                            <div className={`p-2 rounded ${
                                              resource.type === 'ppt' ? 'bg-orange-100' :
                                              resource.type === 'pdf' ? 'bg-red-100' :
                                              resource.type === 'video' ? 'bg-purple-100' :
                                              'bg-blue-100'
                                            }`}>
                                              <ResourceIcon className={`h-5 w-5 ${
                                                resource.type === 'ppt' ? 'text-orange-600' :
                                                resource.type === 'pdf' ? 'text-red-600' :
                                                resource.type === 'video' ? 'text-purple-600' :
                                                'text-blue-600'
                                              }`} />
                                            </div>
                                            <div>
                                              <p className="font-medium text-sm">{resource.title}</p>
                                              {resource.size && (
                                                <p className="text-xs text-muted-foreground">{resource.size}</p>
                                              )}
                                            </div>
                                          </div>
                                          <Button size="sm" variant="outline">
                                            <Download className="h-3 w-3 mr-1" />
                                            {resource.url ? 'Open' : 'Download'}
                                          </Button>
                                        </CardContent>
                                      </Card>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </CardContent>
                        )}
                      </Card>
                    ))}
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
          </>
          )}
        </TabsContent>

        {/* ASSESSMENTS TAB */}
        <TabsContent value="assessments" className="mt-4">
          <Alert className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Practice assessments with answer keys. Click &quot;Show Answers&quot; to reveal correct answers and explanations.
            </AlertDescription>
          </Alert>
          
          {/* Strand Selector for Assessments - Dynamic based on available strands */}
          <Tabs value={selectedAssessmentStrand} onValueChange={setSelectedAssessmentStrand}>
            <TabsList className={`grid w-full mb-4`} style={{ gridTemplateColumns: `repeat(${assessmentData?.strands?.length || 2}, 1fr)` }}>
              {assessmentData?.strands?.map((strand, idx) => (
                <TabsTrigger key={strand.id} value={strand.id} className="text-xs md:text-sm">
                  Strand {strand.number || idx + 1}
                </TabsTrigger>
              ))}
            </TabsList>

            {assessmentData?.strands?.map((strand) => {
              const strandColors = getStrandColors(strand.color);
              const StrandIcon = strand.icon || FileText; // Fallback to FileText if no icon defined
              
              return (
                <TabsContent key={strand.id} value={strand.id} className="space-y-4">
                  {/* Strand Header */}
                  <Card className={`border-l-4 ${strandColors.border} ${strandColors.light}`}>
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${strandColors.bg}`}>
                          {StrandIcon && <StrandIcon className="h-6 w-6 text-white" />}
                        </div>
                        <div>
                          <Badge className={strandColors.bg}>Strand {strand.number}</Badge>
                          <CardTitle className="text-xl mt-1">{strand.title}</CardTitle>
                          <CardDescription>{strand.assessments?.length || 0} assessments available</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>

                  {/* Assessments */}
                  <div className="space-y-4">
                    {(!strand.assessments || strand.assessments.length === 0) ? (
                      <Card className="p-6 text-center text-gray-500">
                        <p>No assessments available for this strand yet.</p>
                        <p className="text-sm mt-2">Check the Unit Tests tab for practice materials.</p>
                      </Card>
                    ) : (
                    strand.assessments.map((assessment) => (
                      <Card key={assessment.id} className="overflow-hidden">
                        <CardHeader 
                          className={`cursor-pointer hover:bg-gray-50 ${strandColors.light}`}
                          onClick={() => toggleAssessment(assessment.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Badge variant="outline" className={`${strandColors.text} border-current`}>
                                {assessment.type}
                              </Badge>
                              <div>
                                <CardTitle className="text-lg">{assessment.title}</CardTitle>
                                <CardDescription>
                                  {assessment.totalMarks} marks • {assessment.timeLimit}
                                </CardDescription>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {/* Print Buttons */}
                              <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => printAssessment(assessment, strand, false)}
                                  className="flex items-center gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                                  title="Print Student Version (No Answers)"
                                >
                                  <Users className="h-3 w-3" />
                                  <Printer className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => printAssessment(assessment, strand, true)}
                                  className="flex items-center gap-1 text-orange-600 border-orange-200 hover:bg-orange-50"
                                  title="Print Teacher Version (With Answers)"
                                >
                                  <UserCheck className="h-3 w-3" />
                                  <Printer className="h-3 w-3" />
                                </Button>
                              </div>
                              {expandedAssessments[assessment.id] ? (
                                <ChevronDown className="h-5 w-5 text-gray-400" />
                              ) : (
                                <ChevronRight className="h-5 w-5 text-gray-400" />
                              )}
                            </div>
                          </div>
                        </CardHeader>

                        {expandedAssessments[assessment.id] && (
                          <CardContent className="pt-4 space-y-6">
                            {/* Print Buttons at Top */}
                            <div className="flex gap-2 pb-4 border-b">
                              <Button
                                variant="outline"
                                onClick={() => printAssessment(assessment, strand, false)}
                                className="flex items-center gap-2 text-blue-600 border-blue-300 hover:bg-blue-50"
                              >
                                <Users className="h-4 w-4" />
                                <Printer className="h-4 w-4" />
                                Print Student Version
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => printAssessment(assessment, strand, true)}
                                className="flex items-center gap-2 text-orange-600 border-orange-300 hover:bg-orange-50"
                              >
                                <UserCheck className="h-4 w-4" />
                                <Printer className="h-4 w-4" />
                                Print Teacher Version
                              </Button>
                            </div>

                            {/* Questions - Handle both questions array and sections array */}
                            {assessment.questions && assessment.questions.length > 0 ? (
                            assessment.questions.map((question, qIndex) => {
                              const questionKey = `${assessment.id}-${question.id}`;
                              const isAnswerShown = showAnswers[questionKey];
                              
                              return (
                                <div key={question.id} className="border rounded-lg p-4 space-y-3">
                                  {/* Question Header */}
                                  <div className="flex items-start justify-between">
                                    <div className="flex items-start gap-2">
                                      <Badge variant="secondary" className="mt-0.5">Q{question.id}</Badge>
                                      <div>
                                        <p className="font-medium">{question.question}</p>
                                        {question.marks && (
                                          <span className="text-sm text-muted-foreground">({question.marks} marks)</span>
                                        )}
                                      </div>
                                    </div>
                                    <Badge variant="outline" className="text-xs">
                                      {question.type === 'multiple_choice' ? 'Multiple Choice' :
                                       question.type === 'true_false' ? 'True/False' :
                                       question.type === 'short_answer' ? 'Short Answer' :
                                       question.type === 'practical' ? 'Practical' :
                                       question.type === 'matching' ? 'Matching' : question.type}
                                    </Badge>
                                  </div>

                                  {/* Multiple Choice Options */}
                                  {question.type === 'multiple_choice' && question.options && (
                                    <div className="space-y-2 ml-8">
                                      {question.options.map((option, oIndex) => (
                                        <div 
                                          key={oIndex} 
                                          className={`flex items-center gap-2 p-2 rounded ${
                                            isAnswerShown && option === question.correctAnswer 
                                              ? 'bg-green-100 border border-green-500' 
                                              : 'bg-gray-50'
                                          }`}
                                        >
                                          <CircleDot className={`h-4 w-4 ${
                                            isAnswerShown && option === question.correctAnswer 
                                              ? 'text-green-600' 
                                              : 'text-gray-400'
                                          }`} />
                                          <span>{option}</span>
                                          {isAnswerShown && option === question.correctAnswer && (
                                            <CheckCircle2 className="h-4 w-4 text-green-600 ml-auto" />
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* True/False Options */}
                                  {question.type === 'true_false' && (
                                    <div className="space-y-2 ml-8">
                                      {['True', 'False'].map((option) => (
                                        <div 
                                          key={option} 
                                          className={`flex items-center gap-2 p-2 rounded ${
                                            isAnswerShown && (option === 'True') === question.correctAnswer 
                                              ? 'bg-green-100 border border-green-500' 
                                              : 'bg-gray-50'
                                          }`}
                                        >
                                          <CircleDot className={`h-4 w-4 ${
                                            isAnswerShown && (option === 'True') === question.correctAnswer 
                                              ? 'text-green-600' 
                                              : 'text-gray-400'
                                          }`} />
                                          <span>{option}</span>
                                          {isAnswerShown && (option === 'True') === question.correctAnswer && (
                                            <CheckCircle2 className="h-4 w-4 text-green-600 ml-auto" />
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Matching Question */}
                                  {question.type === 'matching' && question.pairs && (
                                    <div className="ml-8">
                                      <div className="grid grid-cols-2 gap-4">
                                        <div>
                                          <p className="font-medium text-sm mb-2">Terms:</p>
                                          {question.pairs.map((pair, pIndex) => (
                                            <div key={pIndex} className="p-2 bg-gray-50 rounded mb-2">
                                              {pair.term}
                                            </div>
                                          ))}
                                        </div>
                                        <div>
                                          <p className="font-medium text-sm mb-2">Definitions:</p>
                                          {question.pairs.map((pair, pIndex) => (
                                            <div key={pIndex} className={`p-2 rounded mb-2 ${
                                              isAnswerShown ? 'bg-green-50 border border-green-200' : 'bg-gray-50'
                                            }`}>
                                              {isAnswerShown ? `${pair.term} → ${pair.definition}` : pair.definition}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Short Answer / Practical Space */}
                                  {(question.type === 'short_answer' || question.type === 'practical') && (
                                    <div className="ml-8">
                                      <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-4 min-h-[100px]">
                                        <p className="text-sm text-muted-foreground italic">Write your answer here...</p>
                                      </div>
                                    </div>
                                  )}

                                  {/* Show/Hide Answer Button */}
                                  <div className="flex items-center gap-2 ml-8">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => toggleShowAnswer(questionKey)}
                                      className="flex items-center gap-2"
                                    >
                                      {isAnswerShown ? (
                                        <>
                                          <EyeOff className="h-4 w-4" />
                                          Hide Answer
                                        </>
                                      ) : (
                                        <>
                                          <Eye className="h-4 w-4" />
                                          Show Answer
                                        </>
                                      )}
                                    </Button>
                                  </div>

                                  {/* Answer & Explanation */}
                                  {isAnswerShown && (
                                    <div className="ml-8 space-y-2">
                                      {/* Correct Answer for Short Answer/Practical */}
                                      {(question.type === 'short_answer' || question.type === 'practical') && question.sampleAnswer && (
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                          <p className="font-medium text-green-800 mb-2 flex items-center gap-2">
                                            <CheckCircle2 className="h-4 w-4" />
                                            Sample Answer:
                                          </p>
                                          <pre className="whitespace-pre-wrap text-sm text-green-700 font-mono bg-white p-3 rounded">
                                            {question.sampleAnswer}
                                          </pre>
                                        </div>
                                      )}

                                      {/* Explanation */}
                                      {question.explanation && (
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                          <p className="font-medium text-blue-800 mb-1 flex items-center gap-2">
                                            <Lightbulb className="h-4 w-4" />
                                            Explanation:
                                          </p>
                                          <p className="text-sm text-blue-700">{question.explanation}</p>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                            ) : assessment.sections && assessment.sections.length > 0 ? (
                              /* Display sections for French-style assessments */
                              <div className="space-y-4">
                                <p className="text-sm text-gray-600 mb-4">This assessment contains {assessment.sections.length} sections:</p>
                                {assessment.sections.map((section, sIdx) => (
                                  <div key={sIdx} className="border rounded-lg p-4 bg-gray-50">
                                    <div className="flex justify-between items-center mb-2">
                                      <h5 className="font-medium text-purple-800">{section.name}</h5>
                                      <Badge variant="outline">{section.marks} marks</Badge>
                                    </div>
                                    {section.questions && (
                                      <p className="text-sm text-gray-600">{section.questions} questions</p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-500 italic">No questions available for this assessment.</p>
                            )}

                            {/* Show All Answers Button - only show if questions exist */}
                            {assessment.questions && assessment.questions.length > 0 && (
                            <div className="flex justify-center pt-4">
                              <Button
                                variant="default"
                                onClick={() => {
                                  const allQuestionKeys = assessment.questions.map(q => `${assessment.id}-${q.id}`);
                                  const allShown = allQuestionKeys.every(key => showAnswers[key]);
                                  const newShowAnswers = { ...showAnswers };
                                  allQuestionKeys.forEach(key => {
                                    newShowAnswers[key] = !allShown;
                                  });
                                  setShowAnswers(newShowAnswers);
                                }}
                                className="flex items-center gap-2"
                              >
                                <Eye className="h-4 w-4" />
                                Show All Answers
                              </Button>
                            </div>
                            )}
                          </CardContent>
                        )}
                      </Card>
                    ))
                    )}
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </TabsContent>

        {/* TERM EXAM TAB - AI Generator */}
        <TabsContent value="termexam" className="mt-4">
          <TermExamGenerator 
            subject={selectedSubject} 
            yearLevel={parseInt(selectedYear.replace('year', ''))}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
